import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  FileText, Search, User, Clock, AlertTriangle, Plus, X, Check, ChevronRight, ChevronLeft,
  Stethoscope, Pill, FlaskConical, MessageSquare, Calendar, Printer, Copy,
  Save, Heart, Activity, Thermometer, Weight, Droplets, Wind, AlertCircle,
  ChevronDown, ChevronUp, Trash2, Edit2, RefreshCw, ClipboardList, Upload, Eye, MoreHorizontal,
  Volume2, RotateCcw, CheckCircle, SkipForward, Image, TrendingUp, Info, Lock, Box,
  Sunrise, Sun, Sunset, Moon, NotebookPen, Minus, Syringe, FileDown,
} from 'lucide-react';
import { visitAPI, prescriptionAPI, medicineAPI, doctorAPI, patientAPI, medicalServiceAPI, patientDocumentAPI, instructionAPI, referralAPI, patientReportNoteAPI, intakeAPI, clinicalSettingsAPI, reorderAPI, serverFetch } from '../services/api';
import PrescriptionDetailModal from './PrescriptionDetailModal';
import PatientIntakeModal from './PatientIntakeModal';
import CopyPrescriptionModal, { copySourcesFrom, CopyPreviousStrip, baselineAfterCopy } from './prescription/CopyPrescriptionModal';
import { templateContent, planTemplateApply, runTemplateApply, templateApplyReport, medicineAlreadyOn } from './prescription/templateLines';
import { stockStateFor } from './prescription/stockState';
import { typePrefix, stockCount } from './prescription/medicineLabel';
import { keepLocalRxEdits, localRxFields, localRxEditsDiffer, restoreLocalRxEdits } from './prescription/localRxEdits';
import { canBeGivenHere, courseDoses, isGivenHere } from './prescription/clinicCourse';
import { getAgeBandKey, BAND_LABEL, assessVitals, vitalChipToneClass, vitalSeverityByLabel } from '../utils/vitals';
import { formatClinicDate, formatClinicDateTime, formatClinicTime, clinicToday, clinicNow, toClinicDate } from '../utils/clinicTime';
import { toTitleCase } from '../utils/text';
import { scrollContentToTop } from '../utils/scroll';
import { REPORT_NOTE_CATEGORIES, reportNoteCategory } from '../utils/reportNotes';
import { onActivateKey, ROW_FOCUS_RING } from '../utils/keyboard';
// Reuse the Patient View Modal's data tabs so the prescription header surfaces the exact same
// clinical context (self-fetching components keyed by patientId).
import {
  PatientVitalsTab,
  PatientLabReportsTab,
  PatientRadiologyReportsTab,
  PatientMedicalHistoryTab,
  PatientDetailsModal,
} from './PatientManagement';
import PrescriptionPrint from './PrescriptionPrint';
// Tokens carry a per-doctor prefix; show them the way the patient's receipt does.
import { formatVisitToken, visitIssuedAt } from './TokenPrintModal';
import { printNode } from '../utils/printDocument';
import { savePrescriptionPdf } from '../utils/pdfDocument';
import usePdfDownload from '../hooks/usePdfDownload';
import { authedMediaUrl } from '../utils/media';
import DatePicker from './DatePicker';
import Select from './Select';
import { useToast, useConfirm } from './UIProvider';
import DeferVisitModal from './DeferVisitModal';
import SortHeader, { useSort, sortRows } from './SortHeader';
import TablePager, { usePagedRows } from './TablePager';
import { VitalsModal } from './NurseStation';
import useDialog, { useChangedSince } from '../hooks/useDialog';

// ============ CONSTANTS ============
const DOSE_OPTIONS = [
  { value: 0, label: '0' },
  { value: 0.25, label: '¼' },
  { value: 0.5, label: '½' },
  { value: 0.75, label: '¾' },
  { value: 1, label: '1' },
  { value: 1.5, label: '1½' },
  { value: 2, label: '2' },
  { value: 2.5, label: '2½' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
];

// Pretty label for a dose value — fractions for the small tablet/capsule amounts, plain numbers
// otherwise (ml, drops, etc.).
const FRACTION_LABEL = { 0: '0', 0.25: '¼', 0.5: '½', 0.75: '¾', 1: '1', 1.5: '1½', 2: '2', 2.5: '2½', 3: '3' };
const doseLabel = (v) => FRACTION_LABEL[v] ?? String(v);

// Dosage units per medicine type — drives the per-slot dropdown options, the unit shown in the
// UI, the `doseUnit` sent to the backend (which maps it to Urdu, see PrescriptionService), and the
// Urdu preview word. Falls back to TABLET for unknown / external medicines.
const DOSE_CONFIG = {
  TABLET:     { unit: 'Tablet',      unitUrdu: 'گولی',     opts: [0, 0.5, 1, 1.5, 2, 3] },
  CAPSULE:    { unit: 'Capsule',     unitUrdu: 'کیپسول',   opts: [0, 1, 2, 3] },
  SYRUP:      { unit: 'ml',          unitUrdu: 'ml',       opts: [0, 2.5, 5, 7.5, 10, 15, 20] },
  SUSPENSION: { unit: 'ml',          unitUrdu: 'ml',       opts: [0, 2.5, 5, 7.5, 10, 15, 20] },
  // A nebuliser solution is measured in ml like a syrup. Without this key the fallback is TABLET,
  // so it would be prescribed in "Tablet" units and printed that way in Urdu.
  SOLUTION:   { unit: 'ml',          unitUrdu: 'ml',       opts: [0, 2.5, 5, 7.5, 10, 15, 20] },
  DROPS:      { unit: 'Drops',       unitUrdu: 'قطرے',     opts: [0, 1, 2, 3, 4, 5, 6] },
  INJECTION:  { unit: 'Injection',   unitUrdu: 'ٹیکہ',     opts: [0, 1, 2] },
  INHALER:    { unit: 'Puff',        unitUrdu: 'پھونک',    opts: [0, 1, 2, 3] },
  CREAM:      { unit: 'Application',  unitUrdu: 'بار',      opts: [0, 1, 2, 3] },
  OINTMENT:   { unit: 'Application',  unitUrdu: 'بار',      opts: [0, 1, 2, 3] },
  GEL:        { unit: 'Application',  unitUrdu: 'بار',      opts: [0, 1, 2, 3] },
  // Without these the fallback is TABLET, so a lotion was written "1 Tablet" and printed گولی.
  LOTION:     { unit: 'Application',  unitUrdu: 'بار',      opts: [0, 1, 2, 3] },
  SPRAY:      { unit: 'Spray',       unitUrdu: 'اسپرے',    opts: [0, 1, 2, 3] },
  SACHET:     { unit: 'Sachet',      unitUrdu: 'ساشے',     opts: [0, 0.5, 1, 2] },
  POWDER:     { unit: 'Sachet',      unitUrdu: 'ساشے',     opts: [0, 0.5, 1, 2] },
};
// A syrup can be prescribed by volume or by spoon. Which one is the DOCTOR's choice, not the
// medicine's: the same bottle is written "5 ml" for a clinic that hands out a measuring cap and
// "1 teaspoon" for a patient who will use one from the kitchen. The millilitres are what the
// pharmacy dispenses either way, so mlPer is the conversion the backend applies (DispenseQty
// carries the same numbers).
const SPOON_OPTS = [0.5, 1, 1.5, 2, 2.5, 3];
// A spoon dose's volume, shown beside it wherever a spoon is picked or read: "1 tsp (5 ml)".
// Rounded to 2 places so a float product never reaches the screen as 7.500000000001.
const spoonMl = (dose, mlPer) => `${Math.round(Number(dose) * mlPer * 100) / 100} ml`;
const LIQUID_DOSE_UNITS = [
  // ml stays free text: any volume is a legitimate ml dose. Spoons are not measured that finely -
  // nobody pours 1.3 teaspoons - so they get a fixed list, rendered as fractions.
  { value: 'ml',   label: 'ml',                 unitUrdu: 'ml',      mlPer: 1,  opts: null,        dflt: '' },
  { value: 'tsp',  label: 'Teaspoon (5 ml)',    unitUrdu: 'چمچ',      mlPer: 5,  opts: SPOON_OPTS,  dflt: 1 },
  { value: 'tbsp', label: 'Tablespoon (15 ml)', unitUrdu: 'بڑا چمچ',  mlPer: 15, opts: SPOON_OPTS,  dflt: 1 },
];
// DROPS stays out on purpose: drops are counted, not poured, so the spoon toggle makes no sense
// for them. A solution is poured, so it belongs here.
const LIQUID_TYPES = new Set(['SYRUP', 'SUSPENSION', 'SOLUTION']);
const isLiquidType = (t) => LIQUID_TYPES.has(String(t || '').toUpperCase());

/**
 * The dosing config for a medicine. For a liquid the unit is whichever the doctor picked, so the
 * label, the Urdu word and the input all follow it; everything else is fixed by the dosage form.
 */
const doseConfigFor = (type, doseUnit) => {
  const base = DOSE_CONFIG[type] || DOSE_CONFIG.TABLET;
  if (!isLiquidType(type)) return base;
  const u = LIQUID_DOSE_UNITS.find(x => x.value === doseUnit) || LIQUID_DOSE_UNITS[0];
  return { ...base, unit: u.value, unitUrdu: u.unitUrdu, isLiquid: true, mlPer: u.mlPer, opts: u.opts || base.opts, spoonOpts: u.opts };
};

// Doses per day for each frequency, mirroring DispenseQty.dosesPerDay on the backend so the
// stock advisory and the billed quantity agree. Weekly is one dose per 7 days. As Needed,
// Immediately and Custom have no honest per-day figure, so they're absent and treated as 0.
const FREQ_PER_DAY = {
  ONCE_DAILY: 1, DAILY: 1,
  TWICE_DAILY: 2, EVERY_12_HOURS: 2,
  THRICE_DAILY: 3, EVERY_8_HOURS: 3,
  FOUR_TIMES: 4, EVERY_6_HOURS: 4,
  EVERY_4_HOURS: 6,
  WEEKLY: 1 / 7,
};

// Pair the "; "-separated English special instructions with the "؛ "-separated Urdu ones by index,
// so each can render as "English (Urdu)".
const pairInstructions = (en, ur) => {
  const ens = (en || '').split(/;\s*/).map(s => s.trim()).filter(Boolean);
  const urs = (ur || '').split(/؛\s*/).map(s => s.trim());
  return ens.map((e, i) => ({ en: e, ur: urs[i] || '' }));
};

// Soft length caps for the prescription free-text fields. `diagnosis` is the only one the backend
// hard-limits (@Size(max=500)); the rest are TEXT columns (unbounded in the DB) given a generous
// frontend cap so the live counter has a target and runaway input is prevented.
// Duration units that carry no count: they ARE the duration. "5 until finished" is not a thing,
// so the Duration box is hidden for these and any number already typed is cleared. DOSES is
// deliberately not here — "5 doses" counts something.
const UNCOUNTED_DURATION_UNITS = new Set(['UNTIL_FINISHED', 'CONTINUOUS']);
// Continuous therapy runs without an end date, but stock does not: the pharmacy still has to hand
// over a countable number of units. So for CONTINUOUS the duration value carries DAYS OF SUPPLY,
// how much to dispense now, rather than a course length, and the instruction still reads
// "Continuous". Mirrors PrescriptionMedicine.CONTINUOUS_SUPPLY_DAYS_* on the backend, which snaps
// whatever it is sent onto the same steps. "Until Finished" gets no supply figure: it means the
// pack the patient already has, so there is nothing to multiply out.
const CONTINUOUS_SUPPLY_DAYS = { DEFAULT: 15, MIN: 5, MAX: 15, STEP: 5 };
// What a course is set to before the doctor says otherwise. Three days, the clinic's usual course.
const DEFAULT_COURSE_DAYS = 3;

// Duration for a compact one-line summary. Continuous and Until Finished are the course themselves,
// and a continuous row's number is days of supply rather than a course length, so neither of them
// shows a count here.
const durationSummary = (m) => {
  const unit = String(m.durationUnit || '').toUpperCase();
  if (unit === 'CONTINUOUS') return m.durationValue ? `Continuous (${m.durationValue} days)` : 'Continuous';
  if (unit === 'UNTIL_FINISHED') return 'Until finished';
  if (!m.durationValue && !m.durationUnit) return null;
  return [m.durationValue, m.durationUnit].filter(Boolean).join(' ');
};

// The duration value means a different thing per unit, so it is re-seeded on every switch rather
// than carried across: a course length for Days/Weeks/Months, days of supply for Continuous, and
// nothing at all for Until Finished.
const durationValueForUnit = (unit, prevUnit, prevValue) => {
  if (unit === 'CONTINUOUS') return CONTINUOUS_SUPPLY_DAYS.DEFAULT;
  if (UNCOUNTED_DURATION_UNITS.has(unit)) return null;
  // Leaving Continuous, whose figure was a supply length and not a course, so it must not carry.
  if (prevUnit === 'CONTINUOUS' || !prevValue) return DEFAULT_COURSE_DAYS;
  return prevValue;
};

const DIAGNOSIS_MAXLEN = 2000;
const NOTES_MAXLEN = 2000;
const REPORT_NOTE_MAXLEN = 2000;

// A prescription stops being editable once it has been dispensed, billed, or closed. The backend
// refuses writes in those states, so the writer opens read-only instead of failing at save time.
const EDITABLE_RX_STATUSES = ['INITIATED', 'DRAFT', 'PRESCRIBED'];
const isRxEditable = (rx) => !rx?.status || EDITABLE_RX_STATUSES.includes(rx.status);
// Lowercase, for the read-only banner's sentence ("This prescription is issued and is locked").
// Separate from RX_STATUS_LABEL below, which is Title Case for badges and filters.
const RX_LOCKED_REASON = {
  ISSUED: 'issued', INVOICED: 'invoiced', COMPLETED: 'completed',
};
// Where "Copy previous prescription" is offered. PRESCRIBED is still editable, but it is a finished
// record being amended, not a new one being started, and the server refuses to fill it from another.
const COPY_TARGET_STATUSES = ['INITIATED', 'DRAFT'];

// The Diagnosis-step fields a prescription must carry, returned as the labels the doctor sees.
// Shared by all three gates that enforce them — leaving the step, completing, and amending an
// already-finalised Rx — so none of them can drift from the others. `complaints` is passed in
// rather than read off formData because each caller has its own idea of what counts: the stepper
// includes a typed-but-not-yet-Added complaint, the save path uses the list it is about to persist.
const missingDiagnosisFields = (formData, complaints) => {
  const b = [];
  // Listed in on-screen order (Diagnosis sits last on the form: it is the conclusion).
  if (!complaints || complaints.length === 0) b.push('Chief Complaints');
  if (!formData.clinicalNotes || !formData.clinicalNotes.trim()) b.push('Clinical Notes');
  if (!formData.examination || !formData.examination.trim()) b.push('Examination');
  if (!formData.diagnosis || !formData.diagnosis.trim()) b.push('Diagnosis');
  return b;
};

// complaints and patientInstructions are stored as JSON text, but the template editor builds its
// prescription in memory with them already as arrays. Anything unreadable becomes an empty list
// rather than taking the whole writer down.
const parseJsonList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];
  try { const v = JSON.parse(value); return Array.isArray(v) ? v : []; } catch { return []; }
};

// The Diagnosis and Advice fields the writer's form takes from a saved prescription. The form's
// first state and "Copy previous prescription" both read through here, so a field added to one
// cannot be forgotten by the other. `freshComplaintIds` gives every complaint a new id: removing a
// complaint filters by id, so copied ones must not share an id with anything already on screen.
const rxFormFields = (rx, { freshComplaintIds = false } = {}) => {
  const complaints = parseJsonList(rx?.complaints);
  const stamp = Date.now();
  return {
    diagnosis: rx?.diagnosis || '',
    complaints: freshComplaintIds ? complaints.map((c, i) => ({ ...c, id: stamp + i })) : complaints,
    clinicalNotes: rx?.clinicalNotes || '',
    examination: rx?.examination || '',
    selectedInstructions: parseJsonList(rx?.patientInstructions),
    followupDays: rx?.followupDays || '',
    consultationOnly: rx?.consultationOnly || false,
  };
};

// Live character counter shown under a capped field — grey normally, amber near the limit, red at it.
const CharCount = ({ value, max }) => {
  const n = (value || '').length;
  const cls = n >= max ? 'text-rose-600' : n >= max * 0.9 ? 'text-amber-600' : 'text-gray-400';
  return <span className={`text-[11px] tabular-nums ${cls}`}>{n} / {max}</span>;
};

// Clinical-safety keyword scan. When the doctor's free text mentions something that should be
// recorded as a persistent patient alert (allergy, pregnancy, a chronic condition…), we offer to
// add the matching MedicalAlert before leaving the Diagnosis step. alertType/severity map to the
// MedicalAlert enum so the alert form can be pre-filled. Order matters — the most specific /
// critical patterns first (one suggestion per alertType).
const CLINICAL_FLAGS = [
  { re: /\banaphylax/i,                          label: 'Anaphylaxis',  alertType: 'ALLERGY_DRUG',         severity: 'CRITICAL', title: 'Anaphylaxis' },
  { re: /\baller(gy|gic|gies)\b/i,               label: 'Allergy',      alertType: 'ALLERGY_DRUG',         severity: 'HIGH',     title: 'Allergy' },
  { re: /\bpregnan/i,                            label: 'Pregnancy',    alertType: 'PREGNANCY',            severity: 'HIGH',     title: 'Pregnancy' },
  { re: /\b(breast ?feed|lactat)/i,              label: 'Breastfeeding', alertType: 'CHRONIC_OTHER',       severity: 'MEDIUM',   title: 'Breastfeeding' },
  { re: /\bdiabet/i,                             label: 'Diabetes',     alertType: 'CHRONIC_DIABETES',     severity: 'HIGH',     title: 'Diabetes' },
  { re: /\b(hypertensi|high blood pressure)/i,   label: 'Hypertension', alertType: 'CHRONIC_HYPERTENSION', severity: 'HIGH',     title: 'Hypertension' },
  { re: /\basthma/i,                             label: 'Asthma',       alertType: 'CHRONIC_ASTHMA',       severity: 'HIGH',     title: 'Asthma' },
  { re: /\bthyroid/i,                            label: 'Thyroid disorder', alertType: 'CHRONIC_THYROID',  severity: 'MEDIUM',   title: 'Thyroid disorder' },
  { re: /\b(renal|kidney)\b/i,                   label: 'Kidney disease', alertType: 'CHRONIC_KIDNEY',     severity: 'HIGH',     title: 'Kidney disease' },
  { re: /\b(hepatic|cirrhosis|liver disease)/i,  label: 'Liver disease', alertType: 'CHRONIC_LIVER',       severity: 'HIGH',     title: 'Liver disease' },
  { re: /\b(cardiac|heart disease)/i,            label: 'Heart disease', alertType: 'CHRONIC_HEART',       severity: 'HIGH',     title: 'Heart disease' },
  { re: /\b(cancer|malignan|tumou?r)/i,          label: 'Cancer',       alertType: 'CHRONIC_CANCER',       severity: 'HIGH',     title: 'Cancer' },
];
const detectClinicalFlags = (text) => {
  const t = String(text || '');
  if (!t.trim()) return [];
  const out = [];
  const seen = new Set();
  for (const f of CLINICAL_FLAGS) {
    if (!seen.has(f.alertType) && f.re.test(t)) { seen.add(f.alertType); out.push(f); }
  }
  return out;
};

// Document types that have their own dedicated header links (Lab Reports, Radiology, Past
// Prescriptions). The generic "Documents" modal excludes these so it only lists the leftover
// docs (Discharge Summary, Referral, Other, etc.) — no overlap with the other links.
const RADIOLOGY_DOC_TYPES = new Set(['XRAY', 'CT_SCAN', 'MRI', 'ULTRASOUND', 'ECG']);
const DOCS_HIDDEN_TYPES = new Set(['LAB_REPORT', 'PRESCRIPTION', ...RADIOLOGY_DOC_TYPES]);

const SEVERITY_COLORS = {
  LOW: 'bg-green-100 text-green-800 border-green-300',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
  CRITICAL: 'bg-red-100 text-red-800 border-red-300',
};

// Restore-on-refresh gate (module scope — survives component remounts within a page session,
// resets on a full page reload). The "reopen the last Rx" behaviour is meant ONLY for a browser
// refresh while viewing a prescription — NOT for in-app navigation into the module, which should
// land on the queue/home. So we only restore when the app BOOTED directly on the prescriptions
// route (captured once at module load), and only the first time the component mounts in this page
// session. Navigating in from another page (app booted elsewhere) → BOOTED_ON_RX_ROUTE is false →
// no restore. Re-entering prescriptions later → rxRestoreConsumed is true → no restore.
const BOOTED_ON_RX_ROUTE = (() => {
  try {
    return (window.location.hash || '').toLowerCase().includes('prescription');
  } catch (e) {
    return false;
  }
})();
// Whether the page was actually (re)loaded by the browser — i.e. a refresh / direct-open / back-
// forward restore — as opposed to in-app SPA navigation (which never reloads the page). This is the
// true signal for "reopen the last Rx": it's immune to hot-reloads in dev (which would otherwise
// reset the module-level consume flag and wrongly re-arm the restore on in-app navigation).
const IS_PAGE_RELOAD = (() => {
  try {
    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav && nav.type) return nav.type === 'reload' || nav.type === 'back_forward';
    // Legacy fallback
    return performance.navigation && performance.navigation.type === 1;
  } catch (e) {
    return false;
  }
})();
let rxRestoreConsumed = false;

// Patient identity shown in the page header while a prescription is open (avatar + name + MRN +
// Minor/Adult & status badges + age · gender · token · RX#). Lives in the top header box so the
// prescription card below can focus on the clinical work.
const STATUS_PILL = {
  COMPLETED: 'bg-green-100 text-green-700', ISSUED: 'bg-blue-100 text-blue-700',
  PRESCRIBED: 'bg-purple-100 text-purple-700', INVOICED: 'bg-amber-100 text-amber-800',
  DRAFT: 'bg-amber-100 text-amber-700', INITIATED: 'bg-gray-100 text-gray-600',
};
const PatientIdentityHeader = ({ patient, visit, prescription }) => {
  const isMinor = patient && (patient.adult === false || patient.minor === true);
  const ini = `${(patient?.firstName || '').charAt(0)}${(patient?.lastName || '').charAt(0)}`.toUpperCase();
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-lg ring-4 ring-white/70 flex-shrink-0">
        {ini || <User className="w-6 h-6" />}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 truncate">{patient?.firstName} {patient?.lastName}</h1>
          {patient?.mrn && <span className="text-xs font-mono text-gray-400">{patient.mrn}</span>}
          {patient?.dateOfBirth && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${
              isMinor ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
            }`}>
              <span className={`w-1 h-1 rounded-full ${isMinor ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              {isMinor ? 'Minor' : 'Adult'}
            </span>
          )}
          {prescription?.status && (
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_PILL[prescription.status] || STATUS_PILL.INITIATED}`}>
              {prescription.status}
            </span>
          )}
        </div>
        <div className="text-sm text-gray-500 truncate">
          {patientAgeLabel(patient)} · {patient?.gender} · Token {formatVisitToken(visit)}
          {prescription?.prescriptionNumber && <> · <span className="font-mono">{prescription.prescriptionNumber}</span></>}
        </div>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const PrescriptionManagement = ({ authUser, hasPermission, allowSaveTemplateFromPrescription = false }) => {
  const isDoctor = authUser?.role === 'DOCTOR';
  const notify = useToast();
  const confirm = useConfirm();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('PRESCRIPTIONS', action) : true),
    [hasPermission],
  );
  // The patient profile opened from a queue/history row is a Patients-module surface, so its tab
  // visibility has to be checked against PATIENTS, not PRESCRIPTIONS. Using `can` here would ask
  // the wrong module and show the doctor the wrong tabs.
  const canPatient = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('PATIENTS', action) : true),
    [hasPermission],
  );

  // Patient profile shown for review before calling someone in. Fetched fresh by id rather than
  // reusing the row's embedded patient, because list payloads carry a partial patient and the
  // profile expects the full record.
  const [profilePatient, setProfilePatient] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const openPatientProfile = useCallback(async (patientId) => {
    if (!patientId || profileLoading) return;
    setProfileLoading(true);
    try {
      setProfilePatient(await patientAPI.getById(patientId));
    } catch (e) {
      notify('error', 'Could not open the patient profile. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  }, [notify, profileLoading]);
  // State
  const [activeView, setActiveView] = useState('queue'); // queue, prescription, templateEdit, history
  // The first-visit medical history. A patient who has never had one taken cannot be prescribed
  // for until it is (owner's decision, 2026-09-23), so this holds the patient whose form is due.
  const [intakeDuePatient, setIntakeDuePatient] = useState(null);
  // Switching the primary tab/view swaps the whole page content inside the app <main>; reset
  // scroll to the top on every change (incl. programmatic). The writer's multi-STEP wizard
  // handles its own inner-panel scroll via contentScrollRef and is intentionally not touched.
  useEffect(() => { scrollContentToTop(); }, [activeView]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  // Template currently being edited in the writer's patient-less "template edit mode" (null = not
  // editing a template). Used to bump the TemplatesView remount key on return so the list refreshes.
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templatesRefreshKey, setTemplatesRefreshKey] = useState(0);
  // When the clinic is running nurse-off, the doctor records vitals before the Rx wizard
  // opens. `vitalsPendingVisit` holds the visit waiting for vitals; the VitalsModal saves
  // them via the same visitAPI.recordVitals path the nurse uses, then we re-enter the Rx
  // flow with the updated visit.
  const [vitalsPendingVisit, setVitalsPendingVisit] = useState(null);
  const [prescription, setPrescription] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Persist the open prescription across a browser refresh. The URL hash only encodes the
  // module (#/prescription), so without this a refresh while writing an Rx dropped the doctor
  // back to the (often empty) queue. We snapshot which prescription is open to localStorage and
  // restore it on mount; the captured-once ref guards against the persist effect (which runs on
  // first render with the default 'queue' view) clearing the key before we read it.
  const RX_OPEN_KEY = 'clinic_rx_open';
  const initialOpenRef = useRef(
    (() => {
      try {
        return JSON.parse(localStorage.getItem(RX_OPEN_KEY) || 'null');
      } catch (e) {
        return null;
      }
    })(),
  );
  const restoredRef = useRef(false);

  // Queue state
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [deferVisit, setDeferVisit] = useState(null); // visit currently being deferred (modal open if non-null)

  const [currentDoctor, setCurrentDoctor] = useState(
    isDoctor ? { id: authUser.id, firstName: authUser.firstName, lastName: authUser.lastName, fullName: authUser.fullName, specialization: authUser.specialization } : null
  );
  const [doctors, setDoctors] = useState([]);

  // Load doctors list on mount (only for non-doctor roles)
  useEffect(() => {
    if (!isDoctor) {
      loadDoctors();
    }
  }, []);

  // Refetch the queue whenever the doctor selection changes — including the
  // "All Doctors" (null) case for admins, which fetches today's visits across
  // every doctor. The previous behaviour silently switched to History when no
  // doctor was selected; that surprised admins on first load.
  useEffect(() => {
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDoctor]);

  const loadDoctors = async () => {
    try {
      const data = await doctorAPI.getAll();
      setDoctors(data);
      // Don't auto-select a doctor for admins — they default to "All Doctors" (null)
      // so the queue shows patients across the whole clinic by default.
    } catch (err) {
      console.error('Failed to load doctors:', err);
    }
  };

  const loadQueue = async () => {
    setQueueLoading(true);
    try {
      // currentDoctor null → "All Doctors" mode (admin); the API returns today's visits
      // for every doctor when doctorId is omitted.
      const data = await visitAPI.getTodaysVisits(currentDoctor?.id || null);
      // Show the doctor's whole active flow: ready, called (announced, walking in), and in
      // consultation. Patients drop off the list once they're COMPLETED or CANCELLED.
      const filteredQueue = (data || []).filter(v =>
        v.status === 'READY_FOR_DOCTOR' || v.status === 'CALLED' || v.status === 'IN_CONSULTATION'
      );
      setQueue(filteredQueue);
    } catch (err) {
      console.error('Failed to load queue:', err);
      setError('Failed to load patient queue');
    } finally {
      setQueueLoading(false);
    }
  };

  // Call / Recall / Complete handlers — exposed on each queue row so the doctor can drive
  // the call/recall/complete lifecycle from their own page without bouncing to Token Queue.
  // The token-calls endpoints broadcast to the Display Board AND stamp doctorCalledAt; the
  // complete endpoint marks the visit done so it falls off the queue.
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  const authedFetch = (path, options = {}) => serverFetch(`${apiBase}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(localStorage.getItem('clinic_auth_token')
        ? { 'Authorization': `Bearer ${localStorage.getItem('clinic_auth_token')}` }
        : {}),
      ...(options.headers || {}),
    },
  });

  const handleCallToken = async (visit) => {
    try {
      const res = await authedFetch(`/token-calls/${visit.id}/call`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to call token');
      const data = await res.json();
      notify('success', `Token ${data.tokenNumber} called for ${data.patientName}`);
      loadQueue();
    } catch (e) {
      notify('error', 'Failed to call token: ' + (e.message || 'Please try again'));
    }
  };

  const handleRecallToken = async (visit) => {
    try {
      const res = await authedFetch(`/token-calls/${visit.id}/recall`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to recall token');
      notify('success', 'Token recalled - announcement sent to Display Board');
      loadQueue();
    } catch (e) {
      notify('error', 'Failed to recall token: ' + (e.message || 'Please try again'));
    }
  };

  const handleDeferVisit = async (visitId, afterVisitId) => {
    try {
      const res = await authedFetch(`/visits/${visitId}/defer`, {
        method: 'POST',
        body: JSON.stringify({ afterVisitId: afterVisitId || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to defer patient');
      }
      notify('success', 'Patient deferred - re-queued for callback');
      setDeferVisit(null);
      loadQueue();
    } catch (e) {
      notify('error', e.message || 'Failed to defer patient');
    }
  };

  const handleCompleteVisit = async (visit) => {
    const ok = await confirm({
      title: 'Complete visit?',
      message: `Mark token ${formatVisitToken(visit)} (${visit.patient?.firstName || ''} ${visit.patient?.lastName || ''}) as completed?`,
      confirmText: 'Complete',
      tone: 'success',
    });
    if (!ok) return;
    try {
      await visitAPI.completeVisit(visit.id);
      notify('success', 'Visit completed');
      loadQueue();
    } catch (e) {
      notify('error', 'Failed to complete visit: ' + (e.message || 'Please try again'));
    }
  };

  const handleSelectPatient = async (visit) => {
    // If this visit reached the doctor without vitals, the doctor records them: divert into the
    // VitalsModal first — once saved, the modal's onSaved handler calls back into this function
    // with the updated visit so we proceed exactly as if vitals had already been there.
    //
    // The question is decided from the VISIT, never from the doctor's current Nurse Operational
    // setting. That setting only governs where a token is sent at the moment it is issued; reading
    // it later asks "does this doctor have a nurse now", when the thing we need to know is "was
    // this patient ever sent to one". The two came apart on QA when an admin switched the setting
    // on 18 seconds after a token had been issued under it: the visit had gone straight to the
    // doctor with nobody assigned to its vitals, but by the time the Rx opened the flag said the
    // doctor had a nurse, so this guard waved it through and the consultation happened blind.
    //
    // The visit alone answers it. Only recordVitals moves a visit out of PENDING_VITALS, so any
    // visit sitting in the doctor's stage with no vitals was never triaged — whether because the
    // doctor has no nurse, or because it was an emergency that skipped the queue.
    //
    // "Recorded" deliberately does NOT require BP: for infants and young children BP is optional,
    // so requiring it would re-open the modal in a loop right after a BP-less set is saved. Pulse
    // is required in every age band, with temp/weight as fallbacks.
    const hasVitals = visit && (visit.pulse != null || visit.temperature != null || visit.weight != null);
    if (visit && !hasVitals) {
      setVitalsPendingVisit(visit);
      return;
    }

    setSelectedVisit(visit);
    setLoading(true);
    setError('');

    try {
      // Check if prescription already exists for this visit
      let existingPrescription = null;
      try {
        existingPrescription = await prescriptionAPI.getByVisit(visit.id);
      } catch (e) {
        // No existing prescription
      }

      if (existingPrescription && existingPrescription.id) {
        setPrescription(existingPrescription);
        // Already dispensed or billed? The backend refuses writes, so open it read-only.
        setReadOnly(!isRxEditable(existingPrescription));
      } else {
        // Create new prescription — admin in "All Doctors" mode acts on behalf of the
        // doctor assigned to this visit (visit.doctor.id), since there's no explicit
        // doctor in the dropdown to attribute the Rx to.
        const rxDoctorId = currentDoctor?.id || visit.doctor?.id;
        if (!rxDoctorId) {
          setError('No doctor assigned to this visit - cannot create prescription.');
          setLoading(false);
          return;
        }
        const newPrescription = await prescriptionAPI.create(visit.id, rxDoctorId);
        setPrescription(newPrescription);
        setReadOnly(false);
      }
      setActiveView('prescription');
    } catch (err) {
      console.error('Failed to load/create prescription:', err);
      setError('Failed to load prescription: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToQueue = () => {
    setActiveView('queue');
    setSelectedVisit(null);
    setPrescription(null);
    setReadOnly(false);
    loadQueue();
  };

  // Fetch fresh prescription data from API before opening
  const loadAndOpenPrescription = async (prescriptionData, isReadOnly) => {
    setLoading(true);
    try {
      const fresh = await prescriptionAPI.getById(prescriptionData.id);
      setPrescription(fresh);
      setSelectedVisit({
        id: fresh.visit?.id || prescriptionData.visit?.id,
        patient: fresh.patient || prescriptionData.patient,
        tokenNumber: fresh.visit?.tokenNumber || prescriptionData.visit?.tokenNumber || 'N/A'
      });
      // A dispensed / billed / closed prescription can never be saved, so it always opens
      // read-only regardless of how the caller asked for it.
      setReadOnly(isReadOnly || !isRxEditable(fresh));
      setActiveView('prescription');
    } catch (err) {
      console.error('Failed to load prescription:', err);
      // Fallback to stale data
      setPrescription(prescriptionData);
      setSelectedVisit({
        id: prescriptionData.visit?.id,
        patient: prescriptionData.patient,
        tokenNumber: prescriptionData.visit?.tokenNumber || 'N/A'
      });
      setReadOnly(isReadOnly || !isRxEditable(prescriptionData));
      setActiveView('prescription');
    } finally {
      setLoading(false);
    }
  };

  // Does this patient still owe their first-visit medical history? Checked once the workspace has
  // actually opened, rather than in each of the three paths that open it (queue, patient search,
  // and the refresh-restore), so none of them can forget to ask. Read-only views are never held
  // up: nothing is being written, and an old prescription is often opened just to be read.
  const rxPatientId = prescription?.patient?.id || selectedVisit?.patient?.id || null;
  useEffect(() => {
    if (activeView !== 'prescription' || readOnly || !rxPatientId) {
      setIntakeDuePatient(null);
      return undefined;
    }
    let cancelled = false;
    intakeAPI.forPatient(rxPatientId)
      .then(data => {
        if (cancelled) return;
        setIntakeDuePatient(
          data?.pending ? (prescription?.patient || selectedVisit?.patient || null) : null
        );
      })
      // A blip on this lookup must not lock the doctor out of the prescription.
      .catch(() => { if (!cancelled) setIntakeDuePatient(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, readOnly, rxPatientId]);

  // Snapshot / clear which prescription is open so a refresh can restore it.
  useEffect(() => {
    try {
      if (activeView === 'prescription' && prescription?.id) {
        localStorage.setItem(RX_OPEN_KEY, JSON.stringify({ prescriptionId: prescription.id, readOnly }));
      } else {
        localStorage.removeItem(RX_OPEN_KEY);
      }
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, prescription?.id, readOnly]);

  // On mount, reopen the prescription that was open before the refresh. Fetch first so a
  // deleted/inaccessible prescription clears the key and falls back to the queue cleanly
  // instead of opening a broken view.
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    // Only restore on a genuine refresh that booted on the Rx route, and only once per page
    // session. Otherwise (in-app navigation into the module) land on the queue/home.
    // NOTE: rxRestoreConsumed is flipped AFTER the async restore (past the network await), not
    // synchronously — under React StrictMode the component mounts→unmounts→remounts, and a
    // synchronous flag would let the throwaway first mount consume the restore and leave the
    // live second mount on the queue. Deferring it lets the live mount restore too, while still
    // blocking later real navigations.
    // Restore ONLY on a genuine browser (re)load that booted on the Rx route — never on in-app
    // navigation into the module, which should always land on the queue/home.
    if (!IS_PAGE_RELOAD || !BOOTED_ON_RX_ROUTE || rxRestoreConsumed) return;
    const saved = initialOpenRef.current;
    // Consume the one-time boot-restore opportunity even when there's nothing to restore yet — so
    // booting on the Rx route with no open Rx, then opening one and navigating back in-app, doesn't
    // wrongly reopen it. Deferred to a microtask so StrictMode's throwaway first mount doesn't
    // consume it before the live mount restores.
    Promise.resolve().then(() => { rxRestoreConsumed = true; });
    if (!saved?.prescriptionId) return;
    (async () => {
      try {
        const fresh = await prescriptionAPI.getById(saved.prescriptionId);
        await loadAndOpenPrescription(fresh, !!saved.readOnly);
      } catch (e) {
        try { localStorage.removeItem(RX_OPEN_KEY); } catch (_) { /* ignore */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler to view a specific prescription from history (read-only)
  const handleViewPrescription = async (prescriptionData) => {
    await loadAndOpenPrescription(prescriptionData, true);
  };

  // Handler to edit a specific prescription from history
  const handleEditPrescription = async (prescriptionData) => {
    await loadAndOpenPrescription(prescriptionData, false);
  };

  // ---------- Template edit mode ----------
  // Open the existing prescription writer in a patient-less "template edit mode". We build a local
  // pseudo-prescription from the template's JSON fields so the writer's existing formData
  // initializer + medicine/lab sections pre-fill exactly as they do for a real prescription. The
  // temp ids let the local (no-server) medicine/lab editing track items by id.
  const handleEditTemplate = (template) => {
    const tempId = () => `tmp_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    const pseudo = {
      id: null,
      status: 'INITIATED',
      diagnosis: template.diagnosis || '',
      // formData.complaints reads prescription.complaints (string or array of {complaint, since}).
      complaints: safeJsonArray(template.complaintsJson).map((c, i) => ({ ...c, id: Date.now() + i })),
      clinicalNotes: template.clinicalNotes || '',
      examination: template.examination || '',
      consultationOnly: !!template.consultationOnly,
      followupDays: template.followupDays != null ? template.followupDays : '',
      // formData.selectedInstructions reads prescription.patientInstructions.
      patientInstructions: safeJsonArray(template.instructionsJson),
      medicines: safeJsonArray(template.medicinesJson).map(m => ({ ...m, id: tempId() })),
      labTests: safeJsonArray(template.labTestsJson).map(t => ({ ...t, id: tempId() })),
    };
    setEditingTemplate(template);
    setPrescription(pseudo);
    setSelectedVisit(null);
    setReadOnly(false);
    setActiveView('templateEdit');
  };

  const handleBackToTemplates = () => {
    setActiveView('templates');
    setEditingTemplate(null);
    setPrescription(null);
    setSelectedVisit(null);
    setReadOnly(false);
    // Bump the key so TemplatesView remounts and re-fetches the (possibly updated) list.
    setTemplatesRefreshKey(k => k + 1);
  };

  // Open the template editor on a BLANK template (no editingTemplate). The editor shows a doctor
  // picker (since there's no prescription/doctor context) so the new template can be filed under
  // a chosen doctor — required because templates are owned by a doctor.
  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setPrescription({
      id: null, status: 'INITIATED', diagnosis: '', complaints: [], clinicalNotes: '',
      examination: '', consultationOnly: false, followupDays: '',
      patientInstructions: [], medicines: [], labTests: [],
    });
    setSelectedVisit(null);
    setReadOnly(false);
    setActiveView('templateEdit');
  };

  return (
    // Fill the available height and lay out as a column so the page title stays fixed and the
    // content area below owns the single scrollbar — no nested page+content double-scroll.
    <div className="h-full flex flex-col bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      {/* Header — hidden in prescription view; PrescriptionForm renders its own header band there */}
      {activeView !== 'prescription' && (
      <div className="mb-6 flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex items-center justify-center shadow-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Prescriptions</h1>
              <p className="text-gray-600">Write and manage patient prescriptions</p>
            </div>
          </div>

          {/* Doctor Selector */}
          <div className="flex items-center space-x-4">
            {isDoctor ? (
              <div className="flex items-center space-x-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                <Stethoscope className="w-4 h-4 text-emerald-600" />
                <span className="font-medium text-emerald-800">{authUser.fullName || `Dr. ${authUser.firstName} ${authUser.lastName}`}</span>
                {authUser.specialization && (
                  <span className="text-xs text-emerald-600">({authUser.specialization})</span>
                )}
              </div>
            ) : (
              <Select
                value={currentDoctor?.id || '__all__'}
                onChange={(v) => {
                  if (v === '__all__') {
                    setCurrentDoctor(null);
                  } else {
                    setCurrentDoctor(doctors.find(d => d.id === v));
                  }
                }}
                options={[
                  { value: '__all__', label: 'All Doctors' },
                  ...doctors.map(doc => ({
                    value: doc.id,
                    label: doc.fullName || ('Dr. ' + doc.firstName + ' ' + doc.lastName),
                  })),
                ]}
                className="min-w-[12rem]"
                buttonClassName="px-4 py-2 rounded-lg"
              />
            )}
            {/* Header refresh is the Today's Queue refresh — only show it on that tab. Every
                other view (New Prescription, Today's Prescriptions, Prescription History,
                Templates) has its own local refresh, so a header queue-refresh there just
                confuses (it reloads data that isn't on screen). */}
            {activeView === 'queue' && (
              <button
                onClick={loadQueue}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                title="Refresh Queue"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation — the open tab is navy like the side pane, as every selected tab is (owner, 2026-10-01). Queue and History are independently gated; Search and
            Prescribed-Today follow the Queue tab's permission (they're alternate views of
            today's work). */}
        {activeView !== 'prescription' && activeView !== 'templateEdit' && (
          <div className="mt-4 flex space-x-2">
            {can('VIEW_QUEUE_TAB') && (
              <button
                onClick={() => setActiveView('queue')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeView === 'queue'
                    ? 'bg-navy-800 text-white shadow-sm'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                <Clock className="w-4 h-4 inline mr-2" />
                Today's Queue
              </button>
            )}
            {can('VIEW_QUEUE_TAB') && can('CREATE') && (
              <button
                onClick={() => currentDoctor && setActiveView('search')}
                disabled={!currentDoctor}
                title={!currentDoctor ? 'Select a specific doctor to start a new prescription' : ''}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeView === 'search'
                    ? 'bg-navy-800 text-white shadow-sm'
                    : !currentDoctor
                    ? 'bg-white text-gray-300 border border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                <Search className="w-4 h-4 inline mr-2" />
                New Prescription
              </button>
            )}
            {can('VIEW_QUEUE_TAB') && (
              <button
                onClick={() => setActiveView('prescribed_today')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeView === 'prescribed_today'
                    ? 'bg-navy-800 text-white shadow-sm'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
                title="Prescriptions written today by the selected doctor (or all doctors)"
              >
                <CheckCircle className="w-4 h-4 inline mr-2" />
                Today's Prescriptions
              </button>
            )}
            {can('VIEW_HISTORY_TAB') && (
              <button
                onClick={() => setActiveView('history')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeView === 'history'
                    ? 'bg-navy-800 text-white shadow-sm'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                <ClipboardList className="w-4 h-4 inline mr-2" />
                Prescription History
              </button>
            )}
            {can('VIEW_HISTORY_TAB') && (
              <button
                onClick={() => setActiveView('templates')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeView === 'templates'
                    ? 'bg-navy-800 text-white shadow-sm'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
                title="All saved prescription templates (any doctor)"
              >
                <Save className="w-4 h-4 inline mr-2" />
                Templates
              </button>
            )}
          </div>
        )}
      </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
          <button aria-label="Dismiss" onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main Content — the single scroll area. Fills the remaining height; the form card
          inside is h-full so its footer stays pinned (no page+content double-scroll), while the
          queue/history lists scroll within this container. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
      {activeView === 'queue' ? (
        <DoctorQueue
          queue={queue}
          loading={queueLoading}
          onSelectPatient={handleSelectPatient}
          onRefresh={loadQueue}
          onCall={handleCallToken}
          onRecall={handleRecallToken}
          onDefer={(visit) => setDeferVisit(visit)}
          onComplete={handleCompleteVisit}
          onOpenPatientProfile={openPatientProfile}
          can={can}
        />
      ) : activeView === 'search' ? (
        <PatientSearchPanel
          doctorId={currentDoctor?.id}
          onStartPrescription={handleSelectPatient}
        />
      ) : activeView === 'prescribed_today' ? (
        // Reuses PrescriptionHistory with the date filter locked to "today" — same data
        // path, same row interactions, just no Today/All-Time toggle and a focused tab title.
        <PrescriptionHistory
          // Distinct key per tab. Both tabs render this same component at the same position, so
          // without it React reuses the instance and its state survives the switch — dateFilter is
          // a useState initial value, so a filter picked in History stayed applied here while
          // lockedDateFilter hid the buttons that would have revealed it. That is how "Today's
          // Prescriptions" came to list yesterday's and caption itself "(Yesterday)".
          key="prescribed-today"
          doctorId={currentDoctor?.id}
          onViewPrescription={handleViewPrescription}
          onEditPrescription={handleEditPrescription}
          onOpenPatientProfile={openPatientProfile}
          lockedDateFilter="today"
        />
      ) : activeView === 'history' ? (
        <PrescriptionHistory
          key="history"
          doctorId={currentDoctor?.id}
          onViewPrescription={handleViewPrescription}
          onEditPrescription={handleEditPrescription}
          onOpenPatientProfile={openPatientProfile}
        />
      ) : activeView === 'templates' ? (
        <TemplatesView key={templatesRefreshKey} can={can} onEdit={handleEditTemplate} onCreate={handleCreateTemplate} />
      ) : activeView === 'templateEdit' && prescription ? (
        <PrescriptionForm
          templateEditMode
          template={editingTemplate}
          prescription={prescription}
          setPrescription={setPrescription}
          visit={null}
          doctor={currentDoctor}
          doctors={doctors}
          onBack={handleBackToTemplates}
        />
      ) : activeView === 'prescription' && prescription ? (
        <PrescriptionForm
          key={prescription.id}
          prescription={prescription}
          setPrescription={setPrescription}
          visit={selectedVisit}
          doctor={currentDoctor}
          doctors={doctors}
          onBack={handleBackToQueue}
          readOnly={readOnly}
          allowSaveTemplate={allowSaveTemplateFromPrescription}
          canRefer={can('REFER_PATIENT')}
        />
      ) : null}
      </div>

      {deferVisit && (
        <DeferVisitModal
          visit={deferVisit}
          queue={queue}
          onClose={() => setDeferVisit(null)}
          onConfirm={(afterVisitId) => handleDeferVisit(deferVisit.id, afterVisitId)}
        />
      )}

      {/* Doctor-side vitals capture — only opens when nurse.operational is off and the
          visit has no vitals yet. Same modal the nurse uses, just initiated from the Rx
          flow. After save, re-enter handleSelectPatient with the updated visit. */}
      {vitalsPendingVisit && (
        <VitalsModal
          visit={vitalsPendingVisit}
          recordedBy={authUser?.id}
          onClose={() => setVitalsPendingVisit(null)}
          onSaved={(updatedVisit) => {
            setVitalsPendingVisit(null);
            handleSelectPatient(updatedVisit || vitalsPendingVisit);
          }}
        />
      )}

      {/* Patient profile opened from a queue or history row, so the doctor can review someone
          before calling them in. reviewOnly hides the mutating footer actions; permissions are
          checked against the PATIENTS module because that's the surface being shown. */}
      {profilePatient && (
        <PatientDetailsModal
          patient={profilePatient}
          can={canPatient}
          reviewOnly
          onClose={() => setProfilePatient(null)}
        />
      )}

      {/* The first-visit medical history. Rendered last so it sits over the workspace, and with no
          way to dismiss it other than saving or leaving the prescription. */}
      {intakeDuePatient && (
        <PatientIntakeModal
          patient={intakeDuePatient}
          onSaved={() => setIntakeDuePatient(null)}
          onLeave={() => { setIntakeDuePatient(null); handleBackToQueue(); }}
        />
      )}

    </div>
  );
};

// ============ DOCTOR QUEUE COMPONENT ============
const DoctorQueue = ({ queue, loading, onSelectPatient, onRefresh, onCall, onRecall, onDefer, onComplete, onOpenPatientProfile, can }) => {
  const allowed = (action) => (typeof can === 'function' ? can(action) : true);
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ClipboardList className="w-10 h-10 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Patients in Queue</h3>
        <p className="text-gray-500">Patients will appear here when tokens are issued</p>
        <button
          onClick={onRefresh}
          className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
        >
          Refresh Queue
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
        <h2 className="text-lg font-semibold text-gray-800">Today's Patients</h2>
        <p className="text-sm text-gray-600">{queue.length} patient(s) waiting</p>
      </div>

      <div className="divide-y divide-gray-100">
        {queue.map((visit) => (
          <div
            key={visit.id}
            onClick={() => onOpenPatientProfile && onOpenPatientProfile(visit.patient?.id)}
            role="button"
            tabIndex={0}
            onKeyDown={onActivateKey(() => onOpenPatientProfile && onOpenPatientProfile(visit.patient?.id))}
            title="Open patient profile"
            className="p-4 hover:bg-emerald-50/40 transition-colors flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center space-x-4">
              {/* Token Number */}
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-bold text-xl ${
                visit.isEmergency
                  ? 'bg-red-100 text-red-700 border-2 border-red-300'
                  : visit.status === 'IN_CONSULTATION'
                    ? 'bg-green-100 text-green-700 border-2 border-green-300'
                    : 'bg-emerald-100 text-emerald-700'
              }`}>
                {formatVisitToken(visit)}
              </div>

              {/* Patient Info */}
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="font-semibold text-gray-900">
                    {visit.patient?.firstName} {visit.patient?.lastName}
                  </h3>
                  {visit.isEmergency && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                      EMERGENCY
                    </span>
                  )}
                  {visit.status === 'IN_CONSULTATION' && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      In Consultation
                    </span>
                  )}
                  {visit.status === 'CALLED' && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                      Called
                    </span>
                  )}
                  {visit.status === 'READY_FOR_DOCTOR' && (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                      Ready
                    </span>
                  )}
                  {visit.referredFromVisitId && (
                    <span
                      className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-medium rounded-full"
                      title="Referred from another doctor's consultation - their notes are on the patient's record"
                    >
                      Referred
                    </span>
                  )}
                </div>
                <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 mt-1">
                  <span className="flex items-center gap-1"><User className="h-3.5 w-3.5 text-gray-400" />{visit.patient?.mrn || 'N/A'}</span>
                  {visit.patient?.gender && <span>{toTitleCase(visit.patient.gender)}</span>}
                  <span>{patientAgeLabel(visit.patient)}</span>
                  {/* When the token was issued. This list is in arrival order, so without it the
                      order has nothing on screen explaining it — the same reason the Token Queue
                      shows it, read from the same helper so the two can never disagree. */}
                  <span className="flex items-center gap-1" title="Token issued at">
                    <Clock className="h-3.5 w-3.5 text-gray-400" />
                    {formatClinicTime(visitIssuedAt(visit), {}, 'N/A')}
                  </span>
                </div>
                {/* Vitals summary — coloured by age-band normal range (green normal / amber
                    borderline / red abnormal), same engine as the Token Queue + Rx panel. */}
                {(visit.bpSystolic || visit.pulse || visit.temperature) && (() => {
                  const vsev = vitalSeverityByLabel(
                    { bpSystolic: visit.bpSystolic, bpDiastolic: visit.bpDiastolic, pulse: visit.pulse, temperature: visit.temperature },
                    visit.patient?.dateOfBirth,
                  );
                  return (
                  <div className="flex items-center gap-2 mt-1.5 text-xs">
                    {visit.bpSystolic && visit.bpDiastolic && (
                      <span className={`px-2 py-0.5 rounded-full border font-medium ${vitalChipToneClass(vsev('BP'))}`}>
                        BP {visit.bpSystolic}/{visit.bpDiastolic}
                      </span>
                    )}
                    {visit.pulse && (
                      <span className={`px-2 py-0.5 rounded-full border font-medium ${vitalChipToneClass(vsev('Pulse'))}`}>
                        Pulse {visit.pulse}
                      </span>
                    )}
                    {visit.temperature && (
                      <span className={`px-2 py-0.5 rounded-full border font-medium ${vitalChipToneClass(vsev('Temp'))}`}>
                        {visit.temperature}°F
                      </span>
                    )}
                  </div>
                  );
                })()}
              </div>
            </div>

            {/* Action buttons. Clicking the row opens the patient's profile for review, which is
                read-only and safe to trigger by accident. It deliberately does NOT start a
                prescription — that still needs the explicit button, so a stray click can never
                initiate a new Rx. stopPropagation keeps these buttons from also opening the
                profile. */}
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {visit.status === 'READY_FOR_DOCTOR' && allowed('CALL_TOKEN') && (
                <button
                  onClick={() => onCall && onCall(visit)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors"
                  title="Call patient - broadcasts to Display Board"
                >
                  <Volume2 className="h-3.5 w-3.5" />
                  <span>Call</span>
                </button>
              )}
              {/* Recall/Defer only while the patient is summoned but not yet inside (CALLED);
                  once the Rx is started the visit is IN_CONSULTATION and these disappear. */}
              {visit.status === 'CALLED' && (
                <>
                  {allowed('RECALL_TOKEN') && (
                    <button
                      onClick={() => onRecall && onRecall(visit)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg transition-colors"
                      title="Recall patient - re-announces on Display Board"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      <span>Recall</span>
                    </button>
                  )}
                  {allowed('DEFER_VISIT') && (
                    <button
                      onClick={() => onDefer && onDefer(visit)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold rounded-lg transition-colors"
                      title="Move this patient down the queue"
                    >
                      <SkipForward className="h-3.5 w-3.5" />
                      <span>Defer</span>
                    </button>
                  )}
                </>
              )}
              {/* Primary CTA — explicit start-Rx button. Label switches between "Start" and
                  "Continue" so the doctor knows whether they're creating a fresh prescription
                  or returning to one already in progress on this visit. */}
              <button
                onClick={() => onSelectPatient(visit)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors"
                title={visit.status === 'IN_CONSULTATION' ? 'Continue this patient\'s prescription' : 'Start a prescription for this patient'}
              >
                <FileText className="h-3.5 w-3.5" />
                <span>{visit.status === 'IN_CONSULTATION' ? 'Continue Prescription' : 'Start Prescription'}</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ PATIENT SEARCH PANEL ============
const PatientSearchPanel = ({ doctorId, onStartPrescription }) => {
  const showToast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  // Why an identifier search found nobody, when the server could tell us.
  const [searchHint, setSearchHint] = useState('');
  const [searching, setSearching] = useState(false);
  const [medicalServices, setMedicalServices] = useState([]);
  const [creatingVisit, setCreatingVisit] = useState(false);

  useEffect(() => {
    loadMedicalServices();
  }, []);

  const loadMedicalServices = async () => {
    try {
      const data = await medicalServiceAPI.getAll();
      setMedicalServices(data || []);
    } catch (err) {
      console.error('Failed to load medical services:', err);
    }
  };

  const searchPatients = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchHint('');
      return;
    }
    setSearching(true);
    try {
      const response = await patientAPI.search(query);
      const results = response?.content || response || [];
      setSearchResults(results);
      setSearchHint(response?.searchHint || '');
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
      setSearchHint('');
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) searchPatients(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectPatient = async (patient) => {
    if (medicalServices.length === 0) {
      showToast('warning', 'No medical services available. Please add a medical service first.');
      return;
    }
    setCreatingVisit(true);
    try {
      // Use first medical service as default (Medical OPD)
      const defaultService = medicalServices.find(s => s.serviceType === 'MEDICAL_OPD') || medicalServices[0];
      const visit = await visitAPI.create({
        patientId: patient.id,
        doctorId: doctorId,
        medicalServiceId: defaultService.id,
      });

      onStartPrescription({
        ...visit,
        patient: patient
      });
    } catch (err) {
      console.error('Failed to create visit:', err);
      showToast('error', 'Failed to create visit: ' + err.message);
    } finally {
      setCreatingVisit(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
        <h2 className="text-lg font-semibold text-gray-800">New Prescription</h2>
        <p className="text-sm text-gray-600">Search for a patient to create a new prescription</p>
      </div>

      <div className="p-6">
        {/* Search Input */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by patient name, MR#, or phone number..."
            className="w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
            autoFocus
          />
          {searching && (
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
            </div>
          )}
        </div>

        {/* Search Results */}
        {searchQuery.length >= 2 && (
          <div className="space-y-2">
            {searchResults.length > 0 ? (
              <>
                <p className="text-sm text-gray-500 mb-2">{searchResults.length} patient(s) found</p>
                {searchResults.map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => handleSelectPatient(patient)}
                    disabled={creatingVisit}
                    className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-green-50 hover:border-green-300 transition-colors text-left disabled:opacity-50"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex items-center justify-center">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {patient.firstName} {patient.lastName}
                        </h3>
                        <div className="flex items-center space-x-3 text-sm text-gray-500">
                          <span>MR#: {patient.mrn}</span>
                          <span>•</span>
                          <span>{patient.gender}</span>
                          <span>•</span>
                          <span>{patientAgeLabel(patient)}</span>
                          {patient.phone && (
                            <>
                              <span>•</span>
                              <span>{patient.phone}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-green-600">
                      {creatingVisit ? (
                        <span className="text-sm">Creating...</span>
                      ) : (
                        <>
                          <span className="text-sm font-medium">Start Prescription</span>
                          <ChevronRight className="w-5 h-5" />
                        </>
                      )}
                    </div>
                  </button>
                ))}
              </>
            ) : !searching ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{searchHint || `No patients found matching "${searchQuery}"`}</p>
                <p className="text-sm mt-1">Try a different search term</p>
              </div>
            ) : null}
          </div>
        )}

        {/* Empty State */}
        {searchQuery.length < 2 && (
          <div className="text-center py-12 text-gray-500">
            <Search className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">Search for a Patient</h3>
            <p>Enter at least 2 characters to search</p>
            <div className="mt-4 text-sm">
              <p>Search by:</p>
              <ul className="list-disc list-inside mt-1">
                <li>Patient name (first or last)</li>
                <li>MR Number</li>
                <li>Phone number</li>
                <li>CNIC</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ PRESCRIPTION HISTORY COMPONENT ============
// Prescription statuses as the user should read them. INITIATED (opened) and DRAFT (saved but
// unfinished) are one thing to a reader — "Draft" — so both carry that label and the Draft filter
// matches either. Previously the badge printed the raw enum in caps while the filter offered
// "Draft" for INITIATED only, so DRAFT rows could not be filtered at all and PRESCRIBED was
// labelled "Completed" even though a separate COMPLETED status exists.
const RX_STATUS_LABEL = {
  INITIATED: 'Draft',
  DRAFT: 'Draft',
  PRESCRIBED: 'Prescribed',
  INVOICED: 'Invoiced',
  ISSUED: 'Issued',
  COMPLETED: 'Completed',
};

const RX_STATUS_STYLE = {
  INITIATED: 'bg-gray-100 text-gray-700',
  DRAFT: 'bg-gray-100 text-gray-700',
  PRESCRIBED: 'bg-blue-100 text-blue-700',
  INVOICED: 'bg-amber-100 text-amber-800',
  ISSUED: 'bg-green-100 text-green-700',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
};

// Filter values. 'DRAFT_ANY' is the one that isn't a raw status: it spans INITIATED and DRAFT.
const RX_STATUS_FILTERS = [
  { value: 'all', label: 'All Status' },
  { value: 'DRAFT_ANY', label: 'Draft' },
  { value: 'PRESCRIBED', label: 'Prescribed' },
  { value: 'INVOICED', label: 'Invoiced' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'COMPLETED', label: 'Completed' },
];

const rxStatusLabel = (status) => RX_STATUS_LABEL[status] || status;

const PrescriptionHistory = ({
  doctorId,
  onEditPrescription,
  // Row click opens the patient's profile for review. Read-only, so an accidental click is
  // harmless; the Preview and Delete buttons stop propagation to keep their own behaviour.
  onOpenPatientProfile,
  // When set ('today' | 'all'), forces the date filter to that value and hides the
  // Today/All-Time toggle. Used by the "Today's Prescriptions" tab which is locked to today,
  // while the regular "Prescription History" tab keeps the toggle visible.
  lockedDateFilter = null,
  defaultDateFilter = 'today',
}) => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Date filter: all | today | yesterday | week | month | custom (mirrors the All Patients list).
  const [dateFilter, setDateFilter] = useState(lockedDateFilter || defaultDateFilter);
  const [customStart, setCustomStart] = useState(''); // ISO "YYYY-MM-DD"
  const [customEnd, setCustomEnd] = useState('');
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, prescriptionNumber }
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const deleteConfirmDialogRef = useDialog(() => { if (!deleting) { setDeleteConfirm(null); setDeleteError(null); } }, { confirmDiscard: false });

  const isAllDoctors = !doctorId;
  const todayIso = clinicToday();
  const PRESC_DATE_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'custom', label: 'Custom' },
  ];
  const activeDateFilterLabel = PRESC_DATE_FILTERS.find(f => f.value === dateFilter)?.label || 'All';

  // [start, end) Date range for the active filter — used to filter client-side by prescriptionDate.
  const [rangeStart, rangeEnd] = (() => {
    // Clinic-zone "now" so the day/week/month boundaries match the clinic calendar. The
    // prescriptionDate values compared below are naive clinic wall-clock, so both sides of the
    // comparison express clinic time as a browser-local Date — apples to apples.
    const now = clinicNow();
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const today = startOfDay(now);
    const tomorrow = new Date(today.getTime() + 86400000);
    switch (dateFilter) {
      case 'today': return [today, tomorrow];
      case 'yesterday': return [new Date(today.getTime() - 86400000), today];
      case 'week': {
        const dow = today.getDay(); // 0 Sun … 6 Sat
        const monday = new Date(today.getTime() - (dow === 0 ? 6 : dow - 1) * 86400000);
        return [monday, tomorrow];
      }
      case 'month': return [new Date(now.getFullYear(), now.getMonth(), 1), tomorrow];
      case 'custom': return [
        customStart ? new Date(customStart + 'T00:00:00') : null,
        customEnd ? new Date(new Date(customEnd + 'T00:00:00').getTime() + 86400000) : null, // inclusive end day
      ];
      default: return [null, null]; // all
    }
  })();

  useEffect(() => {
    loadPrescriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, dateFilter]);

  const loadPrescriptions = async () => {
    setLoading(true);
    try {
      let data;
      if (isAllDoctors) {
        // All doctors view (admin only)
        data = dateFilter === 'today'
          ? await prescriptionAPI.getAllTodayPrescriptions()
          : await prescriptionAPI.getAllPrescriptions();
      } else {
        // Single-doctor view
        data = dateFilter === 'today'
          ? await prescriptionAPI.getDoctorTodayPrescriptions(doctorId)
          : await prescriptionAPI.getDoctorAllPrescriptions(doctorId);
      }
      setPrescriptions(data || []);
    } catch (err) {
      console.error('Failed to load prescriptions:', err);
      setPrescriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (rx) => {
    setDeleteError(null);
    setDeleteConfirm({ id: rx.id, prescriptionNumber: rx.prescriptionNumber });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await prescriptionAPI.cancel(deleteConfirm.id);
      setDeleteConfirm(null);
      loadPrescriptions();
    } catch (err) {
      console.error('Failed to delete prescription:', err);
      setDeleteError(err.message || 'Failed to delete prescription');
    } finally {
      setDeleting(false);
    }
  };

  // Date scope first, on its own, so the table footer can say "X of Y": Y is everything in the
  // active date range and X is what survives the search and status filters on top of it.
  const dateScopedPrescriptions = prescriptions.filter(rx => {
    // Date range (client-side) — applied for all filters except "all".
    if (rangeStart || rangeEnd) {
      // toClinicDate, not new Date: prescriptionDate is a date-only string, and the spec says
      // parse that as UTC midnight. West of UTC that lands on the previous day, so every one
      // of today's prescriptions fell outside a one-day "Today" window while still passing
      // the seven-day "This Week" one.
      const d = rx.prescriptionDate ? toClinicDate(rx.prescriptionDate) : null;
      if (!d) return false;
      if (rangeStart && d < rangeStart) return false;
      if (rangeEnd && d >= rangeEnd) return false;
    }
    return true;
  });

  // Filter prescriptions — search across patient name (incl. combined), MRN, Rx#, diagnosis, doctor name
  const filteredPrescriptions = dateScopedPrescriptions.filter(rx => {
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const firstName = (rx.patient?.firstName || '').toLowerCase();
      const lastName  = (rx.patient?.lastName  || '').toLowerCase();
      const fullName  = `${firstName} ${lastName}`.trim();
      const mrn       = (rx.patient?.mrn || '').toLowerCase();
      const rxNumber  = (rx.prescriptionNumber || '').toLowerCase();
      const diagnosis = (rx.diagnosis || '').toLowerCase();
      const docFirst  = (rx.doctor?.firstName || '').toLowerCase();
      const docLast   = (rx.doctor?.lastName  || '').toLowerCase();
      const docFull   = `${docFirst} ${docLast}`.trim();

      const matchesSearch =
        firstName.includes(q) ||
        lastName.includes(q) ||
        fullName.includes(q) ||
        mrn.includes(q) ||
        rxNumber.includes(q) ||
        diagnosis.includes(q) ||
        docFirst.includes(q) ||
        docLast.includes(q) ||
        docFull.includes(q);

      if (!matchesSearch) return false;
    }

    const matchesStatus = filterStatus === 'all'
      || (filterStatus === 'DRAFT_ANY'
            ? (rx.status === 'INITIATED' || rx.status === 'DRAFT')
            : rx.status === filterStatus);
    if (!matchesStatus) return false;

    return true;
  });

  const hasActiveFilters = searchQuery.trim() !== '' || filterStatus !== 'all';
  const clearFilters = () => { setSearchQuery(''); setFilterStatus('all'); };

  // Sorted after filtering. Medicines orders on the names the cell lists, so clicking it groups
  // the same drug together; a prescription with none sinks to the bottom either way.
  const { sortConfig, handleSort } = useSort();
  const sortedPrescriptions = React.useMemo(() => sortRows(filteredPrescriptions, sortConfig, {
    rxNumber:  (rx) => rx.prescriptionNumber,
    patient:   (rx) => `${rx.patient?.firstName || ''} ${rx.patient?.lastName || ''}`.trim(),
    doctor:    (rx) => rx.doctor?.fullName || `${rx.doctor?.firstName || ''} ${rx.doctor?.lastName || ''}`.trim(),
    diagnosis: (rx) => rx.diagnosis,
    medicines: (rx) => (rx.medicines || []).map(m => m.medicineName).join(', '),
    date:      (rx) => rx.prescriptionDate || rx.createdAt,
    status:    (rx) => rxStatusLabel(rx.status),
  }), [filteredPrescriptions, sortConfig]);
  const paging = usePagedRows(sortedPrescriptions, {
    id: isAllDoctors ? 'prescriptions-all' : 'prescriptions',
    resetOn: [sortConfig, searchQuery, filterStatus, dateFilter, customStart, customEnd, doctorId],
  });

  const getStatusBadge = (status) => RX_STATUS_STYLE[status] || 'bg-gray-100 text-gray-700';

  // prescriptionDate is a date with no time on it, so formatting it as a date AND time rendered
  // every row as 12:00 AM — a midnight that was never a real moment, just an empty time field.
  // createdAt carries the actual moment the prescription was started. The plain date is only used
  // if that is somehow missing, so a row shows no time rather than a made-up one.
  const formatRxWhen = (rx) =>
    rx?.createdAt
      ? formatClinicDateTime(rx.createdAt, {}, 'N/A')
      : formatClinicDate(rx?.prescriptionDate, {}, 'N/A');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg">
      {/* Header with Search & Filter */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-t-2xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              Prescription History
              {isAllDoctors && (
                <span className="text-xs font-semibold uppercase bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                  All Doctors
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-600">
              {filteredPrescriptions.length} prescription(s) found
              {` (${activeDateFilterLabel})`}
              {searchQuery && ` - filtered by "${searchQuery}"`}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="ml-2 text-emerald-600 hover:text-emerald-700 font-medium hover:underline transition-colors"
                >
                  Clear filters
                </button>
              )}
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Date filter chips — hidden when the parent locks the date filter
                (e.g. "Today's Prescriptions" tab is always today). */}
            {!lockedDateFilter && (
              <div className="flex items-center gap-1 flex-wrap">
                {PRESC_DATE_FILTERS.map(f => (
                  <button
                    key={f.value}
                    onClick={() => setDateFilter(f.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      dateFilter === f.value
                        ? 'bg-navy-800 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isAllDoctors
                  ? "Search patient, doctor, diagnosis, MR#, Rx#..."
                  : "Search patient, diagnosis, MR#, Rx#..."}
                className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 w-72"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                  title="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            
            {/* Status Filter */}
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              options={RX_STATUS_FILTERS}
              className="min-w-[10rem]"
              buttonClassName="px-3 py-2 rounded-lg"
            />

            {/* Refresh */}
            <button
              onClick={loadPrescriptions}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Custom date range — shown when the "Custom" chip is active. */}
        {!lockedDateFilter && dateFilter === 'custom' && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
              <div className="w-44">
                <DatePicker value={customStart} onChange={setCustomStart} max={customEnd || todayIso} placeholder="Start date" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
              <div className="w-44">
                <DatePicker value={customEnd} onChange={setCustomEnd} min={customStart || undefined} max={todayIso} placeholder="End date" />
              </div>
            </div>
            {(customStart || customEnd) && (
              <button
                onClick={() => { setCustomStart(''); setCustomEnd(''); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg"
              >
                Clear dates
              </button>
            )}
          </div>
        )}
      </div>

      {/* Prescriptions Table */}
      {filteredPrescriptions.length === 0 ? (
        <div className="p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Prescriptions Found</h3>
          <p className="text-gray-500">
            {searchQuery || filterStatus !== 'all'
              ? 'Try adjusting your search or filters'
              : dateFilter === 'today'
                ? isAllDoctors
                  ? 'No prescriptions created today across any doctor. Try switching to "All Time".'
                  : 'No prescriptions created today. Try switching to "All Time".'
                : 'Prescriptions will appear here after you complete them'}
          </p>
        </div>
      ) : (
        <div>
          <TablePager paging={paging} noun="prescription" filteredFrom={dateScopedPrescriptions.length} rounded={false} />
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <SortHeader label="Rx Number" sortKey="rxNumber" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                <SortHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                <SortHeader label="Patient" sortKey="patient" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                {isAllDoctors && (
                  <SortHeader label="Doctor" sortKey="doctor" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                )}
                <SortHeader label="Diagnosis" sortKey="diagnosis" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                <SortHeader label="Medicines" sortKey="medicines" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                <SortHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paging.pageRows.map((rx) => (
                <tr
                  key={rx.id}
                  tabIndex={0}
                  onClick={() => onOpenPatientProfile && onOpenPatientProfile(rx.patient?.id)}
                  onKeyDown={onActivateKey(() => onOpenPatientProfile && onOpenPatientProfile(rx.patient?.id))}
                  title="Open patient profile"
                  className={`hover:bg-gray-50 ${onOpenPatientProfile ? 'cursor-pointer' : ''} ${ROW_FOCUS_RING}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm text-emerald-600">{rx.prescriptionNumber}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatRxWhen(rx)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="font-medium text-gray-900">
                        {rx.patient?.firstName} {rx.patient?.lastName}
                      </p>
                      <p className="text-sm text-gray-500">MR#: {rx.patient?.mrn}</p>
                    </div>
                  </td>
                  {isAllDoctors && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-gradient-to-br from-navy-700 to-navy-900 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                          {(rx.doctor?.firstName?.[0] || '?') + (rx.doctor?.lastName?.[0] || '')}
                        </div>
                        <p className="text-sm text-gray-700">
                          {rx.doctor?.fullName || `Dr. ${rx.doctor?.firstName} ${rx.doctor?.lastName}`}
                        </p>
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900 truncate max-w-xs">
                      {rx.diagnosis || '-'}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    {/* Medicine names written inline (no hover popover); full list with
                        dosages in the native tooltip when truncated. */}
                    {rx.medicines?.length > 0 ? (
                      <p
                        className="text-xs text-gray-700 truncate max-w-[220px]"
                        title={[...rx.medicines]
                          .map(m => `${m.medicineName}${m.strength ? ` ${m.strength}` : ''}${m.dosageInstructionEnglish ? ` · ${m.dosageInstructionEnglish}` : ''}`)
                          .join('\n')}
                      >
                        {[...rx.medicines].map(m => `${m.medicineName}${m.strength ? ` ${m.strength}` : ''}`).join(', ')}
                      </p>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(rx.status)}`}>
                      {rxStatusLabel(rx.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => {
                          setSelectedPrescription(rx);
                          setShowPrintPreview(true);
                        }}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(rx)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <TablePager paging={paging} noun="prescription" filteredFrom={dateScopedPrescriptions.length} position="bottom" />
        </div>
      )}

      {/* Print Preview Modal */}
      {showPrintPreview && selectedPrescription && (
        <PrintPreviewModal
          prescription={selectedPrescription}
          patient={selectedPrescription.patient}
          doctor={selectedPrescription.doctor}
          onClose={() => {
            setShowPrintPreview(false);
            setSelectedPrescription(null);
          }}
          // Hide Edit entirely once the prescription is locked — PrintPreviewModal omits the
          // button when this prop is undefined, so the doctor can't start edits that can't save.
          onEditPrescription={isRxEditable(selectedPrescription) ? () => {
            setShowPrintPreview(false);
            onEditPrescription(selectedPrescription);
            setSelectedPrescription(null);
          } : undefined}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          ref={deleteConfirmDialogRef}
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !deleting) { setDeleteConfirm(null); setDeleteError(null); } }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Delete Prescription</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete prescription <strong className="font-mono">{deleteConfirm.prescriptionNumber}</strong>?
            </p>
            {deleteError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{deleteError}</span>
              </div>
            )}
            <div className="flex space-x-3">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteError(null); }}
                disabled={deleting}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {deleting && <RefreshCw className="w-4 h-4 animate-spin" />}
                <span>{deleting ? 'Deleting...' : 'Delete'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ REFER PATIENT MODAL ============
// The doctor's half of an in-clinic referral: pick a colleague + reason. The visit becomes
// REFERRED, the notes taken so far are kept as the consultation record, and reception settles
// the paid fee with the patient (new token / credit / refund) from the Token Queue.
const ReferPatientModal = ({ visit, doctors = [], hasMedicines = false, onClose, onReferred }) => {
  const showToast = useToast();
  const [doctorList, setDoctorList] = useState(doctors || []);
  const [toDoctorId, setToDoctorId] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const referDialogRef = useDialog(() => { if (!saving) onClose(); });

  useEffect(() => {
    if (!doctorList || doctorList.length === 0) {
      doctorAPI.getAll().then(d => setDoctorList(d || [])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = (doctorList || [])
    .filter(d => d.id !== visit?.doctor?.id)
    .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
  const paid = Number(visit?.finalFee || 0);

  const handleConfirm = async () => {
    if (!toDoctorId) { showToast('warning', 'Pick the doctor to refer to'); return; }
    if (!reason.trim()) { showToast('warning', 'Referral reason is required'); return; }
    setSaving(true);
    try {
      await referralAPI.refer(visit.id, toDoctorId, reason.trim());
      const doc = options.find(d => d.id === toDoctorId);
      onReferred(doc?.fullName || 'the selected doctor');
    } catch (e) {
      showToast('error', e.message || 'Failed to refer patient');
      setSaving(false);
    }
  };

  return (
    <div ref={referDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Refer Patient</h3>
        <p className="text-sm text-gray-500 mb-4">
          Your notes are kept as this consultation's record. Reception will settle the fee with the patient.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Refer to <span className="text-red-500">*</span></label>
            <Select
              value={toDoctorId}
              onChange={setToDoctorId}
              placeholder="Select doctor"
              options={options.map(d => ({
                value: d.id,
                label: (d.fullName || (d.firstName + ' ' + d.lastName)) + (d.specialization ? ' - ' + d.specialization : ''),
              }))}
              buttonClassName="px-3 py-2 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="e.g. Cardiac evaluation needed, refer to cardiologist"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            />
          </div>
          {paid > 0 ? (
            <div className="text-xs text-gray-600 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
              The patient paid <span className="font-semibold">Rs {paid.toLocaleString()}</span> for this consultation.
              Reception will offer them a token to the new doctor, credit for a later visit, or a cash refund.
            </div>
          ) : (
            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              Nothing was paid for this consultation, so there is no fee to settle.
            </div>
          )}
          {hasMedicines && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Medicines already added here stay on this consultation's record but will not be invoiced.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
          >
            {saving ? 'Referring…' : 'Refer Patient'}
          </button>
        </div>
      </div>
    </div>
  );
};

// One row of the prescription's "Medical reports" rail. A row holding something is emerald and
// pulses gently so the doctor notices there is context worth opening before prescribing; an empty
// row stays quiet and grey. Shared by all four rows so they can't drift apart.
const ReportRailRow = ({ icon: Icon, label, count, onClick }) => {
  const hasContent = count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-colors ${
        hasContent
          ? 'border-emerald-300 bg-emerald-50/60 text-emerald-800 hover:bg-emerald-100/70 count-pulse'
          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}
    >
      <span className="inline-flex items-center gap-2"><Icon className="w-4 h-4" />{label}</span>
      {hasContent ? (
        <span className="text-[10px] font-semibold text-white bg-emerald-600 px-1.5 py-0.5 rounded-full">{count}</span>
      ) : (
        <span className="text-[10px] font-medium text-gray-400">No record</span>
      )}
    </button>
  );
};

// ============ PRESCRIPTION FORM COMPONENT ============
const PrescriptionForm = ({ prescription, setPrescription, visit, doctor, onBack, readOnly = false, templateEditMode = false, template = null, doctors = [], allowSaveTemplate = false, canRefer = false }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  // Which doctor's picker lists to show. The prescription's own doctor wins (an admin can open
  // another doctor's Rx); `doctor` covers a brand-new Rx. Template edit mode has neither, so the
  // pickers fall back to the general lists.
  const rxDoctorId = prescription?.doctor?.id || doctor?.id || visit?.doctor?.id || null;
  const rxDoctorSpecialityId =
    prescription?.doctor?.speciality?.id
    || doctor?.speciality?.id || doctor?.specialityId
    || visit?.doctor?.speciality?.id
    || null;
  // Template edit mode: the writer is reused patient-less to edit a saved template. Every
  // template-only behaviour is gated behind `templateEditMode` so the normal Rx flow is unchanged.
  const [templateName, setTemplateName] = useState(template?.templateName || '');
  // Treat a description that merely echoes the name as "no real description" (same rule the
  // template preview uses) so the Edit banner doesn't show the name twice.
  const [templateDescription, setTemplateDescription] = useState(
    template?.description && template.description !== template.templateName ? template.description : ''
  );
  const [templateNameError, setTemplateNameError] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  // A brand-new template (no existing template loaded) → the banner shows a doctor picker since
  // there's no prescription/doctor context to derive the owner from.
  const isNewTemplate = templateEditMode && !template;
  // Default the owner to the current doctor (a doctor-role user files under themselves; admins
  // pick from the list). doctorOptions falls back to the current doctor when the full list isn't
  // loaded (it's only fetched for non-doctor roles).
  const [templateDoctorId, setTemplateDoctorId] = useState(template?.doctor?.id || doctor?.id || '');
  const doctorOptions = (doctors && doctors.length) ? doctors : (doctor ? [doctor] : []);
  // Persist the wizard step per prescription so a browser refresh lands back on the same
  // step (Diagnosis / Orders / After Visit) instead of resetting to Diagnosis.
  const stepKey = prescription?.id ? `prescription_step_${prescription.id}` : null;
  const [activeSection, setActiveSection] = useState(() => {
    try {
      const saved = stepKey ? localStorage.getItem(stepKey) : null;
      return saved || 'diagnosis';
    } catch (e) {
      return 'diagnosis';
    }
  });
  useEffect(() => {
    if (!stepKey) return;
    try { localStorage.setItem(stepKey, activeSection); } catch (e) { /* ignore */ }
  }, [stepKey, activeSection]);
  // Reset the content panel to the top on step change — otherwise the next step inherits the
  // previous step's scroll position (e.g. landing scrolled to the bottom).
  const contentScrollRef = useRef(null);
  useEffect(() => {
    contentScrollRef.current?.scrollTo({ top: 0 });
  }, [activeSection]);
  const [visitedSections, setVisitedSections] = useState(new Set(['vitals']));
  const [saving, setSaving] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  // In-clinic referral modal (doctor hands the patient to a colleague; reception settles the fee).
  const [showReferModal, setShowReferModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [medicalAlerts, setMedicalAlerts] = useState([]);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [showVitalsModal, setShowVitalsModal] = useState(false); // recorded-vitals detail (today/past/trends)
  const [showRetakeVitals, setShowRetakeVitals] = useState(false); // doctor re-records vitals (≤6h window)
  // Clinical-flag prompt (e.g. text mentions "allergy"/"pregnant") shown before leaving Diagnosis.
  const [showFlagPrompt, setShowFlagPrompt] = useState(false);
  const [dismissedFlagTypes, setDismissedFlagTypes] = useState(() => new Set());
  const [alertPrefill, setAlertPrefill] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  // Template the doctor loaded into this prescription (null = none). The footer's Save Template
  // then offers to update that template rather than create a new one.
  const [loadedTemplate, setLoadedTemplate] = useState(null);
  // A template being applied: each line goes to the server in turn, so it takes a moment. The ref
  // turns a second apply away at once; the state keeps the Load Template dialog open and its
  // buttons disabled until it is done. Closing it mid-way would stop nothing, and applying again
  // would plan against the prescription as it was before the first, adding every line twice.
  const [templateApplying, setTemplateApplying] = useState(false);
  const templateApplyingRef = useRef(false);
  const closeConfirmDialogRef = useDialog(() => setShowCloseConfirm(false), { confirmDiscard: false });
  const flagPromptDialogRef = useDialog(() => setShowFlagPrompt(false), { confirmDiscard: false });
  // "Copy previous prescription": the patient's earlier prescriptions (lean summaries, loaded once),
  // the picker, the request in flight, and the last copy's report of what it left out.
  const [rxSummaries, setRxSummaries] = useState([]);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyingFrom, setCopyingFrom] = useState(false);
  const [copyResult, setCopyResult] = useState(null);
  const eligibleCopySources = React.useMemo(
    () => copySourcesFrom(rxSummaries, prescription?.id),
    [rxSummaries, prescription?.id]
  );
  const [patientHistory, setPatientHistory] = useState([]);
  // Counts for the patient-data header link badges. Documents/Lab Reports/Radiology all derive
  // from one documents fetch; vitals from the vitals history; Medical History reuses
  // medicalAlerts; Past Prescriptions reuses patientHistory.
  const [patientDocCount, setPatientDocCount] = useState(0);
  const [patientDocsList, setPatientDocsList] = useState([]); // non-hidden docs for the rail Documents card
  const [labReportCount, setLabReportCount] = useState(0);
  const [radiologyCount, setRadiologyCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historyDetailPrescription, setHistoryDetailPrescription] = useState(null);
  const [showHistoryFullModal, setShowHistoryFullModal] = useState(false);
  const [showPatientDocs, setShowPatientDocs] = useState(false);
  // Doctor-written notes about reports the patient produced without a hard copy. Patient-level, so
  // they survive across visits and are visible to whoever sees the patient next. Never printed.
  const [reportNotes, setReportNotes] = useState([]);
  const [showReportNotes, setShowReportNotes] = useState(false);
  // Which reused patient-data tab (if any) is open in a modal from the header links.
  // null | 'vitals' | 'lab-tests' | 'radiology' | 'medical-history'.
  const [patientDataView, setPatientDataView] = useState(null);
  const [templates, setTemplates] = useState([]);
  const printRef = useRef();
  const diagnosisRef = useRef();
  const [enums, setEnums] = useState({
    frequencies: [],
    timings: [],
    durationUnits: [],
    alertTypes: [],
    severities: []
  });
  const [patientInstructions, setPatientInstructions] = useState([]);
  const [followupTemplates, setFollowupTemplates] = useState([]);

  // Form data. The Diagnosis/Advice fields come through rxFormFields, which the copy path shares.
  const [formData, setFormData] = useState(() => ({
    ...rxFormFields(prescription),
    bpSystolic: prescription.bpSystolic || '',
    bpDiastolic: prescription.bpDiastolic || '',
    pulse: prescription.pulse || '',
    temperature: prescription.temperature || '',
    weight: prescription.weight || '',
    height: prescription.height || '',
    bloodSugar: prescription.bloodSugar || '',
    spo2: prescription.spo2 || '',
    respiratoryRate: prescription.respiratoryRate || '',
    // Pediatric extras — only populated for younger patients but read regardless so the
    // doctor's readonly panel surfaces them whenever the nurse captured them.
    lengthCm: prescription.lengthCm || '',
    headCircumferenceCm: prescription.headCircumferenceCm || '',
    muacCm: prescription.muacCm || '',
    privateNotesEncrypted: '',
    pastLabReports: prescription.pastLabReports || '',
    createAppointment: prescription.createAppointment || false,
  }));

  const patient = visit?.patient;
  // Effective doctor for this prescription — falls back to the prescription's / visit's own
  // doctor when the current-doctor context is null (admin "All Doctors" mode). Used for loading
  // and saving templates so they're keyed to the prescribing doctor either way.
  const effectiveDoctorId = doctor?.id || prescription?.doctor?.id || visit?.doctor?.id;
  // How this user may see the private notes: AUTHOR (read+write), ADMIN or GRANTED (read only),
  // or NONE. Decided by the server, never by the client.
  const [allDoctors, setAllDoctors] = useState([]);
  // Pending (typed-but-not-yet-"Added") Chief Complaint, reported up from DiagnosisSection so it
  // counts toward the required-field check and can be auto-committed when leaving the step.
  const [pendingComplaint, setPendingComplaint] = useState('');

  // ---------- Auto-save draft to localStorage ----------
  const draftKey = prescription?.id ? `prescription_draft_${prescription.id}` : null;

  // Dirty-tracking: only write a recovery draft (and therefore only ever show the
  // "Resume Unsaved Changes?" prompt) once the form has actually diverged from the loaded /
  // last-saved prescription. Previously the 10s auto-save wrote a draft even when the doctor
  // only viewed the Rx, so re-opening it via Edit always triggered a spurious resume prompt.
  // privateNotesEncrypted is excluded from the fingerprint because it loads asynchronously after
  // mount (and is persisted server-side regardless).
  const fingerprint = useCallback((fd) => {
    const { privateNotesEncrypted, ...rest } = fd;
    return JSON.stringify(rest);
  }, []);
  const baselineRef = useRef(null);
  if (baselineRef.current === null) baselineRef.current = fingerprint(formData);

  // The same for the fields the doctor changes on the prescription itself rather than in formData
  // (LOCAL_ONLY_RX_FIELDS: today the bedside sugar tick). They reach the server only with the next
  // save, so a change to them is unsaved work too: it keeps a recovery draft and asks before
  // closing. Snapshotted as the prescription opens and again whenever a save or completion takes
  // the server's copy.
  const localBaselineRef = useRef(null);
  if (localBaselineRef.current === null) localBaselineRef.current = localRxFields(prescription);
  // Both baselines are read when called, not when rendered, so a save that has just moved them
  // is seen by a draft timer or unload handler set up before it.
  const localUnsaved = () => localRxEditsDiffer(localBaselineRef.current, prescription);
  const unsavedWork = () => fingerprint(formData) !== baselineRef.current || localUnsaved();
  const recoveryDraft = () => JSON.stringify({ formData, local: localRxFields(prescription), savedAt: Date.now() });
  // Stable while the local fields hold still, so the draft effects below re-run only when they move.
  const localFieldsJson = JSON.stringify(localRxFields(prescription));

  // On mount, if a saved draft exists and the current prescription hasn't moved past DRAFT,
  // offer to restore the user's work.
  useEffect(() => {
    if (readOnly || !draftKey) return;
    const status = prescription?.status;
    if (status && status !== 'INITIATED' && status !== 'DRAFT') return;

    const promptToRestore = async () => {
      try {
        const raw = localStorage.getItem(draftKey);
        if (!raw) return;
        // `local` holds the prescription's own unsaved fields (the bedside sugar tick). Drafts
        // written before it was kept have only formData.
        const { formData: savedForm, local: savedLocal, savedAt } = JSON.parse(raw);
        if (!savedForm && !savedLocal) return;
        // Only offer restore if the saved draft is newer than the prescription's last update
        const prescriptionUpdated = prescription?.updatedAt ? new Date(prescription.updatedAt).getTime() : 0;
        if (savedAt && savedAt > prescriptionUpdated) {
          const savedDate = new Date(savedAt).toLocaleString();
          const ok = await confirm({
            title: 'Resume Unsaved Changes?',
            subtitle: `Auto-saved on ${savedDate}`,
            message: 'We found unsaved changes from your previous session. Would you like to restore them?',
            confirmText: 'Restore',
            cancelText: 'Discard',
            tone: 'info',
          });
          if (ok) {
            if (savedForm) setFormData(prev => ({ ...prev, ...savedForm }));
            // The tick comes back too; it stays unsaved (it differs from localBaselineRef) until saved.
            if (savedLocal) setPrescription(prev => restoreLocalRxEdits(prev, savedLocal));
          } else {
            localStorage.removeItem(draftKey);
          }
        }
      } catch (err) {
        console.warn('Failed to read draft:', err);
      }
    };

    promptToRestore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Debounced auto-save: write a recovery draft ~1.2s after the doctor stops typing (only once the
  // form is genuinely dirty, so merely opening a prescription never leaves a draft behind). A short
  // debounce — not a long interval — means a refresh loses at most a second of work.
  // A tick of the bedside sugar box counts as well as typing (unsavedWork), and the draft carries it.
  useEffect(() => {
    if (readOnly || !draftKey) return;
    if (!unsavedWork()) return; // unchanged — nothing to save
    const t = setTimeout(() => {
      if (!unsavedWork()) return; // saved meanwhile
      try {
        localStorage.setItem(draftKey, recoveryDraft());
      } catch (err) {
        // localStorage full / private mode — fail silently
      }
    }, 1200);
    return () => clearTimeout(t);
    // unsavedWork and recoveryDraft read formData and the local fields, both listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, localFieldsJson, draftKey, readOnly, fingerprint]);

  // Safety net: persist the draft synchronously on page unload (refresh / close / navigate away),
  // so work typed within the debounce window isn't lost and the restore prompt has it next time.
  useEffect(() => {
    if (readOnly || !draftKey) return;
    const onBeforeUnload = () => {
      if (!unsavedWork()) return;
      try {
        localStorage.setItem(draftKey, recoveryDraft());
      } catch (err) { /* ignore */ }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, localFieldsJson, draftKey, readOnly, fingerprint]);

  const clearDraft = () => {
    if (draftKey) {
      try { localStorage.removeItem(draftKey); } catch (err) { /* ignore */ }
    }
    if (stepKey) {
      try { localStorage.removeItem(stepKey); } catch (err) { /* ignore */ }
    }
  };

  useEffect(() => {
    loadEnums();
    loadConfigurations();
    if (patient?.id) {
      loadMedicalAlerts();
      loadCopySources();
      loadPatientHistory();
      loadDocumentCounts();
      loadReportNotes();
    }
    // Templates are shared system-wide — always load them.
    loadTemplates();
    // Always ask, even when the prescription has no private note yet: this call is what tells us
    // whether the viewer is the author, and only the author may type. Gating it on
    // `hasPrivateNotes` left access at 'NONE' on every prescription without a note, which disabled
    // the box for the author too — so the first note could never be written.
    if (prescription?.id) {
      loadPrivateNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id, doctor?.id, effectiveDoctorId]);

  const loadReportNotes = async () => {
    try {
      const notes = await patientReportNoteAPI.list(patient.id);
      // The rail is PREVIOUS history only: hide the note this very prescription generated on
      // finalize (its text already sits in the Past Lab Reports section below).
      const list = (Array.isArray(notes) ? notes : [])
        .filter(n => !n.sourcePrescriptionId || n.sourcePrescriptionId !== prescription?.id);
      setReportNotes(list);
    } catch (err) {
      console.error('Failed to load report notes:', err);
      setReportNotes([]);
    }
  };

  // The note is stored encrypted and returned decrypted to anyone who can open the prescription,
  // so this is a plain fetch with no access branches left.
  const loadPrivateNotes = async () => {
    try {
      const result = await prescriptionAPI.getPrivateNotes(prescription.id);
      setFormData(prev => ({ ...prev, privateNotesEncrypted: result.notes || '' }));
    } catch (err) {
      console.error('Failed to load private notes:', err);
    }
  };

  // The patient's earlier prescriptions for "Copy previous prescription", as lean summaries. Only a
  // prescription still being started can be filled from one, so no other view asks for them.
  const loadCopySources = async () => {
    if (readOnly || templateEditMode || !COPY_TARGET_STATUSES.includes(prescription?.status)) return;
    try {
      const list = await prescriptionAPI.getPatientSummaries(patient.id);
      setRxSummaries(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load earlier prescriptions:', err);
    }
  };

  const loadPatientHistory = async () => {
    setHistoryLoading(true);
    try {
      const all = await prescriptionAPI.getPatientPrescriptions(patient.id);
      const filtered = (all || [])
        .filter(rx => rx.id !== prescription.id && !rx.isTemplate)
        .slice(0, 10);
      setPatientHistory(filtered);
    } catch (err) {
      console.log('No patient history found');
    } finally {
      setHistoryLoading(false);
    }
  };

  // One documents fetch → Documents, Lab Reports (LAB_REPORT) and Radiology counts for the
  // header link badges. The Documents count excludes the types that have their own links, so it
  // matches what the Documents modal actually shows.
  const loadDocumentCounts = async () => {
    try {
      const docs = await patientDocumentAPI.getByPatient(patient.id);
      const list = Array.isArray(docs) ? docs : [];
      const visibleDocs = list.filter(d => !DOCS_HIDDEN_TYPES.has(d.documentType));
      setPatientDocCount(visibleDocs.length);
      setPatientDocsList(visibleDocs);
      setLabReportCount(list.filter(d => d.documentType === 'LAB_REPORT').length);
      setRadiologyCount(list.filter(d => RADIOLOGY_DOC_TYPES.has(d.documentType)).length);
    } catch (err) {
      setPatientDocCount(0);
      setPatientDocsList([]);
      setLabReportCount(0);
      setRadiologyCount(0);
    }
  };

  const handleViewHistoryPrescription = async (rx) => {
    try {
      const full = await prescriptionAPI.getById(rx.id);
      setHistoryDetailPrescription(full);
    } catch (err) {
      console.error('Failed to load prescription details:', err);
      showToast('error', 'Failed to load prescription details');
    }
  };

  const loadTemplates = async () => {
    try {
      // Templates are shared system-wide — load all active templates regardless of doctor.
      const data = await prescriptionAPI.getAllTemplates();
      setTemplates(data || []);
    } catch (err) {
      console.log('No templates found');
    }
  };

  // Whether a copy would overwrite something: a medicine or lab test line, text in any field the
  // copy fills (saved earlier or typed now), a complaint typed but not yet Added, or any unsaved
  // change at all. Drives the picker's warning, its "Replace" wording and the replace flag.
  const copyTargetHasContent = () =>
    (prescription?.medicines?.length || 0) > 0
    || (prescription?.labTests?.length || 0) > 0
    || !!(formData.diagnosis || '').trim()
    || !!(formData.clinicalNotes || '').trim()
    || !!(formData.examination || '').trim()
    || (formData.complaints || []).length > 0
    || (formData.selectedInstructions || []).length > 0
    || !!formData.followupDays
    || !!pendingComplaint.trim()
    || (baselineRef.current !== null && fingerprint(formData) !== baselineRef.current);

  // Fill this prescription from one of the patient's earlier ones. The server does the whole copy
  // in one transaction and re-checks every medicine against today's stock; the form then takes the
  // copied text from the prescription it sends back, so the next save sends that text again rather
  // than blanking it with what the form held before.
  const handleCopyFromPrevious = async (source) => {
    if (!source || copyingFrom) return;
    setCopyingFrom(true);
    try {
      const resp = await prescriptionAPI.copyFrom(prescription.id, source.prescriptionId, copyTargetHasContent());
      const copied = resp?.prescription;
      if (!copied?.id) throw new Error('The server did not send the copied prescription back.');
      // The bedside sugar tick lives only here until the next save, and the server never saw it,
      // so the copy would send it back unticked. The copy leaves it alone, so keep what is on screen.
      setPrescription(prev => keepLocalRxEdits(prev, copied));
      const fields = rxFormFields(copied, { freshComplaintIds: true });
      // Everything else in the form (vitals, private notes, past lab reports) stays as it is. The
      // copied fields are what the server now holds, so they join the "unchanged" baseline; any
      // other field keeps its old baseline, so an unsaved vitals or past-report edit is still
      // unsaved. Set in the same update so the auto-save effect of this render compares against it.
      setFormData(prev => {
        const next = { ...prev, ...fields };
        baselineRef.current = fingerprint(baselineAfterCopy(baselineRef.current, next, fields) || next);
        return next;
      });
      // A recovery draft from before the copy would offer the old text back on the next open. If
      // something outside the copy is still unsaved, the auto-save writes a fresh draft for it.
      if (draftKey) {
        try { localStorage.removeItem(draftKey); } catch (e) { /* ignore */ }
      }
      // A copy is not a template: Save Template must not offer to update one loaded earlier.
      setLoadedTemplate(null);
      const copiedFrom = resp.copiedFrom || {
        id: source.prescriptionId,
        number: source.prescriptionNumber,
        date: source.prescriptionDate,
        doctorName: source.doctorName,
      };
      setCopyResult({
        copiedFrom,
        skipped: Array.isArray(resp.skipped) ? resp.skipped : [],
        warnings: Array.isArray(resp.warnings) ? resp.warnings : [],
        complaintDurationsCleared: !!resp.complaintDurationsCleared,
      });
      setShowCopyModal(false);
      // Stay on Diagnosis, scrolled up to the banner that says what was and was not copied.
      contentScrollRef.current?.scrollTo?.({ top: 0 });
      // Alerts can have changed since the screen opened (the intake form adds them), and the doctor
      // is about to review copied medicines against them.
      loadMedicalAlerts();
      showToast('success', `Copied from ${copiedFrom.number || source.prescriptionNumber}. Review each step before completing.`);
    } catch (err) {
      console.error('Failed to copy prescription:', err);
      if (err?.code === 'TARGET_NOT_EMPTY') {
        // Lines were added to this prescription meanwhile (another tab, say). Reload it so the
        // picker, still open, offers to replace them instead.
        showToast('error', err.message);
        try {
          const fresh = await prescriptionAPI.getById(prescription.id);
          // As above: an unsaved bedside sugar tick is not on the server yet.
          setPrescription(prev => keepLocalRxEdits(prev, fresh));
        } catch (e) { /* the message above already says what happened */ }
      } else {
        showToast('error', 'Could not copy: ' + (err?.message || 'unknown error'));
      }
    } finally {
      setCopyingFrom(false);
    }
  };

  const loadEnums = async () => {
    try {
      const [frequencies, timings, durationUnits, alertTypes, severities] = await Promise.all([
        prescriptionAPI.getFrequencies(),
        prescriptionAPI.getTimings(),
        prescriptionAPI.getDurationUnits(),
        prescriptionAPI.getAlertTypes(),
        prescriptionAPI.getSeverities(),
      ]);
      setEnums({ frequencies, timings, durationUnits, alertTypes, severities });
    } catch (err) {
      console.error('Failed to load enums:', err);
    }
  };

  const loadConfigurations = async () => {
    try {
      const [instructions, followups] = await Promise.all([
        // Scoped to this doctor's speciality (their own chips first, then the general ones).
        prescriptionAPI.getConfig('PATIENT_INSTRUCTION', rxDoctorId),
        prescriptionAPI.getConfig('FOLLOWUP_TEMPLATE'),
      ]);
      setPatientInstructions(instructions);
      setFollowupTemplates(followups);
    } catch (err) {
      console.error('Failed to load configurations:', err);
    }
  };

  const loadMedicalAlerts = async () => {
    try {
      const alerts = await prescriptionAPI.getPatientAlerts(patient.id);
      setMedicalAlerts(alerts);
      
      // Show alert modal if first time patient has no alerts
      if (alerts.length === 0) {
        // For now, just log - you might want to show modal
        console.log('First visit - should capture medical alerts');
      }
    } catch (err) {
      console.error('Failed to load medical alerts:', err);
    }
  };

  const handleSave = async (complete = false, { silent = false } = {}) => {
    setSaving(true);
    try {
      // Auto-add any pending complaint before saving
      let complaintsToSave = [...formData.complaints];
      if (diagnosisRef.current) {
        const pending = diagnosisRef.current.getPendingComplaint();
        if (pending && pending.complaint.trim()) {
          complaintsToSave.push({ ...pending, id: Date.now() });
          diagnosisRef.current.clearPendingComplaint();
          setFormData(prev => ({ ...prev, complaints: complaintsToSave }));
        }
      }

      // An already-finalised prescription may still be amended, but it must never be saved back
      // with a required field emptied — that is how a PRESCRIBED record ended up carrying a blank
      // clinical history while still showing its Prescribed badge. Drafts stay free-form: parking
      // incomplete work is the whole point of a draft. Enforced here rather than on the buttons so
      // every save path is covered, including "Save & Close" in the close-confirmation modal.
      if (prescription?.status === 'PRESCRIBED') {
        const missing = missingDiagnosisFields(formData, complaintsToSave);
        if (missing.length > 0) {
          showToast('error', `This prescription is already completed, so ${missing.join(', ')} cannot be left empty.`);
          return false;
        }
      }

      const updateData = {
        diagnosis: formData.diagnosis,
        complaints: JSON.stringify(complaintsToSave),
        bpSystolic: formData.bpSystolic ? parseInt(formData.bpSystolic) : null,
        bpDiastolic: formData.bpDiastolic ? parseInt(formData.bpDiastolic) : null,
        pulse: formData.pulse ? parseInt(formData.pulse) : null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        bloodSugar: formData.bloodSugar ? parseInt(formData.bloodSugar) : null,
        spo2: formData.spo2 ? parseInt(formData.spo2) : null,
        clinicalNotes: formData.clinicalNotes,
        privateNotesEncrypted: formData.privateNotesEncrypted || null,
        examination: formData.examination,
        pastLabReports: formData.pastLabReports,
        patientInstructions: JSON.stringify(formData.selectedInstructions),
        followupDays: formData.followupDays ? parseInt(formData.followupDays) : null,
        createAppointment: formData.createAppointment,
        consultationOnly: formData.consultationOnly,
        // The doctor's tick for a glucometer reading at the clinic. Held on the prescription
        // itself, not in formData, because the Lab Tests card owns it.
        bedsideBloodSugar: !!prescription.bedsideBloodSugar,
      };

      await prescriptionAPI.update(prescription.id, updateData);

      // Always fetch fresh data after update to get complete prescription
      const refreshed = await prescriptionAPI.getById(prescription.id);

      // The form now matches what's persisted — reset the dirty baseline so post-save idling
      // doesn't recreate a draft (and re-opening won't show the resume prompt).
      baselineRef.current = fingerprint({ ...formData, complaints: complaintsToSave });
      // Likewise the bedside sugar tick, which this save has just sent: the server's copy is saved.
      localBaselineRef.current = localRxFields(refreshed);

      if (complete) {
        // Persist the latest edits and open the print preview — but do NOT finalise (status →
        // PRESCRIBED) yet. Completion happens only when the user clicks Print or Done in the
        // preview (see handleFinalizeComplete), so closing the preview with X leaves the
        // prescription as a draft, untouched.
        setPrescription(refreshed);
        setShowPrintPreview(true);
      } else {
        setPrescription(refreshed);
        clearDraft();
        if (!silent) showToast('success', 'Prescription saved successfully!');
      }
      return true;
    } catch (err) {
      console.error('Failed to save prescription:', err);
      showToast('error', 'Failed to save: ' + err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Finalise the prescription (status → PRESCRIBED). Invoked ONLY from the print preview's Print /
  // Done actions — never just by opening the preview — so a user who closes the preview with X
  // leaves the prescription as a draft. Returns true on success.
  const handleFinalizeComplete = async () => {
    try {
      await prescriptionAPI.complete(prescription.id);
      const finalData = await prescriptionAPI.getById(prescription.id);
      localBaselineRef.current = localRxFields(finalData);
      setPrescription(finalData);
      clearDraft();
      return true;
    } catch (err) {
      console.error('Failed to complete prescription:', err);
      showToast('error', 'Failed to complete: ' + err.message);
      return false;
    }
  };

  // Builds the template payload from the current prescription/formData. `overrides` lets the
  // caller set id (update) / templateName / description. The content itself (every medicine's
  // full dosing, lab tests with their Urdu) comes from templateContent, which SaveTemplateModal
  // shares, so the two ways of saving a template store the same thing.
  const buildTemplatePayload = (overrides = {}) => ({
    doctorId: effectiveDoctorId,
    templateName: '',
    description: '',
    ...templateContent(formData, prescription),
    ...overrides,
  });

  // Complete & Print — completes and prints straight away. Saving as a template is now a
  // separate, explicit "Save Template" button in the footer (shown once mandatory fields are
  // filled), so this no longer pops up any "save as template?" prompt.
  const handleCompleteClick = () => {
    if (!canComplete) return;
    handleSave(true);
  };

  // Template edit mode — save the edited template in place. Nothing touches the server until this
  // explicit save: medicines/labs were edited locally on `prescription`, and buildTemplatePayload
  // serialises both formData and prescription.medicines/labTests, so the round-trip is faithful.
  const handleSaveTemplateEdit = async () => {
    const name = templateName.trim();
    if (!name) {
      setTemplateNameError('Please enter a template name');
      return;
    }
    // Client-side duplicate check (case-insensitive), excluding the template being edited. The
    // backend enforces uniqueness too — its error is surfaced below if this misses.
    const dup = (templates || []).some(
      t => t.id !== template?.id && (t.templateName || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (dup) {
      setTemplateNameError('A template with this name already exists');
      return;
    }
    setTemplateNameError('');
    setSavingTemplate(true);
    try {
      await prescriptionAPI.saveTemplate(buildTemplatePayload({
        id: template?.id,
        // Preserve the original creator when an admin (no current-doctor context) edits.
        doctorId: effectiveDoctorId || template?.doctor?.id || null,
        templateName: name,
        description: templateDescription.trim(),
      }));
      showToast('success', `Template "${name}" updated`);
      onBack();
    } catch (err) {
      const msg = err.message || 'Failed to save template';
      setTemplateNameError(msg);
      showToast('error', msg);
    } finally {
      setSavingTemplate(false);
    }
  };

  // ---- Template-edit dirty tracking. We snapshot the seeded state once on mount; until something
  // changes the footer shows only "Close", and once it's dirty it offers "Update" (overwrite) or
  // "Save as New" (create a copy). Every line's full dosing is in the snapshot (templateContent),
  // so correcting only a dose counts as a change. ----
  const serializeTemplateState = () => {
    const p = buildTemplatePayload();
    return JSON.stringify({
      name: templateName.trim(),
      description: templateDescription.trim(),
      diagnosis: p.diagnosis, complaintsJson: p.complaintsJson, clinicalNotes: p.clinicalNotes,
      examination: p.examination,
      consultationOnly: p.consultationOnly, followupDays: p.followupDays,
      medicinesJson: p.medicinesJson, labTestsJson: p.labTestsJson, instructionsJson: p.instructionsJson,
    });
  };
  const templateEditBaselineRef = useRef(null);
  useEffect(() => {
    if (templateEditMode) templateEditBaselineRef.current = serializeTemplateState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const templateDirty =
    templateEditMode && templateEditBaselineRef.current !== null
    && serializeTemplateState() !== templateEditBaselineRef.current;

  // Save the edited content as a BRAND-NEW template (no id). The name must not collide with ANY
  // existing template (including the one being edited), so the user renames before saving a copy.
  const handleSaveTemplateAsNew = async () => {
    const name = templateName.trim();
    if (!name) { setTemplateNameError('Please enter a template name'); return; }
    const dup = (templates || []).some(
      t => (t.templateName || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (dup) { setTemplateNameError('A template with this name already exists - rename to save a copy'); return; }
    // A template must be filed under a doctor. New templates use the banner's doctor picker;
    // a "Save as New" copy falls back to the current/loaded doctor.
    const doctorId = templateDoctorId || effectiveDoctorId || template?.doctor?.id || null;
    if (!doctorId) { setTemplateNameError('Please select a doctor for this template'); return; }
    setTemplateNameError('');
    setSavingTemplate(true);
    try {
      await prescriptionAPI.saveTemplate(buildTemplatePayload({
        doctorId,
        templateName: name,
        description: templateDescription.trim(),
      }));
      showToast('success', `Template "${name}" saved`);
      onBack();
    } catch (err) {
      const msg = err.message || 'Failed to save template';
      setTemplateNameError(msg);
      showToast('error', msg);
    } finally {
      setSavingTemplate(false);
    }
  };

  // 3 logical screens. Vitals are no longer a step here — they're recorded by the nurse BEFORE
  // the patient enters the doctor's queue. The doctor sees them as a read-only panel at the top.
  // "Treatment" (id 'orders') bundles Medicines + Lab Tests; "Advice & Follow-up" (id
  // 'after-visit') bundles Instructions + Follow-up. (Internal ids kept as-is.) When a visit is
  // marked Consultation Only on the Diagnosis screen, both are hidden entirely so the doctor can
  // complete the prescription in fewer clicks.
  const allSections = [
    { id: 'diagnosis', label: 'Diagnosis', tab: 'Diagnosis', icon: Stethoscope },
    { id: 'orders', label: 'Treatment', tab: 'Treatment', icon: Pill, sublabel: 'Medicines & Lab Tests' },
    { id: 'after-visit', label: 'Advice & Follow-up', tab: 'Advice', icon: Calendar, sublabel: 'Instructions & Follow-up' },
  ];
  // Consultation only skips the Treatment step only (Advice & Follow-up stays reachable so the
  // doctor can still record advice + follow-up) — matches the redesigned Clinical Workspace.
  const sections = formData.consultationOnly
    ? allSections.filter(s => s.id !== 'orders')
    : allSections;

  // If the user is on the Treatment section when Consultation Only is toggled on, bounce back to Diagnosis
  useEffect(() => {
    if (formData.consultationOnly && activeSection === 'orders') {
      setActiveSection('diagnosis');
    }
  }, [formData.consultationOnly, activeSection]);


  const currentIndex = sections.findIndex(s => s.id === activeSection);
  const isFirstSection = currentIndex === 0;
  const isLastSection = currentIndex === sections.length - 1;

  // Required fields on the Diagnosis step — all must be filled before leaving the step (Next) or
  // completing the prescription.
  // A typed-but-not-yet-Added complaint counts — it's auto-committed when leaving the step.
  const diagnosisFieldBlockers = missingDiagnosisFields(
    formData,
    (formData.complaints || []).length > 0
      ? formData.complaints
      : (pendingComplaint.trim() ? [{ complaint: pendingComplaint }] : []),
  );

  // Completion requires only the documented Diagnosis-step fields. Medicines & lab tests are
  // optional (a visit may legitimately need neither), so they no longer block Complete & Print —
  // consistent with the stepper letting you advance past Treatment without them. The Consultation
  // Only toggle remains as a shortcut to skip the Treatment / Advice steps.
  const completionBlockers = [...diagnosisFieldBlockers];

  const canComplete = completionBlockers.length === 0;

  // Amending a finalised (PRESCRIBED) prescription is allowed, but it may not be saved with a
  // required field blank — the same rule handleSave enforces for every save path. Surfaced here so
  // the footer can disable Save and say why, matching how Continue and Complete already behave.
  const isFinalisedRx = prescription?.status === 'PRESCRIBED';
  const amendBlockers = isFinalisedRx ? diagnosisFieldBlockers : [];
  const saveBlocked = amendBlockers.length > 0;

  // Block leaving the Diagnosis step until its required fields are filled.
  const nextBlockers = activeSection === 'diagnosis' ? diagnosisFieldBlockers : [];
  const nextBlocked = nextBlockers.length > 0;

  // Auto-commit a typed-but-not-"Added" Chief Complaint so it isn't lost on leaving the step.
  const commitPendingComplaint = () => {
    if (diagnosisRef.current) {
      const pending = diagnosisRef.current.getPendingComplaint?.();
      if (pending && pending.complaint.trim()) {
        setFormData(prev => ({ ...prev, complaints: [...prev.complaints, { ...pending, id: Date.now() }] }));
        diagnosisRef.current.clearPendingComplaint();
        setPendingComplaint('');
      }
    }
  };

  // Clinical-safety flags detected in the doctor's free text, minus any already covered by an
  // existing patient alert or dismissed ("not needed") this session. Drives the prompt shown when
  // leaving the Diagnosis step.
  const pendingClinicalFlags = (() => {
    const text = [
      formData.diagnosis,
      formData.examination,
      formData.clinicalNotes,
      formData.pastLabReports,
      pendingComplaint,
      (formData.complaints || []).map(c => c.complaint).join(' '),
    ].join(' ');
    return detectClinicalFlags(text).filter(f =>
      !dismissedFlagTypes.has(f.alertType)
      && !medicalAlerts.some(a => a.alertType === f.alertType)
    );
  })();

  const advanceToNext = () => {
    commitPendingComplaint();
    if (!isLastSection) {
      const nextSection = sections[currentIndex + 1];
      setActiveSection(nextSection.id);
      setVisitedSections(prev => new Set([...prev, nextSection.id]));
    }
  };

  const goToNext = () => {
    if (nextBlocked) return;
    // Before leaving Diagnosis, offer to record any detected clinical flag (allergy, pregnancy…)
    // as a persistent patient alert. Non-blocking — the prompt lets the doctor continue anyway.
    if (activeSection === 'diagnosis' && pendingClinicalFlags.length > 0) {
      setShowFlagPrompt(true);
      return;
    }
    advanceToNext();
  };

  const goToPrevious = () => {
    if (!isFirstSection) {
      setActiveSection(sections[currentIndex - 1].id);
    }
  };

  // All sections are freely navigable (nurse already gated the workflow by recording vitals
  // before the patient reached the doctor's queue — no wizard-level locks here anymore).
  // eslint-disable-next-line no-unused-vars
  const canNavigateTo = (_sectionId) => true;

  const handleBackClick = () => {
    const isIncomplete = prescription.status === 'INITIATED' || prescription.status === 'DRAFT';
    // Also warn when editing an already-finalised prescription (e.g. PRESCRIBED) that has unsaved
    // form changes — otherwise closing silently discards the edits. A bedside sugar tick not yet
    // saved counts too: it is not in formData, and closing would drop it (no lancet or strip billed).
    const isDirty = (baselineRef.current !== null && fingerprint(formData) !== baselineRef.current)
      || localUnsaved();
    if (!readOnly && (isIncomplete || isDirty)) {
      setShowCloseConfirm(true);
    } else {
      onBack();
    }
  };

  // Vitals assessment for the header-band card (nurse-recorded; advisory only). null when none.
  const vitalsStatus = (() => {
    const f = formData;
    const hasAny = f.bpSystolic || f.bpDiastolic || f.pulse || f.temperature || f.weight
      || f.bloodSugar || f.spo2 || f.respiratoryRate || f.muacCm;
    if (!hasAny) return null;
    const band = getAgeBandKey(patient?.dateOfBirth);
    const a = assessVitals(
      { bpSystolic: f.bpSystolic, bpDiastolic: f.bpDiastolic, pulse: f.pulse, temperature: f.temperature,
        spo2: f.spo2, respiratoryRate: f.respiratoryRate, bloodSugar: f.bloodSugar, muacCm: f.muacCm },
      band,
    );
    return { ...a, ageLabel: BAND_LABEL[band] || 'patient' };
  })();

  // "Copy previous prescription" is offered only while a new prescription is being started and the
  // patient has a finished one to copy. Never opened on its own: the blocking intake form and the
  // resume-draft question can both appear as the screen opens.
  const showCopyStrip = !readOnly && !templateEditMode && !!patient
    && COPY_TARGET_STATUSES.includes(prescription?.status)
    && eligibleCopySources.length > 0;
  // The source of the last copy: this session's answer, or after a reload the prescription's own
  // link, with its date and doctor looked up in the summaries already loaded.
  const copiedFromInfo = copyResult?.copiedFrom || (prescription?.copiedFromNumber ? (() => {
    const s = rxSummaries.find(x => x.prescriptionId === prescription.copiedFromId);
    return {
      id: prescription.copiedFromId,
      number: prescription.copiedFromNumber,
      date: s ? (s.prescriptionDate || s.createdAt) : null,
      doctorName: s ? s.doctorName : null,
    };
  })() : null);

  return (
    <div className="h-full flex flex-col">
      {/* ===== TOP HEADER BAND — patient + vitals (left), consultation + use-template (right) ===== */}
      {!templateEditMode && patient && (
        <div className="flex-shrink-0 mb-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap min-w-0">
            <PatientIdentityHeader patient={patient} visit={visit} prescription={prescription} />
            {vitalsStatus && (() => {
              const sev = vitalsStatus.severity;
              const tone = sev === 'normal'
                ? { card: 'border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50', label: 'text-emerald-700', cell: 'border-emerald-100' }
                : sev === 'mild'
                ? { card: 'border-amber-300 bg-amber-50/70 hover:bg-amber-50', label: 'text-amber-700', cell: 'border-amber-200' }
                : { card: 'border-red-300 bg-red-50/70 hover:bg-red-50', label: 'text-red-700', cell: 'border-red-200' };
              const verdict = sev === 'normal' ? 'normal' : sev === 'mild' ? 'borderline' : 'abnormal';
              const f = formData;
              // Only the vitals that were actually recorded — across the full set, not a fixed four.
              // `sevKey` maps a reading to its assessVitals issue label so each cell can be tinted
              // by its OWN severity (red = abnormal, amber = borderline).
              const items = [
                (f.bpSystolic && f.bpDiastolic) && { label: 'BP', value: `${f.bpSystolic}/${f.bpDiastolic}`, sevKey: 'BP' },
                f.pulse && { label: 'Pulse', value: f.pulse, sevKey: 'Pulse' },
                f.temperature && { label: 'Temp', value: `${f.temperature}°F`, sevKey: 'Temp' },
                f.respiratoryRate && { label: 'Resp', value: `${f.respiratoryRate}/min`, sevKey: 'Resp Rate' },
                f.spo2 && { label: 'SpO₂', value: `${f.spo2}%`, sevKey: 'SpO₂' },
                f.weight && { label: 'Weight', value: `${f.weight}kg` },
                f.height && { label: 'Height', value: `${f.height}cm` },
                f.lengthCm && { label: 'Length', value: `${f.lengthCm}cm` },
                f.headCircumferenceCm && { label: 'Head', value: `${f.headCircumferenceCm}cm` },
                f.muacCm && { label: 'MUAC', value: `${f.muacCm}cm`, sevKey: 'MUAC' },
                f.bloodSugar && { label: 'Sugar', value: `${f.bloodSugar}`, sevKey: 'Blood Sugar' },
              ].filter(Boolean);
              const sevByLabel = {};
              (vitalsStatus.issues || []).forEach((i) => { sevByLabel[i.label] = i.severity; });
              const cellTone = (sevKey) => {
                const s = sevKey ? sevByLabel[sevKey] : null;
                if (s === 'concern' || s === 'alarm') return 'bg-red-50 border-red-300 text-red-800';
                if (s === 'mild') return 'bg-amber-50 border-amber-300 text-amber-800';
                return `bg-white text-gray-800 ${tone.cell}`;
              };
              // Out-of-range readings blink to demand attention: abnormal (concern/alarm) in the
              // same rose pulse as the Medical Alerts card, borderline (mild) in an amber pulse so
              // it reads as "review" rather than a red emergency.
              const cellPulse = (sevKey) => {
                const s = sevKey ? sevByLabel[sevKey] : null;
                if (s === 'concern' || s === 'alarm') return 'alert-pulse';
                if (s === 'mild') return 'alert-pulse-amber';
                return '';
              };
              return (
                <button type="button" onClick={() => setShowVitalsModal(true)} title="Vitals - tap for details, historic readings & trends" className={`rounded-xl border p-2.5 transition-colors ${tone.card}`}>
                  <div className="flex items-center gap-3">
                    <div className="text-left">
                      <div className={`text-[10px] font-semibold uppercase tracking-wider ${tone.label}`}>Vitals</div>
                      <div className={`text-[11px] font-semibold inline-flex items-center gap-1 ${tone.label}`}>
                        {sev === 'normal' ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                        {verdict} · {vitalsStatus.ageLabel}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {items.map((it) => (
                        <div key={it.label} className={`px-2.5 py-1 rounded-lg border ${cellTone(it.sevKey)} ${cellPulse(it.sevKey)}`}>
                          <div className="text-[9px] leading-none opacity-60">{it.label}</div>
                          <b className="text-sm leading-tight">{it.value}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                </button>
              );
            })()}
          </div>
        </div>
      )}

      <div className="flex flex-col flex-1 min-h-0 bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* ============ HEADER — actions · step tabs ============ */}
      <div className="flex-shrink-0 border-b border-gray-100 px-5 py-3 flex items-center justify-between gap-4 flex-wrap bg-white">
        {/* LEFT — template-edit banner, or a back arrow in read-only view */}
        <div className="flex items-center gap-3 min-w-0">
        {/* Back arrow — only in read-only view, which has no footer. The editable and template-edit
            flows use the explicit Close button in the footer instead, so the arrow is dropped there. */}
        {readOnly && (
          <button
            onClick={handleBackClick}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors flex-shrink-0"
            title="Close"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}

        {/* Template edit banner — replaces the patient identity + quick-links in template mode.
            Surfaces the editable Template Name (required) and Description that drive the save. */}
        {templateEditMode ? (
          <>
            <div className="w-10 h-10 bg-gradient-to-br from-navy-700 to-navy-900 rounded-lg flex items-center justify-center flex-shrink-0">
              <Save className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">
                  <ClipboardList className="w-3.5 h-3.5" /> {isNewTemplate ? 'New Template' : 'Editing Template'}
                </span>
              </div>
              <div className="flex items-end gap-3 flex-wrap">
                <div className="min-w-0">
                  <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Template Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => { setTemplateName(e.target.value); if (templateNameError) setTemplateNameError(''); }}
                    placeholder="e.g., Viral Fever Standard"
                    className={`px-3 py-1.5 border rounded-lg text-sm font-medium w-56 focus:ring-2 focus:ring-emerald-500 ${
                      templateNameError ? 'border-red-400' : 'border-gray-300'
                    }`}
                  />
                </div>
                {/* Doctor picker — only for a brand-new template (there's no prescription/doctor
                    context to derive the owner). Editing an existing template keeps its owner. */}
                {isNewTemplate && (
                  <div className="min-w-0">
                    <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Doctor <span className="text-red-500">*</span></label>
                    <Select
                      value={templateDoctorId}
                      onChange={(v) => { setTemplateDoctorId(v); if (templateNameError) setTemplateNameError(''); }}
                      placeholder="Select doctor…"
                      options={doctorOptions.map(d => ({
                        value: d.id,
                        label: d.fullName || ((d.firstName || '') + ' ' + (d.lastName || '')).trim(),
                      }))}
                      className="w-56"
                      buttonClassName="px-3 py-1.5 rounded-lg"
                    />
                  </div>
                )}
                <div className="min-w-0">
                  <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Description</label>
                  <input
                    type="text"
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    placeholder="When to use this template"
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-72 max-w-full focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                {templateNameError && (
                  <span className="text-xs text-red-600 font-medium mb-1.5">{templateNameError}</span>
                )}
              </div>
            </div>
          </>
        ) : (
          <span />
        )}
        </div>{/* /LEFT */}

        {/* RIGHT — segmented step tabs, stretched to fill the header */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {readOnly && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 flex-shrink-0">
              <Eye className="w-4 h-4" />
              <span className="text-xs font-semibold">View Only</span>
            </div>
          )}

          {/* Segmented step tabs — full width; click to navigate; Treatment dims when Consultation only is on */}
          <div className="flex flex-1 items-center bg-gray-100 rounded-xl p-1 text-sm font-medium">
            {allSections.map((s, idx) => {
              const isActive = s.id === activeSection;
              const isHidden = formData.consultationOnly && s.id === 'orders';
              // Thin divider between two adjacent inactive tabs (the active white pill already
              // separates the others), so Treatment/Advice don't blend together.
              const prevActive = idx > 0 && allSections[idx - 1].id === activeSection;
              const showDivider = idx > 0 && !isActive && !prevActive;
              return (
                <React.Fragment key={s.id}>
                  {showDivider && <span className="w-px h-5 self-center bg-gray-300" aria-hidden="true" />}
                  <div
                    title={isHidden ? 'Skipped - Consultation only' : s.label}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-lg select-none transition-all ${
                      isActive
                        ? 'bg-white text-emerald-700 font-semibold shadow-sm ring-1 ring-emerald-200'
                        : isHidden
                        ? 'text-gray-300'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {isActive && (
                      <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                      </span>
                    )}
                    {s.tab || s.label}
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Consultation only + Use template — to the right of the tabs, Diagnosis step only. Not in
              a view-only prescription: the editor below is inert there, and applying a template would
              still write its lines to the server and replace the diagnosis and notes on screen. */}
          {!readOnly && !templateEditMode && patient && activeSection === 'diagnosis' && (
            <div className="hidden lg:flex items-center gap-2 flex-none">
              <button
                type="button"
                aria-pressed={!!formData.consultationOnly}
                onClick={() => setFormData(prev => ({ ...prev, consultationOnly: !prev.consultationOnly }))}
                title="No medicines or lab tests - records diagnosis & advice only. The Treatment step is skipped."
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                <Info className="w-3.5 h-3.5 text-gray-400" />
                <span className={`text-xs font-medium ${formData.consultationOnly ? 'text-emerald-700' : 'text-gray-600'}`}>Consultation only</span>
                <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${formData.consultationOnly ? 'bg-emerald-600' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.consultationOnly ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </span>
              </button>
              {templates.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(true)}
                  disabled={templateApplying}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-emerald-700 hover:bg-emerald-50 border border-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ClipboardList className="w-3.5 h-3.5" />Use template
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* A dispensed / billed / closed prescription is locked server-side. Say so plainly rather
          than letting the doctor write changes that would be refused at save time. */}
      {readOnly && !isRxEditable(prescription) && (
        <div className="flex items-center gap-2 px-6 py-2.5 bg-amber-50 border-b border-amber-200 text-sm text-amber-800">
          <Lock className="w-4 h-4 flex-shrink-0" />
          <span>
            This prescription is {RX_LOCKED_REASON[prescription.status] || String(prescription.status || '').toLowerCase()} and is locked. It opens in view mode only.
          </span>
        </div>
      )}

      {/* ============ TWO-PANE WORKSPACE — editor (left) + context rail (right) ============ */}
      <div ref={contentScrollRef} className="flex-1 overflow-y-auto">
        <div className={`grid ${(patient && !templateEditMode) ? 'lg:grid-cols-[1fr_320px]' : 'grid-cols-1'}`}>
          {/* ---- EDITOR ---- */}
          {/* inert as well: pointer-events-none only stopped the mouse, and Tab could still reach and change the fields. */}
          <div inert={readOnly} className={`p-6 space-y-4 bg-gray-50/50 ${(patient && !templateEditMode) ? 'lg:border-r border-gray-100' : ''} ${readOnly ? 'pointer-events-none opacity-75' : ''}`}>
          {/* Start from a previous prescription, or, after a copy, what came across and what did not.
              Here rather than in the header cluster, which is hidden below the lg breakpoint. */}
          {activeSection === 'diagnosis' && showCopyStrip && (
            <CopyPreviousStrip
              copiedFrom={copiedFromInfo}
              result={copyResult}
              onOpen={() => setShowCopyModal(true)}
              disabled={copyingFrom}
            />
          )}
          {activeSection === 'diagnosis' && (
            <DiagnosisSection
              ref={diagnosisRef}
              formData={formData}
              setFormData={setFormData}
              existingMedicinesCount={prescription.medicines?.length || 0}
              existingLabTestsCount={prescription.labTests?.length || 0}
              onPendingComplaintChange={setPendingComplaint}
              templateMode={templateEditMode}
            />
          )}
          {activeSection === 'orders' && (
            <>
              <MedicinesSection
                prescription={prescription}
                setPrescription={setPrescription}
                enums={enums}
                templateMode={templateEditMode}
                doctorId={rxDoctorId}
                doctorSpecialityId={rxDoctorSpecialityId}
              />
              <LabTestsSection
                prescription={prescription}
                setPrescription={setPrescription}
                templateMode={templateEditMode}
                doctorId={rxDoctorId}
                doctorSpecialityId={rxDoctorSpecialityId}
              />
            </>
          )}
          {activeSection === 'after-visit' && (
            <>
              <InstructionsSection
                formData={formData}
                setFormData={setFormData}
                patientInstructions={patientInstructions}
                doctorSpecialityId={rxDoctorSpecialityId}
              />
              <FollowupSection
                formData={formData}
                setFormData={setFormData}
                followupTemplates={followupTemplates}
              />
            </>
          )}
          </div>{/* /EDITOR */}

          {/* ---- CONTEXT RAIL — vitals · medical profile · previous medicines · reports · private notes ---- */}
          {(patient && !templateEditMode) && (
            <div className="p-5 flex flex-col gap-3.5 bg-white">
              {/* Medical Alerts — expanded inline; the whole card pulses when any alert exists */}
              <div className={`rounded-xl border p-3.5 ${medicalAlerts.length > 0 ? 'border-rose-300 alert-pulse' : 'border-amber-200/70 bg-amber-50/30'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className={`inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider ${medicalAlerts.length > 0 ? 'text-rose-700' : 'text-amber-700'}`}>
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Medical Alerts
                  </div>
                  <button type="button" onClick={() => setShowAlertsModal(true)} className={`text-[11px] font-semibold hover:underline ${medicalAlerts.length > 0 ? 'text-rose-700' : 'text-amber-700'}`}>
                    {medicalAlerts.length > 0 ? 'Manage' : '+ Add'}
                  </button>
                </div>
                {medicalAlerts.length === 0 ? (
                  <p className="text-xs text-gray-400">No record</p>
                ) : (
                  <div className="space-y-1.5">
                    {medicalAlerts.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setShowAlertsModal(true)}
                        className={`w-full text-left rounded-lg border px-2.5 py-1.5 ${SEVERITY_COLORS[a.severity] || 'bg-white border-gray-200 text-gray-700'}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium truncate">{a.title}</span>
                          <span className="text-[9px] font-bold uppercase tracking-wide flex-none">{a.severity}</span>
                        </div>
                        <div className="text-[10px] opacity-80">{(a.alertType || '').replace(/_/g, ' ')}</div>
                        {a.description && <div className="text-[11px] opacity-90 mt-0.5">{a.description}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Medical profile — history + past prescriptions */}
              <div className="rounded-xl border border-amber-200/70 bg-amber-50/30 p-3.5 space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Medical profile</div>
                <button type="button" onClick={() => setPatientDataView('medical-history')} className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-amber-200 bg-white hover:bg-amber-50 text-sm text-amber-800 transition-colors">
                  <span className="inline-flex items-center gap-2"><Heart className="w-4 h-4 text-amber-600" />Medical History</span>
                  {medicalAlerts.length > 0 ? <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">{medicalAlerts.length}</span> : <span className="text-[10px] font-medium text-amber-600/70">No record</span>}
                </button>
                <button type="button" onClick={() => setShowHistoryFullModal(true)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-amber-200 bg-white hover:bg-amber-50 text-sm text-amber-800 transition-colors">
                  <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" />Past Prescriptions</span>
                  {patientHistory.length > 0 ? <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">{patientHistory.length}</span> : <span className="text-[10px] font-medium text-amber-600/70">No record</span>}
                </button>
              </div>

              {/* Medical reports + Documents — collapsed rows with counts */}
              <div className="rounded-xl border border-gray-200 p-3.5 space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Medical reports</div>
                <ReportRailRow icon={FlaskConical} label="Lab results" count={labReportCount} onClick={() => setPatientDataView('lab-tests')} />
                <ReportRailRow icon={Image} label="Radiology results" count={radiologyCount} onClick={() => setPatientDataView('radiology')} />
                <ReportRailRow icon={FileText} label="Documents" count={patientDocCount} onClick={() => setShowPatientDocs(true)} />
                {/* Notes about reports the patient showed but never uploaded (typically on their
                    phone). Patient-level, so they stay available on every later visit. */}
                <ReportRailRow icon={NotebookPen} label="Report notes" count={reportNotes.length} onClick={() => setShowReportNotes(true)} />
              </div>

              {/* Private Notes — encrypted, author-only, never printed. An admin can read them and
                  can let one other doctor read them on this prescription; nobody but the author can
                  ever edit them. */}
              <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-3.5 flex-1 flex flex-col min-h-0">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700 mb-1"><Lock className="w-3.5 h-3.5" />Private Notes</div>
                <p className="text-[11px] text-amber-600 mb-2">
                  Encrypted in storage · visible to anyone who can open this prescription · never printed
                </p>
                <div className="flex-1 flex flex-col min-h-0">
                  <textarea
                    value={formData.privateNotesEncrypted}
                    onChange={(e) => setFormData(prev => ({ ...prev, privateNotesEncrypted: e.target.value }))}
                    // Anyone who can edit the prescription can amend the note; a locked
                    // prescription makes it read-only along with everything else.
                    disabled={readOnly}
                    maxLength={NOTES_MAXLEN}
                    placeholder="Confidential notes…"
                    className="w-full flex-1 min-h-[90px] resize-none px-3 py-2 text-sm border border-amber-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-400 focus:border-amber-400 disabled:bg-amber-50 disabled:text-amber-900"
                  />
                  {!readOnly && (
                    <div className="mt-1 flex justify-end"><CharCount value={formData.privateNotesEncrypted} max={NOTES_MAXLEN} /></div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>{/* /grid */}
      </div>{/* /workspace scroll */}

        {/* Template-edit footer — only Cancel + Save Template (no Complete/Print, no Save Draft,
            no Save-as-Template modal). The wizard steps stay navigable above. */}
        {templateEditMode && (
          <div className="flex-shrink-0 border-t border-gray-200 px-6 py-3 flex items-center justify-between gap-3 bg-gray-50">
            <button
              onClick={goToPrevious}
              disabled={isFirstSection}
              className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isFirstSection
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              <span>{isFirstSection ? 'Previous' : sections[currentIndex - 1].label}</span>
            </button>

            <div className="flex-1 min-w-0 text-center">
              {!isLastSection && (
                <button
                  onClick={goToNext}
                  disabled={nextBlocked}
                  title={nextBlocked ? `Required: ${nextBlockers.join(' · ')}` : ''}
                  className={`inline-flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    nextBlocked
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span>{sections[currentIndex + 1].label}</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {isNewTemplate ? (
                // Brand-new template — Close + a single Save Template (create), enabled once a name
                // and doctor are chosen.
                <>
                  <button
                    onClick={onBack}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-white border border-gray-300 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>Close</span>
                  </button>
                  <button
                    onClick={handleSaveTemplateAsNew}
                    disabled={savingTemplate || !templateName.trim() || !templateDoctorId}
                    className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-green-600 hover:to-emerald-700 shadow-md transition-colors disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{savingTemplate ? 'Saving…' : 'Save Template'}</span>
                  </button>
                </>
              ) : !templateDirty ? (
                // Nothing changed — only offer Close.
                <button
                  onClick={onBack}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-medium text-gray-600 bg-white border border-gray-300 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span>Close</span>
                </button>
              ) : (
                // Something changed — offer Update (overwrite) or Save as New (copy).
                <>
                  <button
                    onClick={handleSaveTemplateAsNew}
                    disabled={savingTemplate}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Save as New</span>
                  </button>
                  <button
                    onClick={handleSaveTemplateEdit}
                    disabled={savingTemplate}
                    className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-green-600 hover:to-emerald-700 shadow-md transition-colors disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{savingTemplate ? 'Saving…' : 'Update Template'}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ============ ACTION BAR — hidden in readOnly & template-edit modes ============ */}
        {!readOnly && !templateEditMode && (
          <div className="flex-shrink-0 border-t border-gray-200 px-6 py-3 flex items-center justify-between gap-3 bg-white">
            {/* Left: Back (hidden on the first step) */}
            {!isFirstSection ? (
              <button
                onClick={goToPrevious}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-slate-600 hover:bg-slate-700 shadow-sm"
              >
                <ChevronLeft className="w-4 h-4" />Back
              </button>
            ) : <span />}

            {/* Right: blockers hint + Close + Save Draft + Save Template + Continue/Complete */}
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {(() => {
                const blockers = nextBlocked ? nextBlockers
                  : saveBlocked ? amendBlockers
                  : (isLastSection && !canComplete ? completionBlockers : []);
                if (blockers.length === 0) return null;
                return (
                  <span className="hidden sm:inline text-xs text-amber-700 mr-1 truncate max-w-[260px]">
                    <span className="font-semibold">{blockers.length} required:</span> {blockers.join(' · ')}
                  </span>
                );
              })()}
              {/* Refer — the doctor hands this patient to a colleague; notes so far are kept
                  and reception settles the paid fee with the patient. */}
              {canRefer && visit && (
                <button
                  onClick={() => setShowReferModal(true)}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-violet-700 border border-violet-200 hover:bg-violet-50 disabled:opacity-50"
                >
                  Refer
                </button>
              )}
              <button
                onClick={handleBackClick}
                disabled={saving}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              >
                Close
              </button>
              <button
                onClick={async () => {
                  // Save first (so nothing is at risk), then offer to close — a doctor saving a
                  // draft usually wants to park this Rx and move to the next patient.
                  const ok = await handleSave(false, { silent: true });
                  if (!ok) return;
                  const close = await confirm({
                    title: isFinalisedRx ? 'Changes Saved' : 'Draft Saved',
                    message: isFinalisedRx
                      ? 'Your changes to this completed prescription have been saved. Close it for now?'
                      : 'The prescription is saved as a draft and stays in the queue. You can resume it anytime. Close it for now?',
                    confirmText: 'Close Prescription',
                    cancelText: 'Keep Editing',
                  });
                  if (close) onBack();
                }}
                disabled={saving || saveBlocked}
                title={saveBlocked ? `Required on a completed prescription: ${amendBlockers.join(' · ')}` : ''}
                className={`px-4 py-2 rounded-lg text-sm font-medium border disabled:opacity-50 ${
                  saveBlocked
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'text-amber-700 border-amber-200 hover:bg-amber-50'
                }`}
              >
                {/* A finalised Rx is being amended, not drafted — the copy has to match. */}
                {isFinalisedRx ? 'Save Changes' : 'Save Draft'}
              </button>
              {/* Save Template — last step only, once mandatory fields are filled (same gate as
                  Complete). Gated behind the clinic setting; when off, templates are created only
                  from the Templates screen. */}
              {allowSaveTemplate && isLastSection && canComplete && (
                <button
                  onClick={() => setShowSaveTemplateModal(true)}
                  disabled={saving}
                  title="Save this prescription as a reusable template"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-teal-700 border border-teal-300 hover:bg-teal-50 disabled:opacity-50"
                >
                  <ClipboardList className="w-4 h-4" />Save Template
                </button>
              )}
              {!isLastSection ? (
                <button
                  onClick={goToNext}
                  disabled={nextBlocked}
                  title={nextBlocked ? `Required: ${nextBlockers.join(' · ')}` : ''}
                  className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    nextBlocked ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
                  }`}
                >
                  {formData.consultationOnly && activeSection === 'diagnosis' ? 'Continue to Advice' : 'Continue'}
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleCompleteClick}
                  disabled={saving || !canComplete}
                  title={!canComplete ? completionBlockers.join(' · ') : 'Complete and print prescription'}
                  className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    canComplete ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Printer className="w-4 h-4" />{saving ? 'Saving…' : 'Complete & Print'}
                </button>
              )}
            </div>
          </div>
        )}

      {/* Close Confirmation Modal */}
      {showCloseConfirm && (() => {
        const neverSaved = prescription.status === 'INITIATED';
        return (
          <div ref={closeConfirmDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${neverSaved ? 'bg-red-100' : 'bg-amber-100'}`}>
                  <AlertTriangle className={`w-6 h-6 ${neverSaved ? 'text-red-600' : 'text-amber-600'}`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {neverSaved ? 'Discard Prescription?' : 'Discard Changes?'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {neverSaved ? 'This action cannot be undone' : 'Unsaved changes will be lost'}
                  </p>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                {neverSaved
                  ? <>This prescription has not been saved. If you close now, it will be <strong>permanently cancelled and deleted</strong>. Would you like to save it as a draft instead?</>
                  : <>You have unsaved changes. If you close now, your recent edits will be <strong>discarded</strong>. The last saved version will be kept.</>
                }
              </p>
              <div className="flex flex-col space-y-2">
                <button
                  onClick={async () => {
                    const success = await handleSave(false);
                    if (success) {
                      setShowCloseConfirm(false);
                      onBack();
                    }
                  }}
                  disabled={saving}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                >
                  <Save className="w-4 h-4" />
                  <span>{saving ? 'Saving...' : 'Save & Close'}</span>
                </button>
                <button
                  onClick={async () => {
                    if (neverSaved) {
                      setCancelling(true);
                      try {
                        await prescriptionAPI.cancel(prescription.id);
                        clearDraft();
                        setShowCloseConfirm(false);
                        onBack();
                      } catch (err) {
                        console.error('Failed to cancel prescription:', err);
                        showToast('error', 'Failed to cancel prescription: ' + err.message);
                        setShowCloseConfirm(false);
                      } finally {
                        setCancelling(false);
                      }
                    } else {
                      clearDraft();
                      setShowCloseConfirm(false);
                      onBack();
                    }
                  }}
                  disabled={cancelling}
                  className={`w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg transition-colors font-medium ${
                    neverSaved
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-amber-500 text-white hover:bg-amber-600'
                  }`}
                >
                  {neverSaved ? <Trash2 className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  <span>{cancelling ? 'Cancelling...' : neverSaved ? 'Discard & Delete' : 'Discard Changes'}</span>
                </button>
                <button
                  onClick={() => setShowCloseConfirm(false)}
                  className="w-full px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Continue Editing
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Refer Patient Modal */}
      {showReferModal && visit && (
        <ReferPatientModal
          visit={visit}
          doctors={doctors}
          hasMedicines={(prescription.medicines?.length || 0) > 0}
          onClose={() => setShowReferModal(false)}
          onReferred={(docName) => {
            setShowReferModal(false);
            clearDraft();
            showToast('success', `Patient referred to ${docName}. Reception will settle the fee.`);
            onBack();
          }}
        />
      )}

      {/* Medical Alerts Modal */}
      {showAlertsModal && (
        <MedicalAlertsModal
          alerts={medicalAlerts}
          patientId={patient?.id}
          prefill={alertPrefill}
          onClose={() => { setShowAlertsModal(false); setAlertPrefill(null); }}
          onRefresh={loadMedicalAlerts}
          enums={enums}
        />
      )}

      {/* Clinical-flag prompt — offer to record detected terms (allergy / pregnancy / chronic
          condition) as persistent patient alerts before leaving the Diagnosis step. */}
      {showFlagPrompt && pendingClinicalFlags.length > 0 && (
        <div ref={flagPromptDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowFlagPrompt(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 leading-tight">Record a medical alert?</h3>
                <p className="text-xs text-gray-500">Your notes mention something worth flagging on this patient.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Recording these as patient alerts makes them stand out on every future visit. Add the ones that apply:
            </p>
            <div className="space-y-2 mb-4">
              {pendingClinicalFlags.map((f) => (
                <div key={f.alertType} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50/50">
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-amber-800">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />{f.label}
                  </span>
                  <button
                    onClick={() => {
                      setShowFlagPrompt(false);
                      setAlertPrefill({ alertType: f.alertType, title: f.title, description: '', severity: f.severity });
                      setShowAlertsModal(true);
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                  >
                    <Plus className="w-3.5 h-3.5" />Add alert
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  // "Not needed" — remember these so we don't keep re-prompting, then continue.
                  setDismissedFlagTypes(prev => new Set([...prev, ...pendingClinicalFlags.map(f => f.alertType)]));
                  setShowFlagPrompt(false);
                  advanceToNext();
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-white border border-gray-300 hover:bg-gray-50"
              >
                Not needed - continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Preview Modal */}
      {showPrintPreview && (
        <PrintPreviewModal
          prescription={prescription}
          patient={patient}
          // Fall back to the prescription's own doctor (current-doctor context is null in
          // admin "All Doctors" mode → otherwise prints "Dr. undefined undefined").
          doctor={doctor || prescription?.doctor || visit?.doctor}
          // X / click-away — close WITHOUT finalising: the prescription stays a draft so the
          // status doesn't change just because the preview was opened.
          onClose={() => setShowPrintPreview(false)}
          // Print or Done — this is the explicit confirmation, so finalise (status → PRESCRIBED)
          // now, then leave the editor.
          onDone={async () => {
            const ok = await handleFinalizeComplete();
            setShowPrintPreview(false);
            if (ok) onBack();
          }}
        />
      )}

      {/* Load Template Modal */}
      {showTemplateModal && (
        <LoadTemplateModal
          templates={templates}
          prescriptionId={prescription.id}
          busy={templateApplying}
          onClose={() => setShowTemplateModal(false)}
          onApply={async (template) => {
            const parseArr = (json) => {
              try { const v = JSON.parse(json); return Array.isArray(v) ? v : null; } catch { return null; }
            };
            // One apply at a time, and never into a prescription that cannot be edited (the button
            // is hidden there; this is the backstop).
            if (templateApplyingRef.current) return;
            if (readOnly || !isRxEditable(prescription)) {
              showToast('error', 'This prescription is open to view only, so a template cannot be applied to it.');
              setShowTemplateModal(false);
              return;
            }
            templateApplyingRef.current = true;
            setTemplateApplying(true);
            try {
              // Append template medicines + lab tests, one at a time and in order, skipping any
              // already on the prescription (the picker's own duplicate test: inventory id first,
              // then name). A line the server refuses is noted and the rest carry on, so one bad
              // line cannot leave half a template with nothing below it applied. The server refuses
              // a medicine that is out of stock, expired or no longer carried; the stock read here
              // is only for advice on low stock, so the template still applies without it.
              const stock = await medicineAPI.getAllWithInventory('MEDICINE').catch((e) => {
                console.error('Failed to read stock for the template advice:', e);
                return null;
              });
              const plan = planTemplateApply(template, prescription, { stock });
              const outcome = await runTemplateApply(plan, prescription.id, prescriptionAPI);
              // Load the rest of the prescription from the template (text/array fields replace).
              const tplComplaints = parseArr(template.complaintsJson);
              const tplInstructions = parseArr(template.instructionsJson);
              // Fill from the template, but never wipe an existing value with a blank one the
              // template happens to lack (so applying a partial template is additive, not lossy).
              setFormData(prev => ({
                ...prev,
                diagnosis: template.diagnosis || prev.diagnosis,
                complaints: (tplComplaints && tplComplaints.length > 0)
                  ? tplComplaints.map((c, i) => ({ ...c, id: Date.now() + i }))
                  : prev.complaints,
                clinicalNotes: template.clinicalNotes || prev.clinicalNotes,
                examination: template.examination || prev.examination,
                selectedInstructions: (tplInstructions && tplInstructions.length > 0) ? tplInstructions : prev.selectedInstructions,
                consultationOnly: template.consultationOnly != null ? template.consultationOnly : prev.consultationOnly,
                followupDays: template.followupDays || prev.followupDays,
              }));
              // Refresh prescription. If the reload itself fails, the last line the server added
              // answered with the whole prescription, which is nearly as fresh. Either way an
              // unsaved bedside sugar tick is kept, since the server has not seen it yet.
              let updated = null;
              try {
                updated = await prescriptionAPI.getById(prescription.id);
              } catch (e) {
                console.error('Failed to reload the prescription after applying a template:', e);
                updated = outcome.latest;
              }
              if (updated) setPrescription(prev => keepLocalRxEdits(prev, updated));
              setShowTemplateModal(false);
              // Remember which template was loaded, so the footer's Save Template offers to update it.
              setLoadedTemplate(template);
              // Say plainly which lines did not go in and why, and which doses need a look (a
              // frequency line from an old template has no amount and would bill one dose a day).
              // A problem stays on screen until dismissed.
              const report = templateApplyReport(template.templateName, plan, outcome, { canSaveTemplateHere: allowSaveTemplate });
              showToast(report.tone, (
                <div>
                  <div>{report.headline}</div>
                  {report.lines.length > 0 && (
                    <ul className="mt-1 list-disc pl-4 space-y-0.5 font-normal">
                      {report.lines.map((line, i) => <li key={i}>{line}</li>)}
                    </ul>
                  )}
                </div>
              ), report.sticky ? 0 : 6000);
            } catch (err) {
              console.error('Failed to apply template:', err);
              showToast('error', 'Failed to apply template');
            } finally {
              templateApplyingRef.current = false;
              setTemplateApplying(false);
            }
          }}
        />
      )}

      {/* Copy a previous prescription: the picker behind the Diagnosis step's strip */}
      {showCopyModal && (
        <CopyPrescriptionModal
          patientName={patient ? (patient.fullName || `${patient.firstName || ''} ${patient.lastName || ''}`.trim()) : ''}
          currentPrescriptionId={prescription.id}
          sources={eligibleCopySources}
          hasContent={copyTargetHasContent()}
          busy={copyingFrom}
          onConfirm={handleCopyFromPrevious}
          onClose={() => setShowCopyModal(false)}
        />
      )}

      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <SaveTemplateModal
          prescription={prescription}
          formData={formData}
          templates={templates}
          loadedTemplate={loadedTemplate}
          // Fall back to the prescription's / visit's own doctor — `doctor` (current-doctor
          // context) is null when an admin views in "All Doctors" mode.
          doctorId={doctor?.id || prescription?.doctor?.id || visit?.doctor?.id}
          onClose={() => setShowSaveTemplateModal(false)}
          onSaved={() => {
            loadTemplates();
            setShowSaveTemplateModal(false);
          }}
        />
      )}

      {/* Report notes — the doctor's own record of reports the patient showed but never uploaded */}
      {showReportNotes && (
        <ReportNotesModal
          notes={reportNotes}
          patientId={patient?.id}
          onClose={() => setShowReportNotes(false)}
          onRefresh={loadReportNotes}
        />
      )}

      {/* Prescription Detail Modal (from history) */}
      {historyDetailPrescription && (
        <PrescriptionDetailModal
          prescription={historyDetailPrescription}
          onClose={() => setHistoryDetailPrescription(null)}
        />
      )}

      {/* Patient Documents Modal */}
      {showPatientDocs && patient && (
        <PatientDocumentsModal
          patientId={patient.id}
          patientName={patient.fullName || `${patient.firstName} ${patient.lastName}`}
          onClose={() => setShowPatientDocs(false)}
        />
      )}

      {/* Reused Patient View Modal tabs (Vitals / Lab Reports / Radiology / Medical History),
          opened from the header quick-links. */}
      {patientDataView && patient && (
        <PatientDataModal
          view={patientDataView}
          patientId={patient.id}
          patientName={patient.fullName || `${patient.firstName} ${patient.lastName}`}
          onClose={() => setPatientDataView(null)}
        />
      )}


      {/* Patient Full History Modal */}
      {showHistoryFullModal && patient && (
        <PatientHistoryFullModal
          patientId={patient.id}
          patientName={patient.fullName || `${patient.firstName} ${patient.lastName}`}
          currentPrescriptionId={prescription.id}
          onClose={() => setShowHistoryFullModal(false)}
          onViewDetail={handleViewHistoryPrescription}
        />
      )}

      {/* Recorded-vitals detail (today / past / trends) — opened from the header-band Vitals card.
          "Retake vitals" is offered only within 6 hours of the original reading (in case the doctor
          is suspicious of the readings). */}
      {showVitalsModal && (
        <VitalsDetailModal
          patientId={patient?.id}
          formData={formData}
          dateOfBirth={patient?.dateOfBirth}
          assessment={vitalsStatus}
          recordedByName={visit?.vitalsRecordedByName || visit?.recordedByName}
          onClose={() => setShowVitalsModal(false)}
          canRetake={!readOnly && !!visit?.id && (() => {
            const t = visit?.vitalsRecordedAt ? new Date(visit.vitalsRecordedAt).getTime() : null;
            return t != null && (Date.now() - t) <= 6 * 60 * 60 * 1000;
          })()}
          onRetake={() => { setShowVitalsModal(false); setShowRetakeVitals(true); }}
        />
      )}

      {/* Doctor re-records vitals (reuses the nurse's vitals form). Updates the form's vitals so
          the prescription reflects the corrected readings. */}
      {showRetakeVitals && visit?.id && (
        <VitalsModal
          visit={visit}
          recordedBy={doctor?.id}
          onClose={() => setShowRetakeVitals(false)}
          onSaved={(updated) => {
            const v = updated || {};
            setFormData(prev => ({
              ...prev,
              bpSystolic: v.bpSystolic ?? '', bpDiastolic: v.bpDiastolic ?? '',
              pulse: v.pulse ?? '', temperature: v.temperature ?? '', weight: v.weight ?? '',
              height: v.height ?? '', spo2: v.spo2 ?? '', respiratoryRate: v.respiratoryRate ?? '',
              lengthCm: v.lengthCm ?? '', headCircumferenceCm: v.headCircumferenceCm ?? '',
              muacCm: v.muacCm ?? '', bloodSugar: v.bloodSugar ?? '',
            }));
            setShowRetakeVitals(false);
            showToast('success', 'Vitals updated');
          }}
        />
      )}

      </div>{/* /white card */}
    </div>
  );
};

// ============ DIAGNOSIS SECTION ============
// Complaint "duration" (how long the patient has had it) is captured as a number + unit and
// stored as a readable string like "3 Days" (back-compatible with the free-text `since` field).
const DURATION_UNITS = ['Days', 'Weeks', 'Months', 'Years'];
const DURATION_SINGULAR = { Days: 'Day', Weeks: 'Week', Months: 'Month', Years: 'Year' };
const QUICK_DURATIONS = [
  { label: '1 day',  value: 1, unit: 'Days' },
  { label: '2 days', value: 2, unit: 'Days' },
  { label: '3 days', value: 3, unit: 'Days' },
  { label: '4 days', value: 4, unit: 'Days' },
  { label: '1 week', value: 1, unit: 'Weeks' },
  { label: '2 weeks', value: 2, unit: 'Weeks' },
  { label: '1 month', value: 1, unit: 'Months' },
];
const composeSince = (value, unit) => {
  const v = String(value ?? '').trim();
  if (!v) return '';
  const label = Number(v) === 1 ? (DURATION_SINGULAR[unit] || unit) : unit;
  return `${v} ${label}`;
};

// Parse a "since" string (e.g. "4 Days", "1 Week", "2 Weeks", "1 Month") back to a day count so
// complaints can be ordered longest-standing first. No/blank duration sorts last.
const SINCE_UNIT_DAYS = { day: 1, days: 1, week: 7, weeks: 7, month: 30, months: 30, year: 365, years: 365 };
const sinceToDays = (since) => {
  const m = String(since || '').trim().match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/);
  if (!m) return -1;
  return (parseFloat(m[1]) || 0) * (SINCE_UNIT_DAYS[m[2].toLowerCase()] || 1);
};
// Complaints ordered by duration, longest first (stable for equal durations).
const complaintsByDuration = (complaints) =>
  (complaints || [])
    .map((c, i) => ({ c, i }))
    .sort((a, b) => (sinceToDays(b.c.since) - sinceToDays(a.c.since)) || (a.i - b.i))
    .map(x => x.c);

const DiagnosisSection = forwardRef(({ formData, setFormData, existingMedicinesCount = 0, existingLabTestsCount = 0, onPendingComplaintChange, templateMode = false }, ref) => {
  const [newComplaint, setNewComplaint] = useState({ complaint: '', durationValue: '', durationUnit: 'Days' });

  // Past Lab Reports collapses to a single row: most visits have no reports to comment on, so it
  // stays out of the way until the doctor opens it. Already-written text never hides behind the
  // header — it opens with content, both on mount and when the prescription finishes loading.
  const [pastLabOpen, setPastLabOpen] = useState(!!formData.pastLabReports);
  const pastLabAutoOpened = useRef(!!formData.pastLabReports);
  const pastLabRef = useRef(null);
  useEffect(() => {
    if (!pastLabAutoOpened.current && formData.pastLabReports) {
      pastLabAutoOpened.current = true;
      setPastLabOpen(true);
    }
  }, [formData.pastLabReports]);
  const togglePastLab = () => {
    if (pastLabOpen) { setPastLabOpen(false); return; }
    setPastLabOpen(true);
    // Opening is a deliberate click, so land the cursor in the box (never on auto-open).
    requestAnimationFrame(() => pastLabRef.current?.focus());
  };

  useImperativeHandle(ref, () => ({
    // Expose a normalised {complaint, since} so the parent's save/Next auto-commit keeps working.
    getPendingComplaint: () => ({
      complaint: newComplaint.complaint,
      since: composeSince(newComplaint.durationValue, newComplaint.durationUnit),
    }),
    clearPendingComplaint: () => setNewComplaint({ complaint: '', durationValue: '', durationUnit: 'Days' }),
  }));

  // Report the pending complaint text up so the parent can count it toward the required-field
  // check (and auto-commit it when leaving the step) before the user clicks "Add".
  useEffect(() => {
    if (onPendingComplaintChange) onPendingComplaintChange(newComplaint.complaint);
  }, [newComplaint.complaint, onPendingComplaintChange]);

  // Auto-grow the Diagnosis field so a long diagnosis wraps onto new lines instead of scrolling
  // sideways (it looks like a single-line input until the text needs a second line).
  const diagRef = useRef(null);
  useEffect(() => {
    const el = diagRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; }
  }, [formData.diagnosis]);

  const addComplaint = () => {
    if (newComplaint.complaint && newComplaint.complaint.trim()) {
      setFormData(prev => ({
        ...prev,
        complaints: [...prev.complaints, {
          complaint: newComplaint.complaint.trim(),
          since: composeSince(newComplaint.durationValue, newComplaint.durationUnit),
          id: Date.now(),
        }]
      }));
      setNewComplaint({ complaint: '', durationValue: '', durationUnit: 'Days' });
    }
  };

  const removeComplaint = (id) => {
    setFormData(prev => ({
      ...prev,
      complaints: prev.complaints.filter(c => c.id !== id)
    }));
  };

  const hasExistingOrders = existingMedicinesCount > 0 || existingLabTestsCount > 0;

  return (
    <>
      {/* Warning when consultation-only is on but orders are still attached */}
      {formData.consultationOnly && hasExistingOrders && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            This prescription still has <strong>{existingMedicinesCount} medicine(s)</strong> and <strong>{existingLabTestsCount} lab test(s)</strong> attached.
            Turn off Consultation only to review and remove them before completing.
          </span>
        </div>
      )}

      {/* Chief Complaints */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
          <Activity className="w-[15px] h-[15px] text-emerald-600" />Chief Complaints <span className="text-red-500 normal-case">*</span>
        </div>

        {/* Add row — Complaint (~half width) on the left; Duration + unit + Add fill the right half,
            with the Quick chips aligned beneath that right cluster. */}
        <div className="flex flex-col sm:flex-row gap-2 items-start">
          <input
            type="text"
            value={newComplaint.complaint}
            onChange={(e) => setNewComplaint(prev => ({ ...prev, complaint: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addComplaint(); } }}
            placeholder="Complaint (e.g. Headache, Cough)"
            className="w-full sm:w-1/2 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
          <div className="w-full sm:flex-1 flex flex-col gap-2.5">
            <div className="flex items-stretch gap-2">
              <input
                type="number"
                min="0"
                value={newComplaint.durationValue}
                onChange={(e) => setNewComplaint(prev => ({ ...prev, durationValue: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addComplaint(); } }}
                placeholder="e.g. 3"
                title="How long the patient has had this complaint"
                className="flex-1 basis-0 min-w-0 px-2 py-2 text-sm text-center border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <Select
                value={newComplaint.durationUnit}
                onChange={(v) => setNewComplaint(prev => ({ ...prev, durationUnit: v }))}
                options={DURATION_UNITS}
                className="flex-1 basis-0 min-w-0"
                buttonClassName="px-3 py-2 rounded-lg"
              />
              <button
                onClick={addComplaint}
                className="inline-flex items-center justify-center gap-1.5 flex-1 basis-0 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            {/* Quick durations — one tap fills the number + unit, then Add */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-gray-400 mr-0.5">Quick:</span>
              {QUICK_DURATIONS.map(q => {
                const active = String(newComplaint.durationValue) === String(q.value) && newComplaint.durationUnit === q.unit;
                return (
                  <button
                    key={q.label}
                    type="button"
                    onClick={() => setNewComplaint(prev => ({ ...prev, durationValue: q.value, durationUnit: q.unit }))}
                    className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                      active ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                    }`}
                  >
                    {q.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Added complaints — numbered vertical list, ordered by duration (longest-standing first) */}
        {formData.complaints.length > 0 && (
          <div className="mt-3 space-y-2">
            {complaintsByDuration(formData.complaints).map((c, idx) => (
              <div key={c.id || idx} className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold flex items-center justify-center flex-none">{idx + 1}</span>
                  <span className="text-sm text-gray-800 truncate">{c.complaint}</span>
                  {c.since && (
                    <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full flex-none">{c.since}</span>
                  )}
                </div>
                <button aria-label={`Remove ${c.complaint}`} onClick={() => removeComplaint(c.id)} className="text-gray-300 hover:text-rose-500 flex-none">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clinical Notes */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
          <FileText className="w-[15px] h-[15px] text-emerald-600" />Clinical Notes <span className="text-red-500 normal-case">*</span>
        </div>
        <textarea
          value={formData.clinicalNotes}
          onChange={(e) => setFormData(prev => ({ ...prev, clinicalNotes: e.target.value }))}
          rows={3}
          maxLength={NOTES_MAXLEN}
          placeholder="Notes…"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
        <div className="mt-1 flex justify-end"><CharCount value={formData.clinicalNotes} max={NOTES_MAXLEN} /></div>
      </div>

      {/* Examination */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
          <Stethoscope className="w-[15px] h-[15px] text-emerald-600" />Examination <span className="text-red-500 normal-case">*</span>
        </div>
        <textarea
          value={formData.examination}
          onChange={(e) => setFormData(prev => ({ ...prev, examination: e.target.value }))}
          rows={3}
          maxLength={NOTES_MAXLEN}
          placeholder="Examination findings…"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
        <div className="mt-1 flex justify-end"><CharCount value={formData.examination} max={NOTES_MAXLEN} /></div>
      </div>

      {/* Past Lab Reports - sits in the flow so reviewing the patient's reports is a step the
          doctor walks past, not a rail button they must remember. A prescription field like
          Examination: saved with the prescription, and on finalize the backend copies it into the
          Report notes history the rail shows - so it appears there from the NEXT visit onward,
          never during its own visit. Hidden while editing a template: the note belongs to one
          visit, so templates never carry it and typed text would have nowhere to save. */}
      {!templateMode && (
      <div className="bg-white rounded-xl border border-gray-200">
        <button
          type="button"
          onClick={togglePastLab}
          aria-expanded={pastLabOpen}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left rounded-xl hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 flex-none">
              <NotebookPen className="w-[15px] h-[15px] text-emerald-600" />Past Lab Reports
            </div>
            {/* Collapsed with content: show the note itself so the doctor never has to open the
                card to remember what is in it. */}
            {!pastLabOpen && formData.pastLabReports && (
              <span className="text-xs text-gray-600 truncate">{formData.pastLabReports}</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-none">
            {!pastLabOpen && !formData.pastLabReports && (
              <span className="text-[11px] text-gray-400 hidden sm:inline">Add a note if the patient brought reports</span>
            )}
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${pastLabOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {pastLabOpen && (
          <div className="px-5 pb-5">
            <textarea
              ref={pastLabRef}
              value={formData.pastLabReports}
              onChange={(e) => setFormData(prev => ({ ...prev, pastLabReports: e.target.value }))}
              rows={3}
              maxLength={NOTES_MAXLEN}
              placeholder="e.g. CBC from 02 Aug - Hb 10.2, advised iron; Ultrasound normal"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            <div className="mt-1 flex items-center justify-between gap-3">
              <span className="text-[11px] text-gray-400">Shown under Report notes on the next visit</span>
              <CharCount value={formData.pastLabReports} max={NOTES_MAXLEN} />
            </div>
          </div>
        )}
      </div>
      )}

      {/* Diagnosis - deliberately last: it is the conclusion, reached after complaints, notes and
          examination are down, so the form reads in the order the consultation actually happens. */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            <ClipboardList className="w-[15px] h-[15px] text-emerald-600" />Diagnosis/Provisional Diagnosis <span className="text-red-500 normal-case">*</span>
          </div>
          <span className="text-[11px] text-gray-400 hidden sm:inline">Primary clinical diagnosis</span>
        </div>
        <textarea
          ref={diagRef}
          rows={1}
          value={formData.diagnosis}
          onChange={(e) => setFormData(prev => ({ ...prev, diagnosis: e.target.value }))}
          maxLength={DIAGNOSIS_MAXLEN}
          placeholder="Start typing to pick a common diagnosis, or write your own"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none overflow-hidden leading-relaxed"
        />
        <div className="mt-1 flex justify-end"><CharCount value={formData.diagnosis} max={DIAGNOSIS_MAXLEN} /></div>
      </div>
    </>
  );
});

// ============ VITALS READ-ONLY PANEL (shown above the stepper in the prescription form) ============
// Vitals are captured by the nurse before the doctor picks up the patient. The doctor sees them
// here as reference data; they can't be edited in the prescription form. If the nurse made an
// error, she re-records via the Nurse Station.
// Age-band vitals ranges + assessVitals() moved to ../utils/vitals (shared with the
// Token Queue chips so both surfaces interpret a reading identically).

const VitalsReadonlyPanel = ({
  bpSystolic, bpDiastolic, pulse, temperature, weight, height, bloodSugar, spo2, respiratoryRate,
  lengthCm, headCircumferenceCm, muacCm, dateOfBirth,
}) => {
  const hasAny = bpSystolic || bpDiastolic || pulse || temperature || weight || height
    || bloodSugar || spo2 || respiratoryRate || lengthCm || headCircumferenceCm || muacCm;
  if (!hasAny) {
    return (
      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center gap-2 text-sm text-amber-800">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span>No vitals recorded for this visit.</span>
      </div>
    );
  }

  // BMI = weight (kg) / (height_m)^2. For under-2 the nurse will have recorded Length in
  // place of Height — either is fine for the formula. Auto-derived so the doctor doesn't
  // need to calculate it; the chip is tagged "auto" so it's clear it's derived.
  const heightForBmi = height || lengthCm;
  const bmi = (weight && heightForBmi)
    ? (weight / Math.pow(heightForBmi / 100, 2))
    : null;

  // Pediatric "Length" reads as the height equivalent for under-2; if both ended up populated
  // (data oddity), prefer Height since the patient must be ≥ 2 for it to exist.
  const showLength = lengthCm && !height;

  // Assess the vitals against the patient's age-band normal ranges FIRST, so each chip can be
  // coloured by its own finding — neutral when in range, a severity colour only when abnormal
  // (so a normal BP 120/80 or Pulse 80 no longer reads as a red alarm). The banner below rolls
  // these up to one overall status. Advisory only — clinical judgement still wins.
  const band = getAgeBandKey(dateOfBirth);
  const assessment = assessVitals(
    { bpSystolic, bpDiastolic, pulse, temperature, spo2, respiratoryRate, bloodSugar, muacCm },
    band,
  );
  // severity per assessed vital, keyed by the chip's label (with a couple of label aliases).
  const sevByLabel = {};
  assessment.issues.forEach(iss => { sevByLabel[iss.label] = iss.severity; });
  const LABEL_ALIAS = { Resp: 'Resp Rate' };
  // Shared traffic-light tone (green normal / amber borderline / red abnormal).
  const chipTone = (label) =>
    vitalChipToneClass(sevByLabel[label] || sevByLabel[LABEL_ALIAS[label]] || 'normal');

  const chips = [
    bpSystolic && bpDiastolic && { label: 'BP', value: `${bpSystolic}/${bpDiastolic}`, unit: 'mmHg' },
    pulse       && { label: 'Pulse',       value: pulse,                          unit: 'BPM' },
    temperature && { label: 'Temp',        value: temperature,                    unit: '°F' },
    spo2        && { label: 'SpO₂',        value: spo2,                           unit: '%' },
    weight      && { label: 'Weight',      value: weight,                         unit: 'kg' },
    height      && { label: 'Height',      value: height,                         unit: 'cm' },
    showLength  && { label: 'Length',      value: lengthCm,                       unit: 'cm' },
    headCircumferenceCm && { label: 'Head Circ', value: headCircumferenceCm,      unit: 'cm' },
    muacCm      && { label: 'MUAC',        value: muacCm,                         unit: 'cm' },
    bloodSugar  && { label: 'Blood Sugar', value: bloodSugar,                     unit: 'mg/dL' },
    respiratoryRate && { label: 'Resp',    value: respiratoryRate,                unit: '/min' },
    bmi && { label: 'BMI', value: bmi.toFixed(1), unit: 'kg/m²', derived: true },
  ].filter(Boolean);

  // The overall assessment verdict now lives as a pill in the prescription header (moved up to
  // save space). This panel keeps the reference chips, each still colour-coded by its own
  // severity (green normal / amber borderline / red abnormal) via chipTone.
  return (
    <div className="mb-6 rounded-xl border border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Heart className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-emerald-900 uppercase tracking-wide">Vitals (recorded by nurse)</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {chips.map((c, i) => (
          <div key={i} className={`inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-lg border ${chipTone(c.label)}`}>
            <span className="text-xs font-medium opacity-70">{c.label}</span>
            <span className="text-sm font-bold">{c.value}</span>
            <span className="text-[10px] opacity-60">{c.unit}</span>
            {c.derived && <span className="text-[9px] opacity-50 italic ml-0.5">auto</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ RECORDED-VITALS DETAIL MODAL (opened from the header Vitals pill) ============
// Shows this visit's recorded vitals (reusing VitalsReadonlyPanel for the chips) plus the
// auto-derived assessment — which readings are normal / borderline / abnormal. Advisory only.
const VITALS_BANNER = {
  normal:  { box: 'bg-emerald-50 border-emerald-200 text-emerald-800', dot: 'bg-emerald-500', icon: Check,         title: 'Vitals appear within normal ranges' },
  mild:    { box: 'bg-amber-50 border-amber-200 text-amber-800',       dot: 'bg-amber-500',   icon: AlertTriangle, title: 'Mild deviations - clinical correlation suggested' },
  concern: { box: 'bg-orange-50 border-orange-300 text-orange-800',    dot: 'bg-orange-500',  icon: AlertTriangle, title: 'Review needed - readings outside normal range' },
  alarm:   { box: 'bg-red-50 border-red-300 text-red-800 shadow-sm',   dot: 'bg-red-600',     icon: AlertTriangle, title: 'Attention - alarming readings detected' },
};

const VitalsDetailModal = ({ patientId, formData, dateOfBirth, assessment, recordedByName, onClose, onRetake, canRetake }) => {
  const banner = assessment ? (VITALS_BANNER[assessment.severity] || VITALS_BANNER.normal) : null;
  // Outer tabs: this visit's reading → all past readings → trend charts.
  const [tab, setTab] = useState('today');
  const vitalsDetailDialogRef = useDialog(onClose, { confirmDiscard: false });
  const TABS = [
    { key: 'today',  label: "Today's Reading", icon: Heart },
    { key: 'past',   label: 'Past Readings',   icon: Activity },
    { key: 'trends', label: 'Trends',          icon: TrendingUp },
  ];
  return (
    <div
      ref={vitalsDetailDialogRef}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 text-white">
            <Heart className="w-5 h-5" />
            <div>
              <h2 className="text-lg font-bold leading-tight">Vitals</h2>
              {recordedByName && <p className="text-xs text-emerald-100">Today recorded by {recordedByName}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onRetake && canRetake && (
              <button
                onClick={onRetake}
                title="Re-record vitals (allowed within 6 hours of the original reading)"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium border border-white/40"
              >
                <RotateCcw className="w-4 h-4" />Retake vitals
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg text-white" title="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 px-4 border-b border-gray-200 flex-shrink-0">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2.5 -mb-px font-medium text-sm border-b-2 ${
                  active ? 'border-navy-800 text-navy-800' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        <div className="p-5 overflow-y-auto">
          {tab === 'today' ? (
            <>
              {/* This visit's recorded vitals (chips) + assessment detail. */}
              <VitalsReadonlyPanel
                bpSystolic={formData.bpSystolic}
                bpDiastolic={formData.bpDiastolic}
                pulse={formData.pulse}
                temperature={formData.temperature}
                weight={formData.weight}
                height={formData.height}
                bloodSugar={formData.bloodSugar}
                spo2={formData.spo2}
                respiratoryRate={formData.respiratoryRate}
                lengthCm={formData.lengthCm}
                headCircumferenceCm={formData.headCircumferenceCm}
                muacCm={formData.muacCm}
                dateOfBirth={dateOfBirth}
              />
              {banner ? (
                <div className={`rounded-lg border px-3 py-2.5 flex items-start gap-2.5 ${banner.box}`}>
                  <div className={`flex-shrink-0 w-5 h-5 ${banner.dot} text-white rounded-full flex items-center justify-center mt-0.5`}>
                    <banner.icon className="w-3 h-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">
                      {banner.title}
                      <span className="text-xs font-normal opacity-70 ml-1.5">· age band: {assessment.ageLabel}</span>
                    </div>
                    {assessment.issues.length > 0 ? (
                      <ul className="text-xs mt-1 space-y-0.5">
                        {assessment.issues.map((iss, i) => (
                          <li key={i}>
                            <span className="font-semibold">{iss.label} {iss.value}:</span>{' '}
                            <span className="opacity-90">{iss.finding}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-xs opacity-80 mt-0.5">No abnormal readings detected.</div>
                    )}
                    <div className="text-[10px] opacity-60 mt-1 italic">Advisory only - clinical judgement applies.</div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500 text-center py-6">No vitals recorded for this visit.</div>
              )}
            </>
          ) : tab === 'past' ? (
            <PatientVitalsTab patientId={patientId} dateOfBirth={dateOfBirth} initialView="readings" hideTabs />
          ) : (
            <PatientVitalsTab patientId={patientId} dateOfBirth={dateOfBirth} initialView="trends" hideTabs />
          )}
        </div>
      </div>
    </div>
  );
};

// ============ VITALS SECTION (legacy — kept for backward compat but no longer used in the wizard) ============
const VitalsSection = ({ formData, setFormData }) => {
  // BP Systolic, BP Diastolic, and Pulse are REQUIRED — they unlock the rest of the prescription.
  // Assistant typically records these before the patient reaches the doctor.
  const vitalFields = [
    { key: 'bpSystolic',  label: 'BP Systolic',  unit: 'mmHg',  icon: Heart,       color: 'red',    placeholder: '120',  required: true },
    { key: 'bpDiastolic', label: 'BP Diastolic', unit: 'mmHg',  icon: Heart,       color: 'red',    placeholder: '80',   required: true },
    { key: 'pulse',       label: 'Pulse',        unit: 'BPM',   icon: Activity,    color: 'pink',   placeholder: '72',   required: true },
    { key: 'temperature', label: 'Temperature',  unit: '°F',    icon: Thermometer, color: 'orange', placeholder: '98.6' },
    { key: 'weight',      label: 'Weight',       unit: 'KG',    icon: Weight,      color: 'blue',   placeholder: '70' },
    { key: 'bloodSugar',  label: 'Blood Sugar',  unit: 'mg/dL', icon: Droplets,    color: 'purple', placeholder: '100' },
    { key: 'spo2',        label: 'SpO2',         unit: '%',     icon: Wind,        color: 'cyan',   placeholder: '98' },
  ];

  const isFieldMissing = (field) =>
    field.required && !String(formData[field.key] || '').trim();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2 flex items-center">
          <Activity className="w-6 h-6 mr-2 text-emerald-600" />
          Vitals
        </h2>
        <p className="text-gray-500 text-sm">
          <span className="text-red-500 font-medium">BP</span> and <span className="text-red-500 font-medium">Pulse</span> are required - the rest unlock once these are filled.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {vitalFields.map((field) => {
          const missing = isFieldMissing(field);
          return (
            <div
              key={field.key}
              className={`rounded-xl p-4 border-2 transition-colors ${
                missing ? 'bg-red-50/60 border-red-200' : 'bg-gray-50 border-transparent'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <field.icon className={`w-5 h-5 text-${field.color}-500`} />
                <label className="text-sm font-medium text-gray-700">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={formData[field.key]}
                  onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg font-semibold"
                />
                <span className="text-sm text-gray-500">{field.unit}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* BP Display */}
      {formData.bpSystolic && formData.bpDiastolic && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-center">
            <span className="text-lg font-semibold text-red-700">
              Blood Pressure: {formData.bpSystolic}/{formData.bpDiastolic} mmHg
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

// ============ MEDICINES SECTION ============
const MedicinesSection = ({ prescription, setPrescription, enums, templateMode = false, doctorId = null, doctorSpecialityId = null }) => {
  const showToast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  // Controls the search-results dropdown so it can be dismissed (click-outside / Escape) without
  // clearing the typed text.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchBoxRef = useRef(null);
  // Keyboard navigation of the results. -1 means nothing is highlighted, so a bare Enter does not
  // silently pick whatever happens to be first. The list is a set of buttons rather than a <select>,
  // so this has to be driven by hand; it mirrors what the shared Select component already does.
  const [highlight, setHighlight] = useState(-1);
  const resultsRef = useRef(null);
  // Listening only while the results actually show. Picking a medicine clears the search but leaves
  // searchOpen set, and a listener still running behind the Add Medicine dialog took its Escape.
  const resultsShown = searchOpen && searchQuery.length >= 2;
  useEffect(() => {
    if (!resultsShown) return;
    const onDown = (e) => { if (!searchBoxRef.current?.contains(e.target)) setSearchOpen(false); };
    // Capture phase, stopped: Escape closes the results and not a dialog the search sits in.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setSearchOpen(false);
      setHighlight(-1);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
  }, [resultsShown]);

  const searchMedicines = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const results = await medicineAPI.search(query);
      // Discontinued medicines are no longer carried — don't offer them for new prescriptions.
      setSearchResults((results || []).filter(r => !r.discontinued));
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) searchMedicines(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // The same medicine shouldn't be added twice to one prescription. Matched on the inventory id
  // when there is one, because a repackaged liquid can legitimately exist as two records with the
  // same name and different bottle sizes; falling back to the name covers external medicines,
  // which have no inventory row.
  // Shared with template apply (medicineAlreadyOn), so both skip exactly the same lines.
  const medicineAlreadyAdded = (name, inventoryId = null) =>
    medicineAlreadyOn(prescription.medicines, name, inventoryId);

  const handleSelectMedicine = (medicine) => {
    if (medicineAlreadyAdded(medicine.name, medicine.id)) {
      showToast('info', `${medicine.name} is already added`);
      setSearchQuery('');
      setSearchResults([]);
      return;
    }
    setSelectedMedicine(medicine);
    setSearchQuery('');
    setSearchResults([]);
    setShowAddModal(true);
  };

  const handleAddExternalMedicine = () => {
    if (medicineAlreadyAdded(searchQuery)) {
      showToast('info', `${searchQuery.trim()} is already added`);
      setSearchQuery('');
      setSearchResults([]);
      return;
    }
    setSelectedMedicine({ name: searchQuery, isExternal: true });
    setSearchQuery('');
    setSearchResults([]);
    setShowAddModal(true);
  };

  // The one path a result is chosen by, whether it was clicked or Entered, so the keyboard cannot
  // prescribe something the mouse would refuse.
  const chooseResult = (med) => {
    if (medicineAlreadyAdded(med.name, med.id)) {
      showToast('info', `${med.name} is already added`);
      return;
    }
    if (stockStateFor(med).isOut) {
      showToast('warning', `${med.name} is out of stock and can't be prescribed.`);
      // A patient's medicine the shelf could not supply is demand the pharmacy missed, and the
      // purchase planner counts it. A template being written is not a patient, so it is not.
      if (!templateMode && med.id) {
        reorderAPI.noteRefused(med.id, prescription?.id || null, med.status === 'EXPIRED' ? 'EXPIRED' : 'OUT_OF_STOCK');
      }
      return;
    }
    handleSelectMedicine(med);
  };

  // The external-medicine row sits at the end of the list and is a real option, so it takes the
  // last index rather than being unreachable from the keyboard.
  const optionCount = searchResults.length + 1;
  const onSearchKeyDown = (e) => {
    if (e.key === 'Escape') { setSearchOpen(false); setHighlight(-1); return; }
    if (!searchOpen || searchQuery.length < 2) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, optionCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      // Nothing highlighted means Enter is not a selection; typing and hitting Enter should not
      // prescribe whatever happened to sort first.
      if (highlight < 0) return;
      e.preventDefault();
      if (highlight === searchResults.length) handleAddExternalMedicine();
      else if (searchResults[highlight]) chooseResult(searchResults[highlight]);
    }
  };

  // Typing changes the list under the cursor, so the highlight cannot survive it.
  useEffect(() => { setHighlight(-1); }, [searchQuery]);

  // Keep the highlighted row in view: the list scrolls at max-h-60, so arrowing past the fold
  // would otherwise move a selection nobody can see. Queried by attribute rather than child index
  // because the list also holds a "no matching medicine" row that is not an option.
  useEffect(() => {
    if (highlight < 0 || !resultsRef.current) return;
    const el = resultsRef.current.querySelector(`[data-opt="${highlight}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  // Which already-added medicine is being edited (re-opens the dosage modal pre-filled). null = none.
  const [editingMedicine, setEditingMedicine] = useState(null);

  const handleRemoveMedicine = async (medicineId) => {
    // Template mode edits the local pseudo-prescription only — nothing hits the server.
    if (templateMode) {
      setPrescription(p => ({ ...p, medicines: (p.medicines || []).filter(m => m.id !== medicineId) }));
      return;
    }
    try {
      const updated = await prescriptionAPI.removeMedicine(prescription.id, medicineId);
      // The server's copy, less nothing the doctor has not saved yet (the bedside sugar tick).
      setPrescription(prev => keepLocalRxEdits(prev, updated));
    } catch (err) {
      console.error('Failed to remove medicine:', err);
    }
  };

  return (
    <>
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          <Pill className="w-[15px] h-[15px] text-emerald-600" />Medications
        </div>
        <span className="text-xs text-gray-400">{prescription.medicines?.length || 0} added</span>
      </div>

      {/* Search Bar */}
      <div className="relative mb-4" ref={searchBoxRef}>
        <Search className="absolute left-3 top-2.5 w-[18px] h-[18px] text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
          onFocus={() => setSearchOpen(true)}
          onKeyDown={onSearchKeyDown}
          role="combobox"
          aria-expanded={searchOpen && searchQuery.length >= 2}
          aria-activedescendant={highlight >= 0 ? `med-opt-${highlight}` : undefined}
          placeholder="Search medicine - name, generic or code"
          className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
        {isSearching && (
          <div className="absolute right-3 top-2.5">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div>
          </div>
        )}

        {/* Search Results Dropdown */}
        {searchOpen && searchQuery.length >= 2 && !isSearching && (
          <div ref={resultsRef} className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
            {searchResults.map((med, optIdx) => {
              // The doctor sees the stock COUNT, in the unit handed over (owner, 2026-11-02; it was
              // hidden as confidential before). Out-of-stock / expired still blocks prescribing.
              // pendingQuantity = units on today's undispensed prescriptions — advisory only:
              // stock that's likely spoken for by patients ahead of this one.
              const { isOut, isReserved, isLow } = stockStateFor(med);
              const count = stockCount(med);
              const prefix = typePrefix(med.medicineType, med.name);
              const added = medicineAlreadyAdded(med.name, med.id);
              // Bottle size is part of the identity for a repackaged liquid: the same syrup can sit
              // on the shelf as both 20ml and 40ml bottles, at different prices. Without it in the
              // subline the two are indistinguishable here.
              const subline = [med.genericName, med.manufacturer, med.containerSize, med.code].filter(Boolean).join(' · ');
              return (
                <button
                  key={med.id}
                  id={`med-opt-${optIdx}`}
                  data-opt={optIdx}
                  onMouseEnter={() => setHighlight(optIdx)}
                  onClick={() => chooseResult(med)}
                  aria-disabled={isOut || added}
                  className={`w-full px-4 py-2.5 text-left flex items-center justify-between gap-3 ${
                    isOut || added ? 'opacity-70 cursor-not-allowed' : ''
                  } ${highlight === optIdx ? 'bg-emerald-100 shadow-[inset_4px_0_0_0_rgb(var(--c-emerald-600))]' : ''}`}
                >
                  <div className="min-w-0">
                    <div className="truncate">
                      {prefix && (
                        <span className="text-emerald-600 font-semibold mr-1">{prefix}</span>
                      )}
                      <span className="font-medium">{med.name}</span>
                      <span className="text-gray-400 ml-2 text-sm">{med.strength}</span>
                    </div>
                    {subline && <div className="text-[11px] text-gray-400 truncate">{subline}</div>}
                  </div>
                  {added ? (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium inline-flex items-center gap-1 flex-shrink-0">
                      <Check className="w-3 h-3" /> Added
                    </span>
                  ) : isOut ? (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium flex-shrink-0">
                      {med.status === 'EXPIRED' ? 'Expired' : 'Out of stock'}
                    </span>
                  ) : isReserved ? (
                    <span
                      title="Today's earlier prescriptions may use up the remaining stock before this patient pays"
                      className="text-[10px] px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium flex-shrink-0 whitespace-nowrap"
                    >
                      Reserved today{count && ` · ${count.text}`}
                    </span>
                  ) : isLow ? (
                    <span title={count?.title} className="text-[10px] px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium flex-shrink-0 whitespace-nowrap">
                      Low stock{count && ` · ${count.text}`}
                    </span>
                  ) : (
                    <span title={count?.title} className="text-[10px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 font-medium inline-flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
                      <Box className="w-3 h-3" /> {count ? count.text : 'In stock'}
                    </span>
                  )}
                </button>
              );
            })}
            {searchResults.length === 0 && (
              <div className="px-4 py-3 text-sm text-gray-500 border-b border-gray-100">
                No matching medicine in inventory.
              </div>
            )}
            <button
              id={`med-opt-${searchResults.length}`}
              data-opt={searchResults.length}
              onMouseEnter={() => setHighlight(searchResults.length)}
              onClick={handleAddExternalMedicine}
              className={`w-full px-4 py-3 text-left text-emerald-600 text-sm ${
                searchResults.length > 0 ? 'border-t border-gray-100' : ''
              } ${highlight === searchResults.length ? 'bg-emerald-100 shadow-[inset_4px_0_0_0_rgb(var(--c-emerald-600))]' : ''}`}
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Add "{searchQuery}" as external medicine
            </button>
          </div>
        )}
      </div>

      {/* Prescribed Medicines List */}
      <div className="space-y-3">
        {prescription.medicines?.length > 0 ? (
          prescription.medicines.map((med, idx) => {
            // Build dosage chips from the structured fields (fall back to the composed English
            // string for legacy rows that lack them).
            const doseChips = [];
            [['Morning', 'in'], ['Afternoon', 'in'], ['Evening', 'in'], ['Night', 'at']].forEach(([t, prep]) => {
              const v = med[`dose${t}`];
              if (v && Number(v) > 0) doseChips.push(`${doseLabel(Number(v))} ${prep} ${t}`);
            });
            // Written by frequency rather than per-slot doses — the frequency IS the dosage line.
            if (doseChips.length === 0 && med.frequency) {
              doseChips.push((enums.frequencies || []).find(f => f.value === med.frequency)?.label || med.frequency);
            }
            // Timing + duration read as one phrase ("After Meal for 5 days"), so single chip.
            const timingLabel = (enums.timings || []).find(x => x.value === med.timing)?.label;
            // Continuous / Until Finished ARE the course. A continuous row's number is days of
            // supply, not a course length, so it must never be read back out as "for 15 continuous".
            const durationText = UNCOUNTED_DURATION_UNITS.has(String(med.durationUnit || '').toUpperCase())
              ? [
                  (enums.durationUnits || []).find(d => d.value === med.durationUnit)?.label || '',
                  med.durationUnit === 'CONTINUOUS' && med.durationValue ? `(${med.durationValue} days)` : '',
                ].filter(Boolean).join(' ')
              : med.durationValue ? `for ${med.durationValue} ${String(med.durationUnit || 'DAYS').toLowerCase()}` : '';
            if (timingLabel || durationText) doseChips.push([timingLabel, durationText].filter(Boolean).join(' '));
            // Backend rows carry the duration separately in durationUrdu; local/template rows
            // already embed it inside dosageInstructionUrdu (durationUrdu absent there).
            const urduLine = [med.dosageInstructionUrdu, med.durationUrdu].filter(Boolean).join(' - ');
            const instrPairs = med.specialInstructions ? pairInstructions(med.specialInstructions, med.specialInstructionsUrdu) : [];
            // A saved line carries its inventory record, so the count as of loading this Rx.
            // A template line has none and keeps the plain label.
            const lineStock = stockCount(med.medicine);
            const prefix = typePrefix(med.medicineType, med.medicineName);
            return (
              <div key={med.id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold flex items-center justify-center flex-none">{idx + 1}</span>
                    <div className="min-w-0 flex-1">
                      {/* Name line — name + type prefix + stock pill */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">
                          {prefix && <span className="text-emerald-600">{prefix} </span>}
                          {med.medicineName}
                          {med.strength && <span className="text-gray-400 font-normal text-sm ml-1">{med.strength}</span>}
                        </span>
                        {med.isFromInventory === false ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">External</span>
                        ) : (
                          <span title={lineStock?.title} className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full"><Box className="w-3 h-3" />{lineStock ? lineStock.text : 'In stock'}</span>
                        )}
                        {/* An injection or drip course given here, one dose per visit. */}
                        {isGivenHere(med) && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full">
                            <Syringe className="w-3 h-3" aria-hidden="true" />Given here · {courseDoses(med)} visits
                          </span>
                        )}
                      </div>
                      {/* Dosage line — English chips (left), Urdu dosage (right) */}
                      {(doseChips.length > 0 || med.dosageInstructionEnglish || urduLine) && (
                        <div className="flex items-start justify-between gap-4 mt-2">
                          {doseChips.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {doseChips.map((c, i) => (
                                <span key={i} className="text-[11px] px-2 py-0.5 rounded-md bg-gray-100 text-gray-600">{c}</span>
                              ))}
                            </div>
                          ) : med.dosageInstructionEnglish ? (
                            <p className="text-sm text-gray-600">{med.dosageInstructionEnglish}</p>
                          ) : (
                            <span />
                          )}
                          {urduLine && (
                            <div className="urdu-text text-base text-gray-700 text-right leading-loose flex-none max-w-[55%]" dir="rtl">{urduLine}</div>
                          )}
                        </div>
                      )}
                      {/* Per-medicine instructions — each as English (Urdu) */}
                      {instrPairs.length > 0 && (
                        <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Instructions</div>
                          <div className="space-y-0.5">
                            {instrPairs.map((p, i) => (
                              <div key={i} className="text-[13px] text-gray-700 flex items-start gap-1.5">
                                <span className="text-emerald-500 mt-0.5">•</span>
                                <span>{p.en}{p.ur && <span className="urdu-text text-gray-500 ml-2" dir="rtl">({p.ur})</span>}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-0.5 flex-none">
                    <button onClick={() => setEditingMedicine(med)} className="p-2 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50" title="Edit dosage">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleRemoveMedicine(med.id)} className="p-2 rounded-lg text-gray-400 hover:text-rose-500 hover:bg-rose-50" title="Remove">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex items-center justify-center gap-2 py-3 text-sm text-gray-400">
            <Pill className="w-4 h-4 opacity-60" />
            <span>No medicines yet - search above to add.</span>
          </div>
        )}
      </div>
    </div>

      {/* Add / Edit Medicine Modal */}
      {((showAddModal && selectedMedicine) || editingMedicine) && (
        <AddMedicineModal
          medicine={editingMedicine
            ? {
                name: editingMedicine.medicineName,
                medicineType: editingMedicine.medicineType,
                strength: editingMedicine.strength,
                // Inventory link: backend prescription rows carry it as nested `.medicine.id`;
                // local (template) rows carry `inventoryMedicineId`. Preserve either so editing an
                // inventory medicine doesn't drop the link and turn it "External".
                id: editingMedicine.medicine?.id ?? editingMedicine.inventoryMedicineId ?? null,
                isExternal: !editingMedicine.isFromInventory,
                // For the header's stock count only. `quantity`, not `totalQuantity`, so the
                // shortfall warning keeps reading the live search figure it was built on.
                ...(editingMedicine.medicine && {
                  quantity: editingMedicine.medicine.quantity,
                  packagingType: editingMedicine.medicine.packagingType,
                  containerSize: editingMedicine.medicine.containerSize,
                  dispenseSize: editingMedicine.medicine.dispenseSize,
                  sizeUnit: editingMedicine.medicine.sizeUnit,
                }),
              }
            : selectedMedicine}
          editMedicine={editingMedicine}
          prescriptionId={prescription.id}
          enums={enums}
          localMode={templateMode}
          doctorId={doctorId}
          doctorSpecialityId={doctorSpecialityId}
          onClose={() => {
            setShowAddModal(false);
            setSelectedMedicine(null);
            setEditingMedicine(null);
          }}
          onAdd={(payload) => {
            // Template mode: onAdd hands back a single medicine object; normal mode hands back the
            // full refreshed prescription. On edit we replace in place (local) or take the refreshed
            // prescription (normal, where the server swapped the old line for the edited one).
            // The refreshed prescription keeps any unsaved bedside sugar tick (keepLocalRxEdits).
            if (editingMedicine) {
              if (templateMode) {
                setPrescription(p => ({ ...p, medicines: (p.medicines || []).map(m => m.id === editingMedicine.id ? { ...payload, id: editingMedicine.id } : m) }));
              } else {
                setPrescription(prev => keepLocalRxEdits(prev, payload));
              }
            } else if (templateMode) {
              setPrescription(p => ({ ...p, medicines: [...(p.medicines || []), payload] }));
            } else {
              setPrescription(prev => keepLocalRxEdits(prev, payload));
            }
            setShowAddModal(false);
            setSelectedMedicine(null);
            setEditingMedicine(null);
          }}
        />
      )}
    </>
  );
};

// ============ ADD MEDICINE MODAL ============
const AddMedicineModal = ({ medicine, prescriptionId, enums, onClose, onAdd, localMode = false, editMedicine = null, doctorId = null, doctorSpecialityId = null }) => {
  const showToast = useToast();
  // When editing an already-added medicine, pre-fill every field from it; otherwise start fresh
  // from the selected inventory medicine.
  const [formData, setFormData] = useState(() => editMedicine ? {
    medicineName: editMedicine.medicineName || medicine.name || '',
    medicineType: editMedicine.medicineType || 'TABLET',
    strength: editMedicine.strength || '',
    inventoryMedicineId: editMedicine.inventoryMedicineId ?? (medicine.id || null),
    doseMorning: editMedicine.doseMorning || 0,
    doseAfternoon: editMedicine.doseAfternoon || 0,
    doseEvening: editMedicine.doseEvening || 0,
    doseNight: editMedicine.doseNight || 0,
    doseUnit: editMedicine.doseUnit || doseConfigFor(editMedicine.medicineType).unit,
    // How much per dose in frequency mode. Older lines have no amount, so default to one unit
    // rather than leaving the picker blank.
    doseAmount: editMedicine.doseAmount || 1,
    frequency: editMedicine.frequency || 'DAILY',
    frequencyIntervalDays: editMedicine.frequencyIntervalDays ?? '',
    timing: editMedicine.timing || 'AFTER_MEAL',
    durationValue: editMedicine.durationValue
      || (editMedicine.durationUnit === 'CONTINUOUS' ? CONTINUOUS_SUPPLY_DAYS.DEFAULT : DEFAULT_COURSE_DAYS),
    durationUnit: editMedicine.durationUnit || 'DAYS',
    specialInstructions: editMedicine.specialInstructions || '',
    specialInstructionsUrdu: editMedicine.specialInstructionsUrdu || '',
    // Given here one dose per visit, or take-home. Null on a line that cannot be a course; the
    // choice shows (defaulting to here) whenever the line becomes one.
    givenAtClinic: editMedicine.givenAtClinic ?? null,
  } : {
    medicineName: medicine.name || '',
    medicineType: medicine.medicineType || 'TABLET',
    strength: medicine.strength || '',
    inventoryMedicineId: medicine.id || null,
    doseMorning: 0,
    doseAfternoon: 0,
    doseEvening: 0,
    doseNight: 0,
    doseUnit: doseConfigFor(medicine.medicineType).unit,
    doseAmount: 1,
    frequency: 'DAILY',
    frequencyIntervalDays: '',
    timing: 'AFTER_MEAL',
    durationValue: DEFAULT_COURSE_DAYS,
    durationUnit: 'DAYS',
    specialInstructions: '',
    specialInstructionsUrdu: '',
    givenAtClinic: null,
  });
  const [saving, setSaving] = useState(false);
  const [urduPreview, setUrduPreview] = useState('');
  const addMedicineDialogRef = useDialog(onClose);
  // Type-aware dosage units (tablet → گولی, syrup → ml/ایم ایل, drops → قطرے, etc.).
  const doseConfig = doseConfigFor(formData.medicineType, formData.doseUnit);
  // Volume or spoons is the doctor's call, and changing it clears every dose on the form: the single
  // amount AND the four time-of-day doses. A number carried across units is a dosing error, 2.5 read
  // as ml and then as teaspoons being 2.5 ml against 12.5 ml. The time-of-day doses used to be kept,
  // so in "dosage by time" 2.5 ml silently became 2½ tsp, and 5 ml showed as blank but saved as
  // 5 tsp. Choosing the unit that is already selected changes nothing.
  const changeDoseUnit = (unit) => setFormData(prev => (prev.doseUnit === unit.value ? prev : {
    ...prev,
    doseUnit: unit.value,
    doseAmount: unit.dflt,
    doseMorning: 0, doseAfternoon: 0, doseEvening: 0, doseNight: 0,
  }));

  // Dosage and Frequency are two ways of saying the same thing, so the modal shows exactly one.
  // Frequency is the default: most medicines are written as a schedule ("Twice Daily", "Every 8
  // Hours") rather than as four per-slot amounts, so opening on the slots meant the common case
  // started with a click to get out of it. Per-slot dosage stays one click away for the medicines
  // that genuinely need a different amount morning and night.
  // An already-added medicine still reopens in whichever mode it was written in, whatever the
  // default is: reopening a slot-dosed line in frequency mode would show none of its doses.
  const [useFrequency, setUseFrequency] = useState(() => {
    if (!editMedicine) return true;
    // Per-slot doses are the only thing that forces dosage mode, and the two modes are mutually
    // exclusive by construction (switching either way clears the other's fields). So "has no slot
    // doses" is the whole test: it puts a frequency-written line back in frequency mode, and it
    // also lands a line carrying NEITHER on the default rather than on the mode it did not use.
    return !['Morning', 'Afternoon', 'Evening', 'Night']
      .some(k => Number(editMedicine[`dose${k}`]) > 0);
  });
  const switchToFrequency = () => {
    setUseFrequency(true);
    setCustomSlots({});
    setFormData(prev => ({
      ...prev,
      doseMorning: 0, doseAfternoon: 0, doseEvening: 0, doseNight: 0,
      frequency: prev.frequency || 'ONCE_DAILY',
    }));
  };
  const switchToDosage = () => {
    setUseFrequency(false);
    // Drop the frequency so the saved medicine carries one instruction, not two.
    setFormData(prev => ({ ...prev, frequency: null }));
  };

  // Per-slot "Custom..." mode. A slot flips to a free numeric input when the doctor needs an amount
  // the presets don't cover (4 tablets a day, say). Seeded on open so editing a medicine that
  // already carries an off-preset dose shows the value instead of silently snapping to a preset.
  const [customSlots, setCustomSlots] = useState(() => {
    const cfg = doseConfigFor(editMedicine ? editMedicine.medicineType : medicine.medicineType);
    if (cfg.unit === 'ml' || !editMedicine) return {};
    const seeded = {};
    for (const key of ['Morning', 'Afternoon', 'Evening', 'Night']) {
      const v = Number(editMedicine[`dose${key}`]) || 0;
      if (v > 0 && !cfg.opts.includes(v)) seeded[`dose${key}`] = true;
    }
    return seeded;
  });

  // Reusable medication instructions (Settings → Patient Instructions → Medication). The doctor can
  // click a chip to add it to Special Instructions, or add a brand-new one (EN + Urdu) that's saved
  // to the catalog for future use.
  const [medInstructions, setMedInstructions] = useState([]);
  const [showAddInstr, setShowAddInstr] = useState(false);
  const [showInstrPicker, setShowInstrPicker] = useState(false); // collapsed by default to save space
  const [newInstrEn, setNewInstrEn] = useState('');
  const [newInstrUrdu, setNewInstrUrdu] = useState('');
  const [savingInstr, setSavingInstr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await prescriptionAPI.getConfig('MEDICATION_INSTRUCTION', doctorId);
        const arr = Array.isArray(list) ? list : [];
        setMedInstructions(arr);
        // Reconcile the Urdu for already-selected instructions from the catalog. Fixes medicines
        // saved before the Urdu was stored (and avoids needing to re-toggle a chip on Edit): any
        // selected English instruction with a missing Urdu gets it back from its catalog entry.
        setFormData(prev => {
          const ens = (prev.specialInstructions || '').split(/;\s*/).map(s => s.trim()).filter(Boolean);
          if (ens.length === 0) return prev;
          const ursRaw = (prev.specialInstructionsUrdu || '').split(/؛\s*/).map(s => s.trim());
          const urs = ens.map((en, i) => ursRaw[i] || arr.find(it => it.valueEnglish === en)?.valueUrdu || '');
          const joined = urs.join('؛ ');
          return joined === (prev.specialInstructionsUrdu || '') ? prev : { ...prev, specialInstructionsUrdu: joined };
        });
      } catch { /* non-fatal — chips just won't show */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  // Special Instructions is a "; "-separated list of English phrases, with a parallel Urdu list
  // ("؛ "-separated) kept index-aligned so the chosen instruction also prints in Urdu.
  const instrParts = () => (formData.specialInstructions || '').split(/;\s*/).map(s => s.trim()).filter(Boolean);
  const toggleInstr = (en, urdu = '') => {
    setFormData(prev => {
      const ens = (prev.specialInstructions || '').split(/;\s*/).map(s => s.trim()).filter(Boolean);
      const ursRaw = (prev.specialInstructionsUrdu || '').split(/؛\s*/).map(s => s.trim());
      // Align the Urdu list to the English list by index (pad with '' so lengths match) BEFORE we
      // add/remove — the two strings split differently when one is empty, which previously dropped
      // the Urdu of a freshly-added instruction.
      const urs = ens.map((_, i) => ursRaw[i] || '');
      const idx = ens.indexOf(en);
      if (idx >= 0) {
        ens.splice(idx, 1);
        urs.splice(idx, 1);
      } else {
        ens.push(en);
        urs.push(urdu || '');
      }
      return {
        ...prev,
        specialInstructions: ens.join('; '),
        specialInstructionsUrdu: urs.join('؛ '),
      };
    });
  };
  const handleAddInstr = async () => {
    if (!newInstrEn.trim() || !newInstrUrdu.trim()) {
      showToast('error', 'Both English and Urdu are required');
      return;
    }
    setSavingInstr(true);
    try {
      const created = await instructionAPI.create({
        type: 'MEDICATION_INSTRUCTION',
        valueEnglish: newInstrEn.trim(),
        valueUrdu: newInstrUrdu.trim(),
        // Tag it to this doctor's speciality so it stays out of other disciplines' pickers.
        // Doctors without a speciality contribute to the general list.
        specialityId: doctorSpecialityId || '',
      });
      setMedInstructions(prev => [...prev, created]);
      toggleInstr(created.valueEnglish, created.valueUrdu); // auto-add to this medicine
      setNewInstrEn(''); setNewInstrUrdu(''); setShowAddInstr(false);
      showToast('success', 'Instruction saved for future use');
    } catch (e) {
      showToast('error', e.message || 'Failed to save instruction');
    } finally {
      setSavingInstr(false);
    }
  };

  // Generate Urdu preview when dosage changes.
  //
  // durationUnit is in here for the same reason durationValue is: the preview names the unit, so
  // switching Days to Weeks has to rebuild it. Leaving it out was half of why changing the unit
  // did nothing visible — the other half being that the line said "دن" whatever you picked.
  // enums.durationUnits matters too: the preview reads the unit's Urdu from it, so a preview
  // rendered before the enums finish loading has to be redone once they arrive.
  //
  // frequencyIntervalDays is the same trap once more: the interval is part of the wording it
  // builds ("ہر 3 دن بعد"), so leaving it out left the preview showing whatever the interval was
  // when the FREQUENCY last changed - the default 2, "ایک دن چھوڑ کر" - no matter what was typed
  // afterwards. Anything the preview reads has to be listed here.
  useEffect(() => {
    generateUrduPreview();
  }, [
    formData.doseMorning, formData.doseAfternoon, formData.doseEvening, formData.doseNight,
    formData.doseAmount, formData.durationValue, formData.durationUnit,
    formData.frequency, formData.frequencyIntervalDays,
    useFrequency,
    // Both enum lists are read for their Urdu, so a preview built before either finishes loading
    // has to be redone once it arrives. unitUrdu is a plain string, so listing it costs nothing
    // and keeps the rule above true without exception.
    enums.durationUnits, enums.frequencies, doseConfig.unitUrdu,
  ]);

  const generateUrduPreview = () => {
    // Whole numbers use Western digits even in Urdu text (user rule); only fractions keep
    // their spoken Urdu words.
    const urduDose = (dose) => {
      if (dose === 0.25) return 'چوتھائی';
      if (dose === 0.5) return 'آدھی';
      if (dose === 0.75) return 'پون';
      if (dose === 1.5) return 'ڈیڑھ';
      if (dose === 2.5) return 'ڈھائی';
      return dose.toString();
    };

    const u = doseConfig.unitUrdu;
    // When the unit is Latin (e.g. "ml"), keep "5 ml" in left-to-right order inside the RTL line
    // by wrapping the dose+unit in a Unicode LTR isolate (U+2066 … U+2069) — otherwise bidi
    // reorders it to "ml 5".
    const dn = (dose) => {
      // A spoon also carries its millilitres, "1 چمچ (5 ml)", the volume in its own LTR isolate for
      // the same reason. Mirrors PrescriptionService.doseUnitUrdu, which stores the same wording.
      const ml = doseConfig.spoonOpts ? ` ⁦(${spoonMl(dose, doseConfig.mlPer)})⁩` : '';
      const s = `${/[A-Za-z]/.test(u) ? String(dose) : urduDose(dose)} ${u}${ml}`;
      return /[A-Za-z]/.test(u) ? `⁦${s}⁩` : s;
    };
    const parts = [];
    if (useFrequency) {
      // Frequency mode has no per-slot doses: the dose amount says how much and the frequency says
      // how often, so both are needed for the line to mean anything.
      const f = (enums.frequencies || []).find(x => x.value === formData.frequency);
      const amount = parseFloat(formData.doseAmount) || 0;
      // Interval dosing carries its number, so its Urdu is built here rather than read off the
      // enum, whose stored phrase is only a fallback. Mirrors PrescriptionService.frequencyUrdu,
      // which writes the same wording onto the saved prescription. Western digits, per the house
      // rule for numbers in Urdu.
      const n = parseInt(formData.frequencyIntervalDays, 10);
      const freqUrdu = formData.frequency === 'EVERY_N_DAYS' && n > 0
        ? (n === 2 ? 'ایک دن چھوڑ کر' : `ہر ${n} دن بعد`)
        : f?.urdu;
      if (freqUrdu) parts.push(amount > 0 ? `${dn(amount)} ${freqUrdu}` : freqUrdu);
    } else {
      if (formData.doseMorning > 0) parts.push(`صبح ${dn(formData.doseMorning)}`);
      if (formData.doseAfternoon > 0) parts.push(`دوپہر ${dn(formData.doseAfternoon)}`);
      if (formData.doseEvening > 0) parts.push(`شام ${dn(formData.doseEvening)}`);
      if (formData.doseNight > 0) parts.push(`رات ${dn(formData.doseNight)}`);
    }

    // "Until finished" / "Continuous" are not a count of doses, so neither goes through the
    // "N <unit> کے لیے" phrasing. Continuous then appends how many days' worth is being handed
    // over, after the unit rather than instead of it: "مسلسل - 10 دن" says the course continues AND
    // that 10 days were given. Mirrors PrescriptionService, which stores the same phrase.
    if (parts.length > 0 && UNCOUNTED_DURATION_UNITS.has(formData.durationUnit)) {
      const unit = (enums.durationUnits || []).find(x => x.value === formData.durationUnit);
      const supply = Number(formData.durationValue) || CONTINUOUS_SUPPLY_DAYS.DEFAULT;
      if (unit?.urdu) {
        parts.push(formData.durationUnit === 'CONTINUOUS'
          ? `- ${unit.urdu} - ${supply} دن`
          : `- ${unit.urdu}`);
      }
    } else if (parts.length > 0 && formData.durationValue) {
      // Read the unit's Urdu from the same enum the Unit dropdown is built from, rather than the
      // hardcoded "دن" that used to sit here: picking Weeks or Months changed the English but left
      // the preview claiming days, and that preview is what the doctor checks the Urdu against
      // before adding the medicine. Falling back to دن keeps the line sane if a unit ever arrives
      // without an Urdu name; the backend stores its own copy from the same enum, so the two agree.
      const unit = (enums.durationUnits || []).find(x => x.value === formData.durationUnit);
      parts.push(`- ${formData.durationValue} ${unit?.urdu || 'دن'} کے لیے`);
    }

    setUrduPreview(parts.join('، '));
  };

  const handleSave = async () => {
    // Caught before the spinner so the doctor is told what is missing rather than watching a save
    // fail. The server enforces the same rule; this only saves them the round trip.
    if (useFrequency && formData.frequency === 'EVERY_N_DAYS') {
      const n = parseInt(formData.frequencyIntervalDays, 10);
      if (!n || n < 2) {
        showToast('error', 'Enter how many days apart the doses are, at least 2. For one a day, pick Daily.');
        return;
      }
    }
    setSaving(true);
    try {
      // Dose fields can be strings (the syrup/suspension free-text ml inputs); the backend expects
      // numbers, so coerce before building any payload.
      const numericDoses = {
        doseMorning: parseFloat(formData.doseMorning) || 0,
        doseAfternoon: parseFloat(formData.doseAfternoon) || 0,
        doseEvening: parseFloat(formData.doseEvening) || 0,
        doseNight: parseFloat(formData.doseNight) || 0,
      };
      // One instruction per medicine: frequency mode saves no doses, dosage mode saves no
      // frequency. Guards against a stale value left behind by toggling between the two.
      const modeFields = useFrequency
        ? {
            doseMorning: 0, doseAfternoon: 0, doseEvening: 0, doseNight: 0,
            frequency: formData.frequency || 'ONCE_DAILY',
            doseAmount: parseFloat(formData.doseAmount) || null,
            // Only interval dosing carries a number; anything else sends null so switching a line
            // off it cannot leave a stale interval behind on a now-daily medicine.
            frequencyIntervalDays: formData.frequency === 'EVERY_N_DAYS'
              ? (parseInt(formData.frequencyIntervalDays, 10) || null)
              : null,
          }
        // Explicit null clears any amount left behind by toggling out of frequency mode, the same
        // way frequency is nulled here.
        : { ...numericDoses, frequency: null, doseAmount: null, frequencyIntervalDays: null };
      // Template (local) mode — build the medicine object client-side with the SAME field shape we
      // normally POST, plus a temp id and a display dosage string, and hand it back without the API.
      if (localMode) {
        const dosageParts = [];
        if (!useFrequency) {
          if (formData.doseMorning > 0) dosageParts.push(`${doseLabel(formData.doseMorning)} in Morning`);
          if (formData.doseAfternoon > 0) dosageParts.push(`${doseLabel(formData.doseAfternoon)} in Afternoon`);
          if (formData.doseEvening > 0) dosageParts.push(`${doseLabel(formData.doseEvening)} in Evening`);
          if (formData.doseNight > 0) dosageParts.push(`${doseLabel(formData.doseNight)} at Night`);
        }
        // "Until Finished" / "Continuous" read as the course itself, not a count of one: "for
        //  until finished" is not English. Without this branch, hiding the number left the saved
        // line as "for  until_finished".
        const durationUnitLabel = ((enums.durationUnits || [])
          .find(d => d.value === formData.durationUnit)?.label
          || formData.durationUnit || 'Days').toLowerCase();
        const durationText = UNCOUNTED_DURATION_UNITS.has(formData.durationUnit)
          ? (formData.durationUnit === 'CONTINUOUS'
              ? `${durationUnitLabel} - ${Number(formData.durationValue) || CONTINUOUS_SUPPLY_DAYS.DEFAULT} days`
              : durationUnitLabel)
          : `for ${formData.durationValue} ${durationUnitLabel}`;
        const frequencyLabel = (enums.frequencies || []).find(f => f.value === formData.frequency)?.label;
        const freqAmount = parseFloat(formData.doseAmount) || 0;
        const dosageEnglish = dosageParts.length > 0
          ? `${dosageParts.join(', ')} ${doseConfig.unit}(s) ${durationText}`
          : (useFrequency && frequencyLabel
              ? `${freqAmount > 0 ? `${doseLabel(freqAmount)} ${doseConfig.unit}(s) - ` : ''}${frequencyLabel} ${durationText}`
              : '');
        onAdd({
          id: `tmp_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
          ...formData,
          ...modeFields,
          inventoryMedicineId: medicine.isExternal ? null : medicine.id,
          isFromInventory: !medicine.isExternal,
          dosageInstructionEnglish: dosageEnglish,
          dosageInstructionUrdu: urduPreview,
        });
        return;
      }
      // Edit (normal mode): no update endpoint, so the edited line is added in place of the old one,
      // which the server removes in the same save. Two calls (remove, then add) lost the line
      // whenever the add was refused.
      const updated = await prescriptionAPI.addMedicine(prescriptionId, {
        ...formData,
        ...modeFields,
        inventoryMedicineId: medicine.isExternal ? null : medicine.id,
        ...(editMedicine ? { replaceMedicineId: editMedicine.id } : {}),
      });
      onAdd(updated);
    } catch (err) {
      console.error('Failed to add medicine:', err);
      showToast('error', 'Failed to add medicine: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Stock-aware advisory for low-stock inventory medicines. The actual count stays hidden from
  // the doctor (out-of-stock items can't be added at all); here we only warn — non-blocking —
  // when the prescribed course needs more units than are in stock, and suggest fewer days.
  // Only meaningful for discrete-unit medicines (1 dose = 1 unit of stock); for syrup/drops/etc.
  // the dose is measured in ml/drops while stock is counted in bottles/vials, so we don't compare.
  const doseEqualsStockUnit = ['TABLET', 'CAPSULE'].includes(formData.medicineType);
  const slotDoses = (Number(formData.doseMorning) || 0) + (Number(formData.doseAfternoon) || 0)
    + (Number(formData.doseEvening) || 0) + (Number(formData.doseNight) || 0);
  // Frequency mode has no slot doses, so derive the daily amount the same way the backend's
  // DispenseQty does — otherwise the stock warning stayed silent for every frequency-written
  // medicine, which is exactly where a long course can exhaust stock.
  const perDayDoses = useFrequency
    ? (parseFloat(formData.doseAmount) || 0) * (FREQ_PER_DAY[formData.frequency] || 0)
    : slotDoses;
  const UNIT_DAYS = { DAYS: 1, WEEKS: 7, MONTHS: 30, CONTINUOUS: 1 };
  // Continuous lines carry days of supply rather than a course length; the backend snaps the same
  // bounds, so a legacy row opened for editing shows a usable figure instead of a blank.
  const supplyDays = Math.min(
    CONTINUOUS_SUPPLY_DAYS.MAX,
    Math.max(CONTINUOUS_SUPPLY_DAYS.MIN, Number(formData.durationValue) || CONTINUOUS_SUPPLY_DAYS.DEFAULT),
  );
  const continuousSupplyQty = perDayDoses * supplyDays;
  // A continuous line's figure is a day count, so the shortfall warning must read "15 days" rather
  // than the raw unit name ("15 continuous").
  const durationPhrase = formData.durationUnit === 'CONTINUOUS'
    ? `${supplyDays} days`
    : `${formData.durationValue} ${(formData.durationUnit || 'days').toLowerCase()}`;
  // Continuous reads the clamped supply figure, so the shortfall warning is measured against the
  // exact number of days the stepper is showing rather than a raw, possibly empty, field.
  const totalDays = formData.durationUnit === 'CONTINUOUS'
    ? supplyDays
    : (Number(formData.durationValue) || 0) * (UNIT_DAYS[formData.durationUnit] || 1);
  const availableStock = medicine.isExternal ? null : (medicine.totalQuantity ?? null);
  const totalNeeded = perDayDoses * totalDays;
  const stockShort = doseEqualsStockUnit && availableStock != null && perDayDoses > 0 && totalDays > 0 && totalNeeded > availableStock;
  const maxDaysAffordable = (availableStock != null && perDayDoses > 0) ? Math.floor(availableStock / perDayDoses) : null;
  const fmtN = (n) => (Number.isInteger(n) ? n : Number(n.toFixed(1)));

  const TIME_META = [
    { key: 'Morning', Icon: Sunrise, cls: 'text-amber-500' },
    { key: 'Afternoon', Icon: Sun, cls: 'text-amber-500' },
    { key: 'Evening', Icon: Sunset, cls: 'text-orange-500' },
    { key: 'Night', Icon: Moon, cls: 'text-indigo-400' },
  ];
  const headerSubline = [medicine.genericName, medicine.manufacturer, medicine.code].filter(Boolean).join(' · ');
  const headerStock = medicine.isExternal ? null : stockCount(medicine);
  const headerPrefix = typePrefix(formData.medicineType, medicine.name);

  return (
    <div ref={addMedicineDialogRef} className="fixed inset-0 bg-black/55 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">{editMedicine ? 'Edit medicine' : 'Add medicine'}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-bold tracking-tight text-gray-900">
                {headerPrefix && <span className="text-emerald-600">{headerPrefix} </span>}
                {medicine.name}
                {formData.strength && <span className="text-gray-400 font-normal text-base ml-1">{formData.strength}</span>}
              </h3>
              {medicine.isExternal ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">External</span>
              ) : (
                <span title={headerStock?.title} className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full"><Box className="w-3 h-3" />{headerStock ? headerStock.text : 'In stock'}</span>
              )}
            </div>
            {headerSubline &&<div className="text-[12px] text-gray-400 mt-0.5 truncate">{headerSubline}</div>}
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 -mr-2 rounded-lg text-gray-400 hover:bg-gray-100 flex-shrink-0"><X className="w-[18px] h-[18px]" /></button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Low-stock advisory — shown at the top only when the course exceeds available stock. */}
          {stockShort && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3.5 flex items-start gap-2.5 text-sm text-amber-800">
              <AlertTriangle className="w-[18px] h-[18px] flex-shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-semibold">Not enough stock for the full course</p>
                <p className="mt-0.5 text-[13px]">
                  This dosage needs <strong>{fmtN(totalNeeded)}</strong> {(formData.doseUnit || 'unit').toLowerCase()}(s) for{' '}
                  {durationPhrase}, but the available stock falls short by{' '}
                  <strong>{fmtN(totalNeeded - availableStock)}</strong>.{' '}
                  {maxDaysAffordable >= 1
                    ? <>Consider reducing to about <strong>{maxDaysAffordable} day{maxDaysAffordable !== 1 ? 's' : ''}</strong>, or prescribe the remainder as an external medicine.</>
                    : <>The current stock can't cover even a single day at this dosage.</>}
                </p>
              </div>
            </div>
          )}

          {/* Type — only for external medicines (inventory ones carry their own type). */}
          {medicine.isExternal && (
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Type</label>
              <Select
                value={formData.medicineType}
                onChange={(newType) => {
                  setFormData(prev => ({
                    ...prev,
                    medicineType: newType,
                    doseUnit: doseConfigFor(newType).unit,
                    doseMorning: 0, doseAfternoon: 0, doseEvening: 0, doseNight: 0,
                  }));
                  setCustomSlots({}); // the new type has its own presets
                }}
                options={Object.keys(DOSE_CONFIG).map(t => ({
                  value: t,
                  label: t.charAt(0) + t.slice(1).toLowerCase(),
                }))}
                buttonClassName="px-3 py-2"
              />
            </div>
          )}

          {/* Dosage — Morning / Afternoon / Evening / Night with time-of-day icons. Hidden when the
              doctor switches to frequency: the two describe the same thing, so only one is filled. */}
          {!useFrequency && (
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-2">
              Dosage <span className="text-gray-400 font-normal">(in {doseConfig.unit.toLowerCase()})</span>
              {doseConfig.isLiquid && (
                <span className="ml-2 inline-flex rounded-lg border border-gray-200 overflow-hidden align-middle">
                  {LIQUID_DOSE_UNITS.map(u => (
                    <button
                      key={u.value}
                      type="button"
                      onClick={() => changeDoseUnit(u)}
                      title={`Prescribe in ${u.label}`}
                      className={`px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                        doseConfig.unit === u.value
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {u.value === 'ml' ? 'ml' : u.value}
                    </button>
                  ))}
                </span>
              )}
            </label>
            <div className="grid grid-cols-4 gap-3">
              {TIME_META.map(({ key, Icon, cls }) => {
                const fkey = `dose${key}`;
                const empty = !(Number(formData[fkey]) > 0);
                return (
                  <div key={key} className="rounded-xl border border-gray-200 p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-[11px] font-semibold text-gray-500 mb-2">
                      <Icon className={`w-[13px] h-[13px] ${cls}`} />{key}
                    </div>
                    {doseConfig.spoonOpts ? (
                      // Spoons — the same fixed list, plus 0 for a time of day with no dose.
                      <Select
                        value={Number(formData[fkey]) || 0}
                        onChange={(v) => setFormData(prev => ({ ...prev, [fkey]: parseFloat(v) }))}
                        options={[0, ...doseConfig.spoonOpts].map(v => ({
                          value: v,
                          label: v > 0 ? `${doseLabel(v)} (${spoonMl(v, doseConfig.mlPer)})` : doseLabel(v),
                        }))}
                        buttonClassName="px-2 py-1.5 text-center"
                      />
                    ) : doseConfig.isLiquid ? (
                      // ml — free text so the doctor can enter any volume.
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        inputMode="decimal"
                        value={Number(formData[fkey]) > 0 ? formData[fkey] : ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [fkey]: e.target.value }))}
                        placeholder="ml"
                        className={`w-full px-2 py-1.5 text-sm text-center font-semibold border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 bg-white ${empty ? 'text-gray-400' : ''}`}
                      />
                    ) : customSlots[fkey] ? (
                      // Custom amount — for doses the presets don't cover (e.g. 4 tablets a day).
                      <>
                        <input
                          type="number"
                          min="0"
                          max="20"
                          step="0.25"
                          inputMode="decimal"
                          autoFocus
                          value={Number(formData[fkey]) > 0 ? formData[fkey] : ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, [fkey]: e.target.value }))}
                          placeholder={doseConfig.unit.toLowerCase()}
                          className={`w-full px-2 py-1.5 text-sm text-center font-semibold border border-emerald-400 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 bg-white ${empty ? 'text-gray-400' : ''}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            // Snap back to the closest preset so the select can display the value.
                            const current = Number(formData[fkey]) || 0;
                            const snapped = doseConfig.opts.reduce(
                              (best, v) => (Math.abs(v - current) < Math.abs(best - current) ? v : best),
                              doseConfig.opts[0],
                            );
                            setFormData(prev => ({ ...prev, [fkey]: snapped }));
                            setCustomSlots(prev => ({ ...prev, [fkey]: false }));
                          }}
                          className="mt-1 text-[11px] text-emerald-600 hover:text-emerald-700 hover:underline"
                        >
                          Presets
                        </button>
                      </>
                    ) : (
                      <Select
                        value={formData[fkey]}
                        onChange={(v) => {
                          if (v === 'custom') {
                            setCustomSlots(prev => ({ ...prev, [fkey]: true }));
                            return;
                          }
                          setFormData(prev => ({ ...prev, [fkey]: parseFloat(v) }));
                        }}
                        options={[
                          ...doseConfig.opts.map(v => ({ value: v, label: doseLabel(v) })),
                          { value: 'custom', label: 'Custom...' },
                        ]}
                        buttonClassName={'px-2 py-1.5 font-semibold ' + (empty ? 'text-gray-400' : '')}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={switchToFrequency}
              className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50"
            >
              <Clock className="w-3.5 h-3.5" />Use frequency instead
            </button>
          </div>
          )}

          {/* Frequency — the alternative to per-slot dosage, e.g. "Every 6 hours", "As needed".
              Dose sits alongside it: the frequency says how often, the dose says how much, and
              without both the line reads "Daily, for 5 days" without ever stating a quantity. */}
          {useFrequency && (
          <div>
            <div className="grid grid-cols-[1fr_1fr] gap-4">
              <div>
                <div className="flex items-center justify-between mb-1.5 gap-2">
                  <label className="block text-sm font-semibold text-gray-600">
                    Dose <span className="text-gray-400 font-normal">(in {doseConfig.unit.toLowerCase()})</span>
                  </label>
                  {/* Volume or spoons, the doctor's call. Changing it clears the dose rather than
                      converting it; see changeDoseUnit. */}
                  {doseConfig.isLiquid && (
                    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                      {LIQUID_DOSE_UNITS.map(u => (
                        <button
                          key={u.value}
                          type="button"
                          onClick={() => changeDoseUnit(u)}
                          title={`Prescribe in ${u.label}`}
                          className={`px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                            doseConfig.unit === u.value
                              ? 'bg-emerald-600 text-white'
                              : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {u.value === 'ml' ? 'ml' : u.value}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {doseConfig.spoonOpts ? (
                  // Spoons come in halves, not decimals: a fixed list rendered as fractions.
                  <Select
                    value={Number(formData.doseAmount) || 1}
                    onChange={(v) => setFormData(prev => ({ ...prev, doseAmount: parseFloat(v) }))}
                    options={doseConfig.spoonOpts.map(v => ({
                      value: v,
                      label: `${doseLabel(v)} ${doseConfig.unit} (${spoonMl(v, doseConfig.mlPer)})`,
                    }))}
                    buttonClassName="px-3 py-2"
                  />
                ) : doseConfig.isLiquid ? (
                  // ml — free text so any volume can be entered, matching the by-time inputs.
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    inputMode="decimal"
                    value={Number(formData.doseAmount) > 0 ? formData.doseAmount : ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, doseAmount: e.target.value }))}
                    placeholder="ml"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                ) : customSlots.doseAmount ? (
                  <>
                    <input
                      type="number"
                      min="0"
                      max="20"
                      step="0.25"
                      inputMode="decimal"
                      autoFocus
                      value={Number(formData.doseAmount) > 0 ? formData.doseAmount : ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, doseAmount: e.target.value }))}
                      placeholder={doseConfig.unit.toLowerCase()}
                      className="w-full px-3 py-2 text-sm border border-emerald-400 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const current = Number(formData.doseAmount) || 0;
                        const snapped = doseConfig.opts.reduce(
                          (best, v) => (Math.abs(v - current) < Math.abs(best - current) ? v : best),
                          doseConfig.opts[0],
                        );
                        setFormData(prev => ({ ...prev, doseAmount: snapped }));
                        setCustomSlots(prev => ({ ...prev, doseAmount: false }));
                      }}
                      className="mt-1 text-[11px] text-emerald-600 hover:text-emerald-700 hover:underline"
                    >
                      Presets
                    </button>
                  </>
                ) : (
                  <Select
                    value={formData.doseAmount}
                    onChange={(v) => {
                      if (v === 'custom') {
                        setCustomSlots(prev => ({ ...prev, doseAmount: true }));
                        return;
                      }
                      setFormData(prev => ({ ...prev, doseAmount: parseFloat(v) }));
                    }}
                    options={[
                      ...doseConfig.opts.map(v => ({ value: v, label: doseLabel(v) })),
                      { value: 'custom', label: 'Custom...' },
                    ]}
                    buttonClassName="px-3 py-2"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1.5">Frequency</label>
                <Select
                  value={formData.frequency}
                  onChange={(v) => setFormData(prev => ({
                    ...prev,
                    frequency: v,
                    // Default alternate-days the moment interval dosing is picked, so the field is
                    // never blank and the commonest answer needs no typing. Cleared on the way out
                    // so a stale interval cannot ride along on a daily medicine.
                    frequencyIntervalDays: v === 'EVERY_N_DAYS'
                      ? (prev.frequencyIntervalDays || 2)
                      : '',
                  }))}
                  options={enums.frequencies.map(f => ({ value: f.value, label: f.label + ' (' + f.urdu + ')' }))}
                  buttonClassName="px-3 py-2"
                />
                {/* The number behind "Every N Days". This is what Custom never had: it was offered
                    as a choice, asked for nothing, and stored nothing. */}
                {formData.frequency === 'EVERY_N_DAYS' && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-gray-600">Every</span>
                    <input
                      type="number"
                      min="2"
                      max="30"
                      value={formData.frequencyIntervalDays}
                      onChange={(e) => setFormData(prev => ({ ...prev, frequencyIntervalDays: e.target.value }))}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    <span className="text-sm text-gray-600">
                      days{Number(formData.frequencyIntervalDays) === 2 ? ' (alternate days)' : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={switchToDosage}
              className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50"
            >
              <Sunrise className="w-3.5 h-3.5" />Use dosage by time instead
            </button>
          </div>
          )}

          {/* Timing · Duration · Unit — not equal thirds: Timing carries the longest values
              ("After Meal (کھانے کے بعد)") while Duration is a number and Unit is a short word,
              so an even split cropped the timing label.

              "Until Finished" and "Continuous" ARE the duration: a number alongside them means
              nothing ("5 until finished"), so the Duration box is dropped and Unit takes its
              width. Every other unit, Doses included, still counts something. */}
          <div className={`grid gap-4 ${UNCOUNTED_DURATION_UNITS.has(formData.durationUnit)
            ? 'grid-cols-[1.7fr_2fr]'
            : 'grid-cols-[1.7fr_1fr_1fr]'}`}>
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Timing</label>
              <Select
                value={formData.timing}
                onChange={(v) => setFormData(prev => ({ ...prev, timing: v }))}
                options={enums.timings.map(t => ({ value: t.value, label: t.label + ' (' + t.urdu + ')' }))}
                buttonClassName="px-3 py-2"
              />
            </div>
            {!UNCOUNTED_DURATION_UNITS.has(formData.durationUnit) && (
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1.5">Duration</label>
                <input
                  type="number"
                  value={formData.durationValue ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, durationValue: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
                  min="1"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-1.5">Unit</label>
              <Select
                value={formData.durationUnit}
                onChange={(v) => setFormData(prev => ({
                  ...prev,
                  durationUnit: v,
                  durationValue: durationValueForUnit(v, prev.durationUnit, prev.durationValue),
                }))}
                options={enums.durationUnits.map(d => ({ value: d.value, label: d.label + ' (' + d.urdu + ')' }))}
                buttonClassName="px-3 py-2"
              />
            </div>
          </div>

          {/* Continuous supply. The therapy is open-ended, but the pharmacy still has to hand over
              a countable number of units, so the doctor sets how many days' worth to dispense now.
              Kept visibly apart from Duration because it is not a course length: the prescription
              still reads "Continuous", and only the quantity to bill and deduct changes. */}
          {formData.durationUnit === 'CONTINUOUS' && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-600">Dispense now</div>
                  <p className="text-[12px] text-gray-500 mt-0.5">
                    Treatment stays ongoing. This only sets how much to hand over today.
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    aria-label={`Reduce by ${CONTINUOUS_SUPPLY_DAYS.STEP} days`}
                    disabled={supplyDays <= CONTINUOUS_SUPPLY_DAYS.MIN}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      durationValue: Math.max(CONTINUOUS_SUPPLY_DAYS.MIN, supplyDays - CONTINUOUS_SUPPLY_DAYS.STEP),
                    }))}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <div className="w-[86px] text-center text-base font-bold text-gray-900 tabular-nums">
                    {supplyDays} days
                  </div>
                  <button
                    type="button"
                    aria-label={`Increase by ${CONTINUOUS_SUPPLY_DAYS.STEP} days`}
                    disabled={supplyDays >= CONTINUOUS_SUPPLY_DAYS.MAX}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      durationValue: Math.min(CONTINUOUS_SUPPLY_DAYS.MAX, supplyDays + CONTINUOUS_SUPPLY_DAYS.STEP),
                    }))}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {continuousSupplyQty > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200 text-[13px] text-gray-600">
                  Works out to{' '}
                  <strong className="text-gray-900">
                    {fmtN(continuousSupplyQty)} {doseConfig.unit.toLowerCase()}(s)
                  </strong>{' '}
                  to dispense and bill.
                </div>
              )}
            </div>
          )}

          {/* Where an injection or drip course is given. Here, one dose per visit, is the default:
              each visit's bill then carries that visit's dose, and the nurse records it given.
              Take home bills and hands over the whole course today, for injections a patient or a
              relative gives at home. */}
          {canBeGivenHere(formData, { external: !!medicine.isExternal }) && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                <Syringe className="w-4 h-4 text-emerald-600" aria-hidden="true" /> Where is it given?
              </div>
              <div role="radiogroup" aria-label="Where is it given?" className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { value: true, label: 'Here, one dose per visit',
                    hint: `${courseDoses(formData)} visits, each billed for that visit's dose` },
                  { value: false, label: 'Take home', hint: 'The whole course billed and handed over today' },
                ].map((opt) => {
                  const on = opt.value ? formData.givenAtClinic !== false : formData.givenAtClinic === false;
                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setFormData(prev => ({ ...prev, givenAtClinic: opt.value }))}
                      className={`text-left rounded-lg border px-3 py-2 transition-colors ${
                        on ? 'border-emerald-500 bg-white ring-2 ring-emerald-200' : 'border-gray-300 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <span className="block text-sm font-semibold text-gray-900">{opt.label}</span>
                      <span className="block text-xs text-gray-500">{opt.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Urdu Preview */}
          {urduPreview && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 mb-1.5">Urdu instruction preview</div>
              <p className="text-xl text-right urdu-text leading-loose text-gray-800" dir="rtl">{urduPreview}</p>
            </div>
          )}

          {/* Special Instructions — collapsed by default (just the selected ones + a button); the
              chip picker expands on demand so it doesn't occupy the whole modal. */}
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-1.5">Special instructions</label>

            {/* Collapsed summary — selected instructions + a button to open the picker */}
            {!showInstrPicker && (
              <div>
                {instrParts().length > 0 ? (
                  <div className="space-y-0.5 mb-2">
                    {pairInstructions(formData.specialInstructions, formData.specialInstructionsUrdu).map((p, i) => (
                      <div key={i} className="text-[13px] text-gray-700 flex items-start gap-1.5">
                        <span className="text-emerald-500 mt-0.5">•</span>
                        <span>{p.en}{p.ur && <span className="urdu-text text-gray-500 ml-2" dir="rtl">({p.ur})</span>}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 mb-2">No special instructions added.</p>
                )}
                <button
                  type="button"
                  onClick={() => setShowInstrPicker(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                >
                  <Plus className="w-3.5 h-3.5" />{instrParts().length > 0 ? 'Add / edit instructions' : 'Add instructions'}
                </button>
              </div>
            )}

            {/* Expanded picker */}
            {showInstrPicker && (
            <>
            {/* Saved medication-instruction pills — same English + Urdu format as the Advice-step
                Patient Instructions. Tap to add/remove from this medicine. */}
            <div className="flex flex-wrap gap-2 mb-2">
              {medInstructions.map((it) => {
                const selected = instrParts().includes(it.valueEnglish);
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => toggleInstr(it.valueEnglish, it.valueUrdu)}
                    className={`inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full border text-sm transition-colors ${
                      selected ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${selected ? 'bg-white/25' : 'border border-gray-300'}`}>
                      {selected && <Check className="w-3 h-3" />}
                    </span>
                    <span className="whitespace-nowrap">{it.valueEnglish}</span>
                    {it.valueUrdu && (
                      <span className={`text-xs urdu-text whitespace-nowrap ${selected ? 'text-emerald-100' : 'text-gray-400'}`} dir="rtl">{it.valueUrdu}</span>
                    )}
                  </button>
                );
              })}
              {/* Any selected instruction not in the catalog (legacy free-text) — shown so it stays
                  visible and removable now that the free-text box is gone. */}
              {instrParts().filter(p => !medInstructions.some(it => it.valueEnglish === p)).map((p, i) => (
                <button
                  key={`custom-${i}`}
                  type="button"
                  onClick={() => toggleInstr(p)}
                  className="inline-flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full border text-sm bg-emerald-600 border-emerald-600 text-white"
                >
                  <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center bg-white/25"><Check className="w-3 h-3" /></span>
                  <span className="whitespace-nowrap">{p}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowAddInstr(v => !v)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50"
              >
                <Plus className="w-3.5 h-3.5" /> Add new
              </button>
            </div>
            {showAddInstr && (
              <div className="mb-2 p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2">
                <input
                  type="text"
                  value={newInstrEn}
                  onChange={(e) => setNewInstrEn(e.target.value)}
                  placeholder="English instruction (e.g. Take with juice)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <input
                  type="text"
                  dir="rtl"
                  value={newInstrUrdu}
                  onChange={(e) => setNewInstrUrdu(e.target.value)}
                  placeholder="اردو ہدایت"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-lg urdu-text text-right"
                />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setShowAddInstr(false)} className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
                  <button type="button" onClick={handleAddInstr} disabled={savingInstr} className="px-3 py-1.5 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                    {savingInstr ? 'Saving…' : 'Save & add'}
                  </button>
                </div>
                <p className="text-[11px] text-gray-400">Saved to your medication instructions for future use.</p>
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { setShowInstrPicker(false); setShowAddInstr(false); }}
                className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700"
              >
                Done
              </button>
            </div>
            </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-white border border-gray-300 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm disabled:opacity-50"
          >
            {saving ? 'Saving…' : (editMedicine ? 'Update medicine' : 'Add medicine')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ LAB TESTS SECTION ============
/**
 * "Check the blood sugar here" — a glucometer reading taken at the clinic rather than a lab test.
 * Ticking it puts the lancet and the strip named in Settings, Clinical onto the bill, and stock
 * comes off them when the bill is paid.
 *
 * Stock is a WARNING, never a block. A doctor who knows there is a strip in the other drawer, or
 * who wants the reading taken regardless, ticks it anyway; the warning simply offers to undo it.
 */
const BedsideSugarCheck = ({ checked, onChange }) => {
  const [supplies, setSupplies] = useState(null);

  useEffect(() => {
    let cancelled = false;
    clinicalSettingsAPI.bedsideSugar()
      .then((d) => { if (!cancelled) setSupplies(d); })
      .catch(() => { if (!cancelled) setSupplies(null); });
    return () => { cancelled = true; };
  }, []);

  // Anything the clinic named that has run out. A consumable that was never configured is not
  // "out of stock", it is simply not billed, so it raises nothing here.
  const short = ['lancet', 'strip']
    .map((k) => supplies?.[k])
    .filter((x) => x && !x.inStock);

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <label className="flex items-start gap-2.5 cursor-pointer group">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
        />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-800 group-hover:text-gray-900">
            Check blood sugar here
          </span>
          <span className="urdu-text block text-sm text-gray-500" dir="rtl" style={{ lineHeight: 2.2 }}>
            کلینک میں شوگر ٹیسٹ کریں
          </span>
        </span>
      </label>

      {checked && short.length > 0 && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p>
              {short.map((x) => x.name).join(' and ')}{' '}
              {short.length === 1 ? 'is' : 'are'} out of stock. The reading can still be taken, and
              the bill will still carry {short.length === 1 ? 'it' : 'them'}.
            </p>
            <button
              type="button"
              onClick={() => onChange(false)}
              className="mt-1 font-semibold underline hover:no-underline"
            >
              Take it off
            </button>
          </div>
        </div>
      )}

      {checked && supplies && !supplies.lancet && !supplies.strip && (
        <p className="mt-2 text-xs text-gray-500">
          No lancet or strip is set under Settings, Clinical, so nothing will be added to the bill.
        </p>
      )}
    </div>
  );
};

const LabTestsSection = ({ prescription, setPrescription, templateMode = false, doctorId = null, doctorSpecialityId = null }) => {
  const notify = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  // Keyboard navigation of the results, as in MedicinesSection. -1 means nothing is highlighted, so a
  // bare Enter adds nothing.
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const searchBoxRef = useRef(null);
  const resultsRef = useRef(null);
  const [commonTests, setCommonTests] = useState([]);
  // Per-test instruction picker: which test's picker is open, the LAB_INSTRUCTION catalog (+ any
  // added inline), and the add-new form fields.
  const [instrTestId, setInstrTestId] = useState(null);
  const [labInstructions, setLabInstructions] = useState([]);
  const [extraInstructions, setExtraInstructions] = useState([]);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newEn, setNewEn] = useState('');
  const [newUrdu, setNewUrdu] = useState('');
  const [savingNew, setSavingNew] = useState(false);
  // Picking an instruction saves at once; only a half-written new one is unsaved work.
  const testInstructionsDialogRef = useDialog(() => { setInstrTestId(null); setShowAddNew(false); }, {
    isDirty: () => showAddNew && (newEn.trim() !== '' || newUrdu.trim() !== ''),
  });
  const allLabInstructions = [...labInstructions, ...extraInstructions];

  useEffect(() => {
    loadCommonTests();
    (async () => {
      try {
        const list = await prescriptionAPI.getConfig('LAB_INSTRUCTION', doctorId);
        setLabInstructions(Array.isArray(list) ? list : []);
      } catch { /* chips just won't show */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  // Instructions stored on a lab test as "; "-separated English + "؛ "-separated Urdu, index-aligned.
  const splitEns = (s) => (s || '').split(/;\s*/).map(x => x.trim()).filter(Boolean);
  const splitUrs = (s) => (s || '').split(/؛\s*/).map(x => x.trim());

  const persistTestInstr = async (test, ens, urs) => {
    const instructions = ens.join('; ');
    const instructionsUrdu = urs.join('؛ ');
    if (templateMode) {
      setPrescription(p => ({ ...p, labTests: (p.labTests || []).map(t => t.id === test.id ? { ...t, instructions, instructionsUrdu } : t) }));
      return;
    }
    try {
      const updated = await prescriptionAPI.updateLabTest(prescription.id, test.id, { instructions, instructionsUrdu });
      // Keeps an unsaved bedside sugar tick, which sits on this same card.
      setPrescription(prev => keepLocalRxEdits(prev, updated));
    } catch (err) {
      console.error('Failed to update test instructions:', err);
      notify('error', 'Failed to update test instructions');
    }
  };

  const toggleTestInstr = (test, en, urdu = '') => {
    const ens = splitEns(test.instructions);
    const ursRaw = splitUrs(test.instructionsUrdu);
    const urs = ens.map((_, i) => ursRaw[i] || '');
    const idx = ens.indexOf(en);
    if (idx >= 0) { ens.splice(idx, 1); urs.splice(idx, 1); }
    else { ens.push(en); urs.push(urdu || ''); }
    persistTestInstr(test, ens, urs);
  };

  const handleAddNewLabInstr = async (test) => {
    if (!newEn.trim() || !newUrdu.trim()) {
      notify('error', 'Both English and Urdu are required');
      return;
    }
    setSavingNew(true);
    try {
      // Tagged to this doctor's speciality (general when they have none) so it stays out of other
      // disciplines' pickers.
      const created = await instructionAPI.create({ type: 'LAB_INSTRUCTION', valueEnglish: newEn.trim(), valueUrdu: newUrdu.trim(), specialityId: doctorSpecialityId || '' });
      setExtraInstructions(prev => [...prev, created]);
      toggleTestInstr(test, created.valueEnglish, created.valueUrdu);
      setNewEn(''); setNewUrdu(''); setShowAddNew(false);
      notify('success', 'Instruction saved for future use');
    } catch (e) {
      notify('error', e.message || 'Failed to save instruction');
    } finally {
      setSavingNew(false);
    }
  };

  const loadCommonTests = async () => {
    try {
      // The doctor's speciality list first, then the general one. Falls back server-side to the
      // common-test catalog when no quick list has been configured.
      const tests = await prescriptionAPI.getQuickLabTests(doctorId);
      setCommonTests(tests);
    } catch (err) {
      console.error('Failed to load quick tests:', err);
    }
  };

  const searchTests = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const results = await prescriptionAPI.searchLabTests(query);
      setSearchResults(results);
    } catch (err) {
      console.error('Search failed:', err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) searchTests(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const listShown = searchOpen && searchResults.length > 0;
  useEffect(() => {
    if (!listShown) return;
    const onDown = (e) => {
      if (searchBoxRef.current?.contains(e.target)) return;
      setSearchOpen(false);
      setHighlight(-1);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [listShown]);

  // The list under the cursor changes when the query does, and again when its results arrive (the
  // old ones stay on screen meanwhile), so the highlight cannot survive either.
  useEffect(() => { setHighlight(-1); }, [searchQuery, searchResults]);

  // Keep the highlighted entry in view: the list scrolls at max-h-60.
  useEffect(() => {
    if (highlight < 0 || !resultsRef.current) return;
    const el = resultsRef.current.querySelector(`[data-opt="${highlight}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const handleAddTest = async (testName, instructions = '') => {
    // Prevent duplicates — the same lab test shouldn't be ordered twice on one prescription.
    const already = (prescription.labTests || []).some(
      t => (t.testName || '').trim().toLowerCase() === (testName || '').trim().toLowerCase()
    );
    if (already) {
      notify('info', `${testName} is already added`);
      setSearchQuery('');
      setSearchResults([]);
      return;
    }
    // Template (local) mode — append the lab test object (same field shape we POST) with a temp
    // id, without the API.
    if (templateMode) {
      setPrescription(p => ({
        ...p,
        labTests: [...(p.labTests || []), {
          id: `tmp_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
          testName,
          instructions,
          urgency: 'ROUTINE',
        }],
      }));
      setSearchQuery('');
      setSearchResults([]);
      return;
    }
    try {
      const updated = await prescriptionAPI.addLabTest(prescription.id, {
        testName,
        instructions,
        urgency: 'ROUTINE',
      });
      // Ticking bedside sugar and then adding a test must not untick it.
      setPrescription(prev => keepLocalRxEdits(prev, updated));
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      console.error('Failed to add test:', err);
      notify('error', err.message || 'Failed to add test');
    }
  };

  // The one path an entry is picked by, clicked or Entered, so both do the same thing.
  const pickTest = (testName) => {
    setSearchOpen(false);
    setHighlight(-1);
    handleAddTest(testName);
  };

  // Entries: the results, then "Add as new test" when that shows.
  const entryCount = searchResults.length + (searchQuery.length >= 2 ? 1 : 0);
  const onSearchKeyDown = (e) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      // Tab is not prevented: it still moves on, just without leaving the list open.
      setSearchOpen(false);
      setHighlight(-1);
      return;
    }
    if (!listShown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, entryCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      // Nothing highlighted: typing and pressing Enter must not add whatever sorted first.
      if (highlight < 0) return;
      e.preventDefault();
      if (highlight < searchResults.length) pickTest(searchResults[highlight].testName);
      else if (highlight === searchResults.length && searchQuery.length >= 2) pickTest(searchQuery);
    }
  };

  const handleRemoveTest = async (testId) => {
    if (templateMode) {
      setPrescription(p => ({ ...p, labTests: (p.labTests || []).filter(t => t.id !== testId) }));
      return;
    }
    try {
      const updated = await prescriptionAPI.removeLabTest(prescription.id, testId);
      setPrescription(prev => keepLocalRxEdits(prev, updated));
    } catch (err) {
      // A test with a lab report attached can't be removed (the report is linked to it). The
      // server explains why in a complete sentence, so show it rather than failing silently.
      console.error('Failed to remove test:', err);
      notify('error', err.message || 'Failed to remove test');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          <FlaskConical className="w-[15px] h-[15px] text-emerald-600" />Lab Tests
        </div>
        <span className="text-xs text-gray-400">{prescription.labTests?.length || 0} added</span>
      </div>

      {/* Search */}
      <div className="relative mb-3" ref={searchBoxRef}>
        <Search className="absolute left-3 top-2.5 w-[18px] h-[18px] text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
          onFocus={() => setSearchOpen(true)}
          onKeyDown={onSearchKeyDown}
          role="combobox"
          aria-expanded={listShown}
          aria-controls={listShown ? 'lab-test-results' : undefined}
          aria-activedescendant={highlight >= 0 ? `lab-opt-${highlight}` : undefined}
          placeholder="Search lab tests"
          className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />

        {listShown && (
          <div ref={resultsRef} id="lab-test-results" className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
            {searchResults.map((test, i) => (
              <button
                key={test.id}
                id={`lab-opt-${i}`}
                data-opt={i}
                tabIndex={-1}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pickTest(test.testName)}
                className={`w-full px-4 py-2.5 text-left ${highlight === i ? 'bg-emerald-100 shadow-[inset_4px_0_0_0_rgb(var(--c-emerald-600))]' : 'hover:bg-emerald-50'} flex items-center justify-between text-sm`}
              >
                <span>{test.testName}</span>
                <span className="text-gray-400 text-sm">{test.testNameUrdu}</span>
              </button>
            ))}
            {searchQuery.length >= 2 && (
              <button
                id={`lab-opt-${searchResults.length}`}
                data-opt={searchResults.length}
                tabIndex={-1}
                onMouseEnter={() => setHighlight(searchResults.length)}
                onClick={() => pickTest(searchQuery)}
                className={`w-full px-4 py-2.5 text-left ${highlight === searchResults.length ? 'bg-emerald-100 shadow-[inset_4px_0_0_0_rgb(var(--c-emerald-600))]' : 'hover:bg-gray-50'} border-t border-gray-100 text-emerald-600 text-sm`}
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Add "{searchQuery}" as new test
              </button>
            )}
          </div>
        )}
      </div>

      {/* Common Tests — tap to add */}
      <div className="mb-3">
        <div className="text-[11px] text-gray-400 mb-1.5">Common tests - tap to add</div>
        <div className="flex flex-wrap gap-1.5">
          {commonTests.slice(0, 12).map((test) => {
            const added = (prescription.labTests || []).some(
              t => (t.testName || '').trim().toLowerCase() === (test.testName || '').trim().toLowerCase()
            );
            return (
              <button
                key={test.id}
                onClick={() => handleAddTest(test.testName)}
                disabled={added}
                title={added ? 'Already added' : `Add ${test.testName}`}
                className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  added
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 cursor-default'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                {test.testName}
              </button>
            );
          })}
        </div>
      </div>

      {/* Added tests — each row carries a "+ Instructions" picker (attach EN + Urdu advice per test) */}
      {prescription.labTests?.length > 0 ? (
        <div className="space-y-2">
          {prescription.labTests.map((test, idx) => {
            const instrPairs = test.instructions ? pairInstructions(test.instructions, test.instructionsUrdu) : [];
            const open = instrTestId === test.id;
            return (
              <div key={test.id} className="rounded-lg border border-gray-200">
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-teal-50 text-teal-700 text-[11px] font-bold flex items-center justify-center flex-none">{idx + 1}</span>
                    <span className="text-sm text-gray-800 truncate">{test.testName}</span>
                    {test.testNameUrdu && <span className="text-gray-400 text-xs urdu-text flex-none" dir="rtl">{test.testNameUrdu}</span>}
                  </div>
                  <div className="flex items-center gap-1 flex-none">
                    <button
                      type="button"
                      onClick={() => { setInstrTestId(open ? null : test.id); setShowAddNew(false); }}
                      title="Attach instructions to this test"
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border transition-colors ${open ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                    >
                      <Plus className="w-3.5 h-3.5" />Instructions{instrPairs.length > 0 ? ` (${instrPairs.length})` : ''}
                    </button>
                    <button onClick={() => handleRemoveTest(test.id)} className="p-1.5 rounded-lg text-gray-300 hover:text-rose-500" title="Remove test">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Attached instructions — English (Urdu), one per line */}
                {instrPairs.length > 0 && (
                  <div className="px-3 pb-2 pl-10 space-y-0.5">
                    {instrPairs.map((p, i) => (
                      <div key={i} className="text-[12px] text-gray-600 flex items-start gap-1.5">
                        <span className="text-emerald-500 mt-0.5">•</span>
                        <span>{p.en}{p.ur && <span className="urdu-text text-gray-400 ml-2" dir="rtl">({p.ur})</span>}</span>
                      </div>
                    ))}
                  </div>
                )}

              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 py-2 text-sm text-gray-400">
          <FlaskConical className="w-4 h-4 opacity-60" />
          <span>No lab tests yet - search or use Common tests above.</span>
        </div>
      )}

      {/* Not a lab test: the reading the clinic takes itself, on the glucometer. It sits here
          because that is where the doctor is already thinking about sugar, and billing turns it
          into the two consumables it uses. Hidden in template mode, which has no patient. */}
      {!templateMode && (
        <BedsideSugarCheck
          checked={!!prescription.bedsideBloodSugar}
          onChange={(v) => setPrescription((prev) => ({ ...prev, bedsideBloodSugar: v }))}
        />
      )}

      {/* Per-test instruction picker — in a modal so the inline list stays compact */}
      {instrTestId && (() => {
        const t = (prescription.labTests || []).find(x => x.id === instrTestId);
        if (!t) return null;
        const selEns = splitEns(t.instructions);
        const close = () => { setInstrTestId(null); setShowAddNew(false); };
        return (
          <div ref={testInstructionsDialogRef} className="fixed inset-0 bg-black/55 flex items-center justify-center z-[60] p-4" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
              {/* header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">Test instructions</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-gray-900">{t.testName}</h3>
                    {t.testNameUrdu && <span className="text-sm text-gray-400 urdu-text" dir="rtl">{t.testNameUrdu}</span>}
                  </div>
                </div>
                <button aria-label="Close" onClick={close} className="p-2 -mr-2 rounded-lg text-gray-400 hover:bg-gray-100 flex-none"><X className="w-[18px] h-[18px]" /></button>
              </div>
              {/* body */}
              <div className="p-5 overflow-y-auto">
                <div className="text-[11px] text-gray-400 mb-2">Tap to attach an instruction to this test</div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {allLabInstructions.map((it) => {
                    const sel = selEns.includes(it.valueEnglish);
                    return (
                      <button
                        key={it.id || it.valueEnglish}
                        type="button"
                        onClick={() => toggleTestInstr(t, it.valueEnglish, it.valueUrdu)}
                        className={`inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border text-sm transition-colors ${sel ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                      >
                        <span className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${sel ? 'bg-white/25' : 'border border-gray-300'}`}>
                          {sel && <Check className="w-3 h-3" />}
                        </span>
                        <span className="whitespace-nowrap">{it.valueEnglish}</span>
                        {it.valueUrdu && <span className={`text-xs urdu-text whitespace-nowrap ${sel ? 'text-emerald-100' : 'text-gray-400'}`} dir="rtl">{it.valueUrdu}</span>}
                      </button>
                    );
                  })}
                  {selEns.filter(e => !allLabInstructions.some(it => it.valueEnglish === e)).map((e, i) => (
                    <button
                      key={`custom-${i}`}
                      type="button"
                      onClick={() => toggleTestInstr(t, e)}
                      className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border text-sm bg-emerald-600 border-emerald-600 text-white"
                    >
                      <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center bg-white/25"><Check className="w-3 h-3" /></span>
                      <span className="whitespace-nowrap">{e}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setShowAddNew(v => !v)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add new
                  </button>
                </div>
                {showAddNew && (
                  <div className="mt-2 p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2">
                    <input
                      type="text"
                      value={newEn}
                      onChange={(e) => setNewEn(e.target.value)}
                      placeholder="English instruction (e.g. Fasting 12 hours required)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      dir="rtl"
                      value={newUrdu}
                      onChange={(e) => setNewUrdu(e.target.value)}
                      placeholder="اردو ہدایت"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-lg urdu-text text-right"
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-gray-400">Saved to your lab &amp; radiology instructions for future use.</p>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setShowAddNew(false)} className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
                        <button type="button" onClick={() => handleAddNewLabInstr(t)} disabled={savingNew} className="px-3 py-1.5 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50">{savingNew ? 'Saving…' : 'Save & add'}</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* footer */}
              <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button onClick={close} className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700">Done</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// ============ INSTRUCTIONS SECTION ============
const InstructionsSection = ({ formData, setFormData, patientInstructions, doctorSpecialityId = null }) => {
  const showToast = useToast();
  // Instructions created inline are merged with the catalog list from the parent so they appear
  // (and stay selected) without a parent refetch.
  const [extraInstructions, setExtraInstructions] = useState([]);
  const [showAddInstr, setShowAddInstr] = useState(false);
  const [newInstrEn, setNewInstrEn] = useState('');
  const [newInstrUrdu, setNewInstrUrdu] = useState('');
  const [savingInstr, setSavingInstr] = useState(false);
  const allInstructions = [...patientInstructions, ...extraInstructions];

  const toggleInstruction = (key) => {
    setFormData(prev => ({
      ...prev,
      selectedInstructions: prev.selectedInstructions.includes(key)
        ? prev.selectedInstructions.filter(k => k !== key)
        : [...prev.selectedInstructions, key]
    }));
  };

  const handleAddInstr = async () => {
    if (!newInstrEn.trim() || !newInstrUrdu.trim()) {
      showToast('error', 'Both English and Urdu are required');
      return;
    }
    setSavingInstr(true);
    try {
      const created = await instructionAPI.create({
        type: 'PATIENT_INSTRUCTION',
        valueEnglish: newInstrEn.trim(),
        valueUrdu: newInstrUrdu.trim(),
        // Tagged to this doctor's speciality (general when they have none).
        specialityId: doctorSpecialityId || '',
      });
      setExtraInstructions(prev => [...prev, created]);
      if (created.configKey) toggleInstruction(created.configKey);
      setNewInstrEn(''); setNewInstrUrdu(''); setShowAddInstr(false);
      showToast('success', 'Instruction saved for future use');
    } catch (e) {
      showToast('error', e.message || 'Failed to save instruction');
    } finally {
      setSavingInstr(false);
    }
  };

  return (
    <>
      {/* Patient Instructions */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            <MessageSquare className="w-[15px] h-[15px] text-emerald-600" />Patient Instructions
          </div>
          {formData.selectedInstructions.length > 0 && (
            <span className="text-xs text-emerald-600 font-medium">{formData.selectedInstructions.length} selected</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {allInstructions.map((inst) => {
            const selected = formData.selectedInstructions.includes(inst.configKey);
            return (
              <button
                key={inst.configKey}
                type="button"
                onClick={() => toggleInstruction(inst.configKey)}
                className={`inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border text-sm transition-colors ${
                  selected
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${
                  selected ? 'bg-white/25' : 'border border-gray-300'
                }`}>
                  {selected && <Check className="w-3 h-3" />}
                </span>
                <span className="whitespace-nowrap">{inst.valueEnglish}</span>
                <span className={`text-xs urdu-text whitespace-nowrap ${selected ? 'text-emerald-100' : 'text-gray-400'}`} dir="rtl">
                  {inst.valueUrdu}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setShowAddInstr(v => !v)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50"
          >
            <Plus className="w-3.5 h-3.5" />Add new
          </button>
        </div>
        {showAddInstr && (
          <div className="mb-2 p-3 border border-gray-200 rounded-xl bg-gray-50 space-y-2">
            <input
              type="text"
              value={newInstrEn}
              onChange={(e) => setNewInstrEn(e.target.value)}
              placeholder="English instruction (e.g. Drink plenty of fluids)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <input
              type="text"
              dir="rtl"
              value={newInstrUrdu}
              onChange={(e) => setNewInstrUrdu(e.target.value)}
              placeholder="اردو ہدایت"
              className="w-full px-3 py-2 text-lg urdu-text text-right border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-gray-400">Saved to your patient instructions for future use.</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowAddInstr(false)} className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
                <button type="button" onClick={handleAddInstr} disabled={savingInstr} className="px-3 py-1.5 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50">{savingInstr ? 'Saving…' : 'Save & add'}</button>
              </div>
            </div>
          </div>
        )}
        <p className="text-[11px] text-gray-400">Tap to add/remove. These print on the prescription in English &amp; Urdu.</p>
      </div>
    </>
  );
};

// ============ FOLLOWUP SECTION ============
// Follow-up duration is captured as a number + unit (matching the Diagnosis "Since when"
// design) but stored as a plain day count (followupDays) since the appointment date is days+today.
const FOLLOWUP_UNITS = ['Days', 'Weeks', 'Months'];
const FOLLOWUP_MULT = { Days: 1, Weeks: 7, Months: 30 };
const QUICK_FOLLOWUP = [
  { label: '3 days',   value: 3, unit: 'Days' },
  { label: '1 week',   value: 1, unit: 'Weeks' },
  { label: '2 weeks',  value: 2, unit: 'Weeks' },
  { label: '1 month',  value: 1, unit: 'Months' },
  { label: '3 months', value: 3, unit: 'Months' },
];
// Pick the most natural unit for an existing day count (e.g. 14 → 2 Weeks, 30 → 1 Month).
const deriveFollowup = (days) => {
  const d = parseInt(days) || 0;
  if (d <= 0) return { value: '', unit: 'Days' };
  if (d % 30 === 0) return { value: d / 30, unit: 'Months' };
  if (d % 7 === 0) return { value: d / 7, unit: 'Weeks' };
  return { value: d, unit: 'Days' };
};

const FollowupSection = ({ formData, setFormData, followupTemplates }) => {
  const [urduText, setUrduText] = useState('');
  // Editing model (number + unit); followupDays stays the source of truth in days.
  const [fu, setFu] = useState(() => deriveFollowup(formData.followupDays));

  // Re-derive when followupDays changes externally (template / "Copy Last Rx" load) to a value
  // that doesn't match the current editing model — guarded so it never loops.
  useEffect(() => {
    const computed = fu.value === '' ? '' : (parseInt(fu.value) || 0) * FOLLOWUP_MULT[fu.unit];
    if (String(formData.followupDays || '') !== String(computed || '')) {
      setFu(deriveFollowup(formData.followupDays));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.followupDays]);

  const setFollowup = (next) => {
    setFu(next);
    const days = next.value === '' ? '' : (parseInt(next.value) || 0) * FOLLOWUP_MULT[next.unit];
    setFormData(prev => ({ ...prev, followupDays: days || '' }));
  };

  useEffect(() => {
    if (formData.followupDays) {
      fetchUrdu(formData.followupDays);
    } else {
      setUrduText('');
    }
  }, [formData.followupDays]);

  const fetchUrdu = async (days) => {
    try {
      const result = await prescriptionAPI.getFollowupUrdu(days);
      setUrduText(result.urdu);
    } catch (err) {
      console.error('Failed to get urdu:', err);
    }
  };

  const followDays = parseInt(formData.followupDays) || 0;
  const nextVisit = (() => {
    if (followDays <= 0) return null;
    const d = new Date();
    d.setDate(d.getDate() + followDays);
    return d;
  })();
  const relativeLabel = (() => {
    if (!fu.value || !nextVisit) return '';
    const n = Number(fu.value);
    const unit = n === 1 ? fu.unit.toLowerCase().replace(/s$/, '') : fu.unit.toLowerCase();
    return `in ${fu.value} ${unit}`;
  })();

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
        <Calendar className="w-[15px] h-[15px] text-emerald-600" />Follow-up
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-start">
        {/* Controls — number + unit + Clear, with the quick picks on the same line. They only
            drop to a second row when the card is genuinely too narrow to hold them. */}
        <div className="w-full sm:flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="number"
              min="0"
              value={fu.value}
              onChange={(e) => setFollowup({ ...fu, value: e.target.value })}
              placeholder="Duration"
              className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 flex-none"
            />
            <Select
              value={fu.unit}
              onChange={(v) => setFollowup({ ...fu, unit: v })}
              options={FOLLOWUP_UNITS}
              className="w-32 flex-none"
              buttonClassName="px-3 py-2"
            />
            {formData.followupDays ? (
              <button
                type="button"
                onClick={() => setFollowup({ value: '', unit: 'Days' })}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:text-red-600 hover:border-red-300 transition-colors flex-none"
              >
                Clear
              </button>
            ) : null}
            {/* Quick durations */}
            <span className="text-xs text-gray-400 ml-1">Quick:</span>
            {QUICK_FOLLOWUP.map(q => {
              const active = String(fu.value) === String(q.value) && fu.unit === q.unit;
              return (
                <button
                  key={q.label}
                  type="button"
                  onClick={() => setFollowup({ value: q.value, unit: q.unit })}
                  className={`px-3 py-1 text-sm rounded-full transition-colors flex-none ${
                    active ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                  }`}
                >
                  {q.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Resolved next-visit card — sized to its content so the controls keep the room they
            need to stay on one line. */}
        {nextVisit && (
          <div className="w-full sm:w-auto sm:flex-none rounded-xl border border-emerald-200 bg-emerald-50/50 p-3.5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Next visit</div>
                <div className="text-sm font-semibold text-gray-800 mt-0.5">
                  {formatClinicDate(nextVisit.toISOString(), { day: 'numeric', month: 'short', year: 'numeric' })}
                  {relativeLabel && <span className="font-normal text-gray-500"> · {relativeLabel}</span>}
                </div>
              </div>
              {urduText && <div className="urdu-text text-lg text-gray-700 text-right" dir="rtl">{urduText}</div>}
            </div>
          </div>
        )}
      </div>

      {/* Create Appointment — parked until the Appointment module ships. The createAppointment
          field is left in formData / save so this can be re-enabled by restoring this block. */}
    </div>
  );
};

// ============ REPORT NOTES MODAL ============

const ReportNotesModal = ({ notes = [], patientId, onClose, onRefresh }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(notes.length === 0);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ category: 'LAB', noteText: '' });
  const [saving, setSaving] = useState(false);
  // The form saves and stays open, so only a change since it last opened is unsaved.
  const noteChanged = useChangedSince(showForm ? draft : null, `${showForm}:${editingId}`);
  const reportNotesDialogRef = useDialog(onClose, { isDirty: () => noteChanged });

  const resetForm = () => {
    setEditingId(null);
    setDraft({ category: 'LAB', noteText: '' });
    setShowForm(false);
  };

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('clinic_auth_user') || 'null'); } catch { return null; }
  })();

  const handleSave = async () => {
    if (!draft.noteText.trim()) {
      showToast('error', 'Please write the note first');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await patientReportNoteAPI.update(patientId, editingId, {
          category: draft.category,
          noteText: draft.noteText.trim(),
        });
      } else {
        await patientReportNoteAPI.create(patientId, {
          category: draft.category,
          noteText: draft.noteText.trim(),
          authorId: currentUser?.id || null,
          authorName: currentUser?.fullName || currentUser?.username || null,
        });
      }
      showToast('success', editingId ? 'Note updated' : 'Note added');
      resetForm();
      onRefresh();
    } catch (err) {
      showToast('error', err.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (note) => {
    setDraft({ category: note.category || 'GENERAL', noteText: note.noteText || '' });
    setEditingId(note.id);
    setShowForm(true);
  };

  const handleDelete = async (note) => {
    const ok = await confirm({
      title: 'Remove note?',
      message: 'This note will no longer appear for this patient.',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await patientReportNoteAPI.remove(patientId, note.id);
      onRefresh();
    } catch (err) {
      showToast('error', err.message || 'Failed to remove note');
    }
  };

  return (
    <div ref={reportNotesDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center">
              <NotebookPen className="w-5 h-5 mr-2 text-emerald-600" />
              Report Notes
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              For reports the patient showed but did not leave a copy of. Visible on every future visit, never printed.
            </p>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {notes.length === 0 && !showForm && (
            <div className="text-center py-6 text-sm text-gray-400">No report notes for this patient yet.</div>
          )}

          {notes.map((note) => {
            const cat = reportNoteCategory(note.category);
            return (
              <div key={note.id} className="rounded-xl border border-gray-200 p-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cat.pill}`}>{cat.label}</span>
                    <p className="text-sm text-gray-800 mt-2 whitespace-pre-wrap break-words">{note.noteText}</p>
                    <p className="text-[11px] text-gray-400 mt-1.5">
                      {note.authorName ? `${note.authorName} · ` : ''}
                      {note.noteDate ? formatClinicDateTime(note.noteDate) : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => handleEdit(note)} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Edit">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(note)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Remove">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {showForm ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <Select
                  value={draft.category}
                  onChange={(v) => setDraft(prev => ({ ...prev, category: v }))}
                  options={REPORT_NOTE_CATEGORIES.map(c => ({ value: c.key, label: c.label }))}
                  buttonClassName="p-2.5 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note <span className="text-red-500">*</span></label>
                <textarea
                  value={draft.noteText}
                  onChange={(e) => setDraft(prev => ({ ...prev, noteText: e.target.value }))}
                  maxLength={REPORT_NOTE_MAXLEN}
                  rows={4}
                  placeholder="e.g. Seen past CBC on patient's phone. All values normal except RBC, which is low."
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 resize-none"
                />
                <div className="flex justify-end mt-1">
                  <CharCount value={draft.noteText} max={REPORT_NOTE_MAXLEN} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={resetForm} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : (editingId ? 'Update note' : 'Add note')}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-emerald-300 text-emerald-700 rounded-xl hover:bg-emerald-50 text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add note
            </button>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-3 flex justify-end">
          <button onClick={onClose} className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700">Done</button>
        </div>
      </div>
    </div>
  );
};

// ============ MEDICAL ALERTS MODAL ============
// The server's limits on an alert (MedicalAlert: title 200, description 1000).
const ALERT_TITLE_MAX = 200;
const ALERT_DESCRIPTION_MAX = 1000;
const MedicalAlertsModal = ({ alerts, patientId, prefill, onClose, onRefresh, enums }) => {
  const showToast = useToast();
  // When opened from the clinical-flag prompt, `prefill` seeds the form and opens it straight away.
  const confirm = useConfirm();
  const [showAddForm, setShowAddForm] = useState(!!prefill);
  const [editingId, setEditingId] = useState(null); // non-null = overwriting an existing alert
  const [newAlert, setNewAlert] = useState(prefill
    ? { alertType: prefill.alertType || 'ALLERGY_DRUG', title: (prefill.title || '').slice(0, ALERT_TITLE_MAX), description: (prefill.description || '').slice(0, ALERT_DESCRIPTION_MAX), severity: prefill.severity || 'MEDIUM' }
    : { alertType: 'ALLERGY_DRUG', title: '', description: '', severity: 'MEDIUM' });
  // Saves and stays open. A form opened pre-filled from the flag prompt starts out unchanged.
  const alertChanged = useChangedSince(showAddForm ? newAlert : null, `${showAddForm}:${editingId}`);
  const medicalAlertsDialogRef = useDialog(onClose, { isDirty: () => alertChanged });

  const resetForm = () => {
    setShowAddForm(false);
    setEditingId(null);
    setNewAlert({ alertType: 'ALLERGY_DRUG', title: '', description: '', severity: 'MEDIUM' });
  };

  const handleSaveAlert = async () => {
    if (!newAlert.title.trim()) {
      showToast('error', 'Please enter an alert title');
      return;
    }
    try {
      if (editingId) {
        await prescriptionAPI.updateMedicalAlert(patientId, editingId, newAlert);
      } else {
        await prescriptionAPI.addMedicalAlert(patientId, newAlert);
      }
      onRefresh();
      resetForm();
    } catch (err) {
      console.error('Failed to save alert:', err);
      // Surface the server's reason rather than a generic message, so a schema or validation
      // problem is diagnosable from the UI instead of only the console.
      showToast('error', err.message || 'Failed to save alert');
    }
  };

  const handleEditAlert = (alert) => {
    setNewAlert({
      alertType: alert.alertType || 'ALLERGY_DRUG',
      title: alert.title || '',
      description: alert.description || '',
      severity: alert.severity || 'MEDIUM',
    });
    setEditingId(alert.id);
    setShowAddForm(true);
  };

  const handleDeleteAlert = async (alert) => {
    const ok = await confirm({
      title: 'Remove alert?',
      message: `Remove "${alert.title}" from this patient's medical alerts?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await prescriptionAPI.deleteMedicalAlert(patientId, alert.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to remove alert:', err);
      showToast('error', err.message || 'Failed to remove alert');
    }
  };

  return (
    <div ref={medicalAlertsDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
            Medical Alerts
          </h3>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Existing Alerts */}
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`border rounded-xl p-4 ${SEVERITY_COLORS[alert.severity]}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h4 className="font-semibold">{alert.title}</h4>
                  <p className="text-sm opacity-80">{alert.alertType.replace(/_/g, ' ')}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className="text-xs font-medium mr-1">{alert.severity}</span>
                  <button onClick={() => handleEditAlert(alert)} title="Edit" className="p-1.5 rounded-lg hover:bg-black/5">
                    <Edit2 className="w-4 h-4 opacity-70" />
                  </button>
                  <button onClick={() => handleDeleteAlert(alert)} title="Remove" className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-100/60">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {alert.description && (
                <p className="mt-2 text-sm">{alert.description}</p>
              )}
            </div>
          ))}

          {alerts.length === 0 && !showAddForm && (
            <p className="text-center text-gray-500 py-4">No medical alerts recorded</p>
          )}

          {/* Add Form */}
          {showAddForm ? (
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{editingId ? 'Edit alert' : 'New alert'}</p>
              <Select
                value={newAlert.alertType}
                onChange={(v) => setNewAlert(prev => ({ ...prev, alertType: v }))}
                options={enums.alertTypes.map(t => ({ value: t.value, label: t.label }))}
                buttonClassName="px-3 py-2 rounded-lg"
              />
              {/* The server keeps a title to 200 characters and a description to 1,000. Without these
                  limits a longer second alert was refused with a message that named no cause. */}
              <input
                type="text"
                value={newAlert.title}
                maxLength={ALERT_TITLE_MAX}
                onChange={(e) => setNewAlert(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Alert title (e.g., Penicillin Allergy)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              {newAlert.title.length > ALERT_TITLE_MAX - 30 && (
                <p className="text-xs text-gray-500 -mt-2">{newAlert.title.length} of {ALERT_TITLE_MAX} characters. Put the detail in the description.</p>
              )}
              <textarea
                value={newAlert.description}
                maxLength={ALERT_DESCRIPTION_MAX}
                onChange={(e) => setNewAlert(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              {newAlert.description.length > ALERT_DESCRIPTION_MAX - 100 && (
                <p className="text-xs text-gray-500 -mt-2">{newAlert.description.length} of {ALERT_DESCRIPTION_MAX} characters</p>
              )}
              <Select
                value={newAlert.severity}
                onChange={(v) => setNewAlert(prev => ({ ...prev, severity: v }))}
                options={enums.severities.map(sv => ({ value: sv.value, label: sv.label }))}
                buttonClassName="px-3 py-2 rounded-lg"
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAlert}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg"
                >
                  {editingId ? 'Update Alert' : 'Add Alert'}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-emerald-400 hover:text-emerald-600 flex items-center justify-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Medical Alert
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ PRINT PREVIEW MODAL ============
const PrintPreviewModal = ({ prescription, patient, doctor, onClose, onDone, onEditPrescription }) => {
  const printRef = useRef();
  const printPreviewDialogRef = useDialog(onClose, { confirmDiscard: false });
  const [downloading, download] = usePdfDownload();
  const handleDownload = () => download(() => savePrescriptionPdf(printRef.current, {
    title: `Prescription ${prescription?.prescriptionNumber || ''}`.trim(),
    fileName: prescription?.prescriptionNumber || 'Prescription',
  }));

  const handlePrint = () => {
    printNode(printRef.current, `Prescription - ${prescription?.prescriptionNumber}`, {
      // After the OS print dialog returns (whether the user printed or cancelled), close
      // this preview modal too — the user shouldn't have to click "Done" separately.
      // Prefer onDone (the "completion" path) so any downstream success handling fires;
      // fall back to onClose otherwise.
      onAfterPrint: () => {
        if (onDone) onDone();
        else if (onClose) onClose();
      },
    });
  };

  return (
    <div ref={printPreviewDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold flex items-center">
            <Printer className="w-5 h-5 mr-2 text-emerald-600" />
            Print Preview
          </h3>
          <div className="flex items-center space-x-2">
            {onEditPrescription && (
              <button
                onClick={onEditPrescription}
                className="flex items-center space-x-2 px-4 py-2 text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <FileDown className="w-4 h-4" />
              <span>{downloading ? 'Making the PDF…' : 'Download PDF'}</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
            {onDone && (
              <button
                onClick={onDone}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg hover:from-green-600 hover:to-emerald-700"
              >
                <Check className="w-4 h-4" />
                <span>Done</span>
              </button>
            )}
            <button
              aria-label="Close"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto p-6 bg-gray-100">
          <div className="mx-auto" style={{ maxWidth: '210mm' }}>
            <PrescriptionPrint
              ref={printRef}
              prescription={prescription}
              patient={patient}
              doctor={doctor}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ TEMPLATES VIEW (all saved templates, any doctor) ============
// English labels for the instruction keys (mirrors the seeded PATIENT_INSTRUCTION configs) so
// the template preview can show readable instruction text.
const TEMPLATE_INSTRUCTION_EN = {
  AFTER_FOOD: 'Take medicine after food', BEFORE_FOOD: 'Take medicine before food',
  EMPTY_STOMACH: 'Take on empty stomach', WITH_WATER: 'Take with plenty of water',
  NO_DRIVING: 'Avoid driving after taking medicine', AVOID_SUN: 'Avoid direct sunlight',
  NO_ALCOHOL: 'Avoid alcohol', COMPLETE_COURSE: 'Complete the full course of medicine',
  REST: 'Take proper rest', DRINK_FLUIDS: 'Drink plenty of fluids',
  AVOID_OILY: 'Avoid oily/spicy food', LIGHT_DIET: 'Take light diet', NO_SMOKING: 'Avoid smoking',
};
const safeJsonArray = (json) => {
  try { const v = JSON.parse(json); return Array.isArray(v) ? v : []; } catch { return []; }
};

const TemplatesView = ({ can, onEdit, onCreate }) => {
  const notify = useToast();
  const confirm = useConfirm();
  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const canDelete = typeof can === 'function' ? can('DELETE_TEMPLATE') : false;
  // Editing a template rewrites prescription content, so it mirrors the PRESCRIPTIONS "Edit"
  // permission (the same right that lets a user edit an existing prescription's content).
  const canEdit = typeof can === 'function' ? can('EDIT') : false;
  // Creating a template is gated by the PRESCRIPTIONS "Create" right (same right that lets a user
  // start a new prescription, which is the other way a template gets created).
  const canCreate = typeof can === 'function' ? can('CREATE') : false;

  const load = useCallback(async () => {
    try {
      const data = await prescriptionAPI.getAllTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load templates');
      setTemplates([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (t) => {
    const ok = await confirm({
      title: `Delete "${t.templateName}"?`,
      message: 'This permanently removes the template for everyone. Prescriptions already written from it are unaffected. This cannot be undone.',
      confirmText: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    setDeletingId(t.id);
    try {
      await prescriptionAPI.deleteTemplate(t.id);
      notify('success', `"${t.templateName}" deleted`);
      setTemplates(prev => (prev || []).filter(x => x.id !== t.id));
    } catch (e) {
      notify('error', e.message || 'Failed to delete template');
    } finally {
      setDeletingId(null);
    }
  };

  const counts = (t) => ({
    meds: safeJsonArray(t.medicinesJson).length,
    labs: safeJsonArray(t.labTestsJson).length,
  });
  const docName = (t) => t.doctor
    ? (t.doctor.fullName || `${t.doctor.firstName || ''} ${t.doctor.lastName || ''}`.trim() || '—')
    : '—';

  const filtered = (templates || []).filter(t => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (t.templateName || '').toLowerCase().includes(q)
      || (t.diagnosis || '').toLowerCase().includes(q)
      || docName(t).toLowerCase().includes(q);
  });

  // Created By and Follow-up sort on the underlying value, not on the em-dash the cell prints for
  // a missing one, so unattributed templates collect at the bottom instead of under "-".
  const { sortConfig, handleSort } = useSort();
  const sortedTemplates = React.useMemo(() => sortRows(filtered, sortConfig, {
    template:  (t) => t.templateName,
    createdBy: (t) => (t.doctor ? (t.doctor.fullName || `${t.doctor.firstName || ''} ${t.doctor.lastName || ''}`.trim()) : ''),
    diagnosis: (t) => t.diagnosis,
    medicines: (t) => safeJsonArray(t.medicinesJson).length,
    labTests:  (t) => safeJsonArray(t.labTestsJson).length,
    followup:  (t) => (t.followupDays != null ? Number(t.followupDays) : null),
  }), [filtered, sortConfig]);
  const paging = usePagedRows(sortedTemplates, { id: 'rx-templates', resetOn: [sortConfig, query] });

  if (templates === null) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" /></div>;
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-t-2xl flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Save className="w-5 h-5 text-teal-600" /> Saved Templates
            <span className="text-xs font-semibold uppercase bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">All Doctors</span>
          </h2>
          <p className="text-sm text-gray-600">
            {filtered.length} template(s) - click a row to preview its contents.
            {query.trim() !== '' && (
              <button
                onClick={() => setQuery('')}
                className="ml-2 text-emerald-600 hover:text-emerald-700 font-medium hover:underline transition-colors"
              >
                Clear search
              </button>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canCreate && onCreate && (
            <button
              onClick={onCreate}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm whitespace-nowrap"
            >
              <Plus className="w-4 h-4" /> New Template
            </button>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, diagnosis, doctor…"
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 w-72"
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="p-8 text-center text-red-600">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center">
          <Save className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-500 mb-1">No templates found</h3>
          <p className="text-gray-400 text-sm">Templates created here, or saved from a prescription, appear here.</p>
        </div>
      ) : (
        <div>
          <TablePager paging={paging} noun="template" filteredFrom={templates.length} rounded={false} />
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <SortHeader label="Template" sortKey="template" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                <SortHeader label="Created By" sortKey="createdBy" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                <SortHeader label="Diagnosis" sortKey="diagnosis" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                <SortHeader label="Medicines" sortKey="medicines" sortConfig={sortConfig} onSort={handleSort} align="center" className="px-6 py-3" />
                <SortHeader label="Lab Tests" sortKey="labTests" sortConfig={sortConfig} onSort={handleSort} align="center" className="px-6 py-3" />
                <SortHeader label="Follow-up" sortKey="followup" sortConfig={sortConfig} onSort={handleSort} align="right" className="px-6 py-3" />
                {(canEdit || canDelete) && <th className="text-right px-6 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paging.pageRows.map(t => {
                const c = counts(t);
                return (
                  <tr key={t.id} tabIndex={0} onClick={() => setSelected(t)} onKeyDown={onActivateKey(() => setSelected(t))} className={`hover:bg-emerald-50/50 cursor-pointer transition-colors ${ROW_FOCUS_RING}`}>
                    <td className="px-6 py-3">
                      <div className="font-semibold text-gray-900">{t.templateName}</div>
                      {t.description && <div className="text-xs text-gray-500 truncate max-w-xs">{t.description}</div>}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700">{docName(t)}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{t.diagnosis || '—'}</td>
                    <td className="px-6 py-3 text-center text-sm">{c.meds}</td>
                    <td className="px-6 py-3 text-center text-sm">{c.labs}</td>
                    <td className="px-6 py-3 text-right text-sm text-gray-600">{t.followupDays ? `${t.followupDays} days` : '—'}</td>
                    {(canEdit || canDelete) && (
                      <td className="px-6 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onEdit && onEdit(t); }}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Edit template"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDelete(t); }}
                              disabled={deletingId === t.id}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Delete template"
                            >
                              {deletingId === t.id
                                ? <RefreshCw className="w-4 h-4 animate-spin" />
                                : <Trash2 className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          <TablePager paging={paging} noun="template" filteredFrom={templates.length} position="bottom" />
        </div>
      )}

      {selected && <TemplateViewModal template={selected} docName={docName(selected)} onClose={() => setSelected(null)} />}
    </div>
  );
};

// Read-only preview of a template's full contents.
const TemplateViewModal = ({ template, docName, onClose }) => {
  const templateViewDialogRef = useDialog(onClose, { confirmDiscard: false });
  const complaints = safeJsonArray(template.complaintsJson);
  const medicines = safeJsonArray(template.medicinesJson);
  const labTests = safeJsonArray(template.labTestsJson);
  const instructions = safeJsonArray(template.instructionsJson);

  // Section with a coloured icon chip + label, matching the app's card style.
  const Section = ({ icon: Icon, tone, title, children }) => (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-6 h-6 rounded-lg flex items-center justify-center ${tone}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</span>
      </div>
      <div className="pl-8">{children}</div>
    </div>
  );
  const showDescription = template.description && template.description !== template.templateName;

  return (
    <div ref={templateViewDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-5 flex items-start justify-between flex-shrink-0">
          <div className="flex items-center gap-3 text-white min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Save className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold leading-tight truncate">{template.templateName}</h2>
              <p className="text-xs text-teal-100 truncate">Created by {docName}</p>
            </div>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg text-white flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>

        {/* Summary chips */}
        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/60 flex flex-wrap gap-2 flex-shrink-0">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold"><Pill className="w-3.5 h-3.5" />{medicines.length} medicine{medicines.length === 1 ? '' : 's'}</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold"><FlaskConical className="w-3.5 h-3.5" />{labTests.length} lab test{labTests.length === 1 ? '' : 's'}</span>
          {template.followupDays ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold"><Calendar className="w-3.5 h-3.5" />Follow-up {template.followupDays}d</span>
          ) : null}
          {template.consultationOnly && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold">Consultation only</span>
          )}
        </div>

        <div className="p-6 overflow-y-auto space-y-5 text-sm">
          {showDescription && <p className="text-sm text-gray-500 italic -mt-1">{template.description}</p>}

          <Section icon={Stethoscope} tone="bg-emerald-100 text-emerald-600" title="Diagnosis">
            <span className="text-gray-900 font-medium">{template.diagnosis || '—'}</span>
          </Section>

          {complaints.length > 0 && (
            <Section icon={AlertCircle} tone="bg-orange-100 text-orange-600" title="Chief Complaints">
              <div className="flex flex-wrap gap-1.5">
                {complaints.map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-200 text-gray-800">
                    {c.complaint}
                    {c.since && <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full">{c.since}</span>}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {template.clinicalNotes && (
            <Section icon={FileText} tone="bg-emerald-100 text-emerald-600" title="Clinical Notes">
              <div className="text-gray-800 whitespace-pre-wrap">{template.clinicalNotes}</div>
            </Section>
          )}
          {template.examination && (
            <Section icon={Activity} tone="bg-teal-100 text-teal-600" title="Examination">
              <div className="text-gray-800 whitespace-pre-wrap">{template.examination}</div>
            </Section>
          )}

          {medicines.length > 0 && (
            <Section icon={Pill} tone="bg-emerald-100 text-emerald-600" title="Medicines">
              <div className="space-y-1.5">
                {medicines.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
                    <span className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <span className="font-medium text-gray-900">{m.medicineName}</span>
                    {m.strength && <span className="text-gray-500 text-xs">{m.strength}</span>}
                    <span className="ml-auto text-xs text-gray-500">
                      {[m.frequency, durationSummary(m)].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {labTests.length > 0 && (
            <Section icon={FlaskConical} tone="bg-teal-100 text-teal-600" title="Lab Tests">
              <div className="flex flex-wrap gap-1.5">
                {labTests.map((t, i) => <span key={i} className="px-2.5 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-medium">{t.testName}</span>)}
              </div>
            </Section>
          )}

          {instructions.length > 0 && (
            <Section icon={MessageSquare} tone="bg-teal-100 text-emerald-600" title="Patient Instructions">
              <div className="flex flex-wrap gap-1.5">
                {instructions.map((key, i) => (
                  <span key={i} className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">{TEMPLATE_INSTRUCTION_EN[key] || key}</span>
                ))}
              </div>
            </Section>
          )}
          {template.followupDays ? (
            <Section icon={Calendar} tone="bg-amber-100 text-amber-600" title="Follow-up">
              <span className="text-gray-900 font-medium">After {template.followupDays} days</span>
            </Section>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ============ LOAD TEMPLATE MODAL ============
// `busy` is true while the writer applies a template (it owns the flag, so it outlives this dialog).
// Applying sends each line in turn and takes a moment; the dialog cannot be closed meanwhile, by X,
// Cancel or Escape, since closing would stop nothing and a second apply would add every line twice.
const LoadTemplateModal = ({ templates, onClose, onApply, busy = false }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const close = () => { if (!busy) onClose(); };
  const loadTemplateDialogRef = useDialog(close, { confirmDiscard: false });
  const apply = () => {
    if (!selectedTemplate || busy) return;
    onApply(selectedTemplate);
  };

  return (
    <div ref={loadTemplateDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center">
            <ClipboardList className="w-5 h-5 mr-2 text-amber-600" />
            Load Template
          </h3>
          <button aria-label="Close" onClick={close} disabled={busy} className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No templates saved yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template)}
                  disabled={busy}
                  className={`w-full text-left p-4 rounded-xl border transition-colors disabled:cursor-not-allowed ${
                    selectedTemplate?.id === template.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <h4 className="font-semibold text-gray-900">{template.templateName}</h4>
                  {template.description && (
                    <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                  )}
                  <div className="flex items-center space-x-3 mt-2 text-xs text-gray-400">
                    {template.diagnosis && <span>Diagnosis: {template.diagnosis}</span>}
                    <span>Used {template.usageCount || 0} times</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={close}
            disabled={busy}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={apply}
            disabled={!selectedTemplate || busy}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            {busy ? 'Applying…' : 'Apply Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ SAVE TEMPLATE MODAL ============
const SaveTemplateModal = ({ prescription, formData, doctorId, templates = [], loadedTemplate = null, onClose, onSaved }) => {
  const showToast = useToast();
  // 'new' = create a brand-new template; 'update' = overwrite an existing one in place.
  // Default to updating the template that was loaded into this Rx (if any), else create new.
  const [mode, setMode] = useState(loadedTemplate ? 'update' : 'new');
  const [selectedTemplateId, setSelectedTemplateId] = useState(loadedTemplate?.id || '');
  const [templateName, setTemplateName] = useState(loadedTemplate?.templateName || '');
  const [description, setDescription] = useState(loadedTemplate?.description || '');
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const saveTemplateDialogRef = useDialog(onClose);

  const hasTemplates = templates.length > 0;

  // Picking an existing template to overwrite prefills its name + description (still editable).
  const handlePickTemplate = (id) => {
    setSelectedTemplateId(id);
    const t = templates.find((x) => String(x.id) === String(id));
    if (t) { setTemplateName(t.templateName || ''); setDescription(t.description || ''); }
    setNameError('');
  };

  const handleSave = async () => {
    const isUpdate = mode === 'update';
    if (isUpdate && !selectedTemplateId) {
      setNameError('Please choose a template to update');
      return;
    }
    if (!templateName.trim()) {
      setNameError('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      await prescriptionAPI.saveTemplate({
        // Including an id makes the backend overwrite that template; omitting it creates a new one.
        ...(isUpdate ? { id: selectedTemplateId } : {}),
        doctorId,
        templateName: templateName.trim(),
        description: description.trim(),
        // Capture the whole prescription so loading it restores everything: the same content the
        // writer's own template saves store (templateContent), dosing and all.
        ...templateContent(formData, prescription),
      });
      showToast('success', mode === 'update' ? 'Template updated successfully!' : 'Template saved successfully!');
      onSaved();
    } catch (err) {
      console.error('Failed to save template:', err);
      showToast('error', 'Failed to save template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={saveTemplateDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center">
            <Save className="w-5 h-5 mr-2 text-teal-600" />
            {mode === 'update' ? 'Update Template' : 'Save as Template'}
          </h3>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* New vs. Update existing — only offer Update when there are templates to overwrite. */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => { setMode('new'); setNameError(''); }}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                mode === 'new'
                  ? 'bg-teal-600 border-teal-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Create new
            </button>
            <button
              type="button"
              disabled={!hasTemplates}
              onClick={() => { setMode('update'); setNameError(''); if (!selectedTemplateId && loadedTemplate) handlePickTemplate(loadedTemplate.id); }}
              title={hasTemplates ? '' : 'No existing templates to update'}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                mode === 'update'
                  ? 'bg-teal-600 border-teal-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Update existing
            </button>
          </div>

          {/* Update mode — pick which existing template to overwrite. */}
          {mode === 'update' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template to update <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedTemplateId}
                onChange={handlePickTemplate}
                placeholder="Select a template…"
                options={templates.map((t) => ({ value: t.id, label: t.templateName }))}
                buttonClassName="px-4 py-2 rounded-lg"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => { setTemplateName(e.target.value); if (nameError) setNameError(''); }}
              placeholder="e.g., Viral Fever Standard, HTN Follow-up"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 ${nameError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
            />
            {nameError && <p className="text-red-500 text-xs mt-1">{nameError}</p>}
            {mode === 'update' && (
              <p className="text-gray-400 text-xs mt-1">Overwrites the selected template with the current prescription. You can rename it here.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Brief description of when to use this template..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <p className="font-medium mb-1">Will save:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>{prescription.medicines?.length || 0} medicine(s)</li>
              <li>{prescription.labTests?.length || 0} lab test(s)</li>
              {formData.diagnosis && <li>Diagnosis: {formData.diagnosis}</li>}
              {formData.followupDays && <li>Follow-up: {formData.followupDays} days</li>}
            </ul>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !templateName.trim() || (mode === 'update' && !selectedTemplateId)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : mode === 'update' ? 'Update Template' : 'Save Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ PRESCRIPTION DETAIL MODAL (HISTORY) ============
// PrescriptionDetailModal moved to its own shared file (./PrescriptionDetailModal.js) so the
// patient details modal and other surfaces can reuse the exact same read-only view.

// ============ PATIENT HISTORY FULL MODAL ============
const PatientHistoryFullModal = ({ patientId, patientName, currentPrescriptionId, onClose, onViewDetail }) => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const patientHistoryDialogRef = useDialog(onClose, { confirmDiscard: false });

  useEffect(() => {
    const load = async () => {
      try {
        const all = await prescriptionAPI.getPatientPrescriptions(patientId);
        setPrescriptions((all || []).filter(rx => rx.id !== currentPrescriptionId && !rx.isTemplate));
      } catch (err) {
        console.error('Failed to load patient history:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [patientId, currentPrescriptionId]);

  return (
    <div ref={patientHistoryDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold flex items-center">
            <Clock className="w-5 h-5 mr-2 text-emerald-600" />
            All Prescriptions - {patientName}
          </h3>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading prescriptions...</div>
          ) : prescriptions.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No past prescriptions found</div>
          ) : (
            <div className="space-y-2">
              {prescriptions.map((rx) => (
                <button
                  key={rx.id}
                  onClick={() => onViewDetail(rx)}
                  className="w-full text-left p-4 bg-gray-50 hover:bg-emerald-50 rounded-xl transition-colors border border-gray-200 hover:border-emerald-300"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-900">
                      {rx.prescriptionNumber || `#${rx.id}`}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_PILL[rx.status] || 'bg-gray-100 text-gray-600'}`}>
                      {rxStatusLabel(rx.status)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {rx.createdAt && formatClinicDate(rx.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    {rx.doctor && (
                      <span>{rx.doctor.fullName || `Dr. ${rx.doctor.firstName} ${rx.doctor.lastName}`}</span>
                    )}
                  </div>
                  {rx.diagnosis && (
                    <p className="text-sm text-gray-600 mt-1.5 truncate">{rx.diagnosis}</p>
                  )}
                  {/* Medicines and lab tests listed by name, same presentation as the Patient
                      Details Prescriptions tab - the doctor reads what was given at a glance
                      instead of opening each prescription to find out. */}
                  {rx.medicines?.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                        <Pill className="w-3 h-3" /> Medicines ({rx.medicines.length})
                      </div>
                      <ul className="space-y-0.5">
                        {/* The API serialises these as a set, so re-establish the written order. */}
                        {[...rx.medicines]
                          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                          .map((m, i) => (
                            <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                              <span className="text-emerald-500 mt-0.5 shrink-0">•</span>
                              <span>
                                <span className="font-medium text-gray-900">{m.medicineName}</span>
                                {m.strength && <span className="text-gray-400 ml-1">{m.strength}</span>}
                                {m.dosageInstructionEnglish && (
                                  <span className="text-gray-500 ml-1">· {m.dosageInstructionEnglish}</span>
                                )}
                              </span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                  {rx.labTests?.length > 0 && (
                    <div className="mt-2 text-xs">
                      <span className="font-semibold text-gray-500 uppercase tracking-wider mr-2 inline-flex items-center gap-1">
                        <FlaskConical className="w-3 h-3" /> Lab Tests ({rx.labTests.length})
                      </span>
                      <span className="text-gray-700">
                        {[...rx.labTests]
                          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                          .map((t) => t.testName)
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ HELPER FUNCTIONS ============
const calculateAge = (dob) => {
  if (!dob) return 'N/A';
  const birthDate = toClinicDate(dob);
  const today = clinicNow();
  if (!birthDate) return 'N/A';
  let years = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    years--;
  }
  // "0 yrs" is useless for an infant — the doctor needs to know if this is a 2-day-old or
  // an 11-month-old. Switch to a finer scale below 1 year.
  if (years >= 1) return `${years} yr${years === 1 ? '' : 's'}`;
  const totalMonths = (today.getFullYear() - birthDate.getFullYear()) * 12
    + (today.getMonth() - birthDate.getMonth())
    - (today.getDate() < birthDate.getDate() ? 1 : 0);
  if (totalMonths >= 1) return `${totalMonths} month${totalMonths === 1 ? '' : 's'}`;
  const days = Math.floor((today - birthDate) / 86400000);
  if (days <= 28) return days <= 1 ? 'newborn' : `${days} days`;
  return `${days} days`;
};

// The age as the doctor should read it: "about 6 yrs" when the date on file is an estimate the
// family gave, and then only as fine as that answer was.
const patientAgeLabel = (patient) => {
  const age = calculateAge(patient?.dateOfBirth);
  if (!patient?.dobEstimated || age === 'N/A') return age;
  return `about ${age.replace(/ \d+ days?$/, '')}`;
};

// Pick the patient's primary relation label using the priority used elsewhere in the app:
// minor → Guardian (with relationship); adult woman with husband on file → Husband; otherwise
// → Father. Returns null if none on file.
const pickPatientRelation = (patient) => {
  if (!patient) return null;
  const isMinor = patient.adult === false || patient.minor === true;
  if (isMinor && patient.guardianName) {
    const rel = patient.guardianRelationshipOther && patient.guardianRelationship === 'OTHER'
      ? patient.guardianRelationshipOther
      : (patient.guardianRelationship
          ? patient.guardianRelationship.charAt(0) + patient.guardianRelationship.slice(1).toLowerCase()
          : null);
    return { label: 'Guardian', value: patient.guardianName, suffix: rel ? `(${rel})` : null };
  }
  if (patient.husbandName) return { label: 'Husband', value: patient.husbandName };
  if (patient.fatherName) return { label: 'Father', value: patient.fatherName };
  return null;
};

// ============ PATIENT DATA MODAL (reuses Patient View Modal tabs) ============
// Titled modal shell around a reused Patient View Modal tab component. The inner components
// (imported from PatientManagement) self-fetch by patientId, so this is just a container.
const PATIENT_DATA_VIEWS = {
  'medical-history': { title: 'Medical History',   Icon: Heart,        Component: PatientMedicalHistoryTab },
  'lab-tests':       { title: 'Lab Reports',       Icon: FlaskConical, Component: PatientLabReportsTab },
  'radiology':       { title: 'Radiology Reports', Icon: Image,        Component: PatientRadiologyReportsTab },
};

const PatientDataModal = ({ view, patientId, patientName, onClose }) => {
  const patientDataDialogRef = useDialog(onClose, { confirmDiscard: false });
  const cfg = PATIENT_DATA_VIEWS[view];
  if (!cfg) return null;
  const { title, Icon, Component } = cfg;
  return (
    <div
      ref={patientDataDialogRef}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 text-white">
            <Icon className="w-5 h-5" />
            <div>
              <h2 className="text-lg font-bold leading-tight">{title}</h2>
              {patientName && <p className="text-xs text-emerald-100">{patientName}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg text-white" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          <Component patientId={patientId} />
        </div>
      </div>
    </div>
  );
};

// ============ PATIENT DOCUMENTS MODAL (read-only viewer for prescription screen) ============
const DOC_TYPE_LABELS = {
  PRESCRIPTION: 'Prescription', LAB_REPORT: 'Lab Report', XRAY: 'X-Ray',
  CT_SCAN: 'CT Scan', MRI: 'MRI', ULTRASOUND: 'Ultrasound', ECG: 'ECG',
  DISCHARGE_SUMMARY: 'Discharge Summary', REFERRAL: 'Referral', OTHER: 'Other',
};

const PatientDocumentsModal = ({ patientId, patientName, onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const patientDocumentsDialogRef = useDialog(onClose, { confirmDiscard: false });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await patientDocumentAPI.getByPatient(patientId);
        // Exclude types that have their own dedicated header links (Lab Reports, Radiology, Past
        // Prescriptions) so this generic view only shows the leftover documents.
        const list = (Array.isArray(data) ? data : []).filter(d => !DOCS_HIDDEN_TYPES.has(d.documentType));
        setDocuments(list);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [patientId]);

  const filtered = filter === 'ALL' ? documents : documents.filter(d => d.documentType === filter);
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  const token = localStorage.getItem('clinic_auth_token');
  const docUrl = (docId) => `${apiBase}/patients/${patientId}/documents/${docId}/download?token=${token}`;

  const types = ['ALL', ...new Set(documents.map(d => d.documentType))];

  return (
    <div ref={patientDocumentsDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Patient Documents</h2>
            <p className="text-white/70 text-sm">{patientName} - {documents.length} document{documents.length !== 1 ? 's' : ''}</p>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="h-5 w-5 text-white" /></button>
        </div>

        {/* Filter pills */}
        {documents.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap gap-2">
            {types.map(t => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  filter === t ? 'bg-navy-100 text-navy-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t === 'ALL' ? 'All' : (DOC_TYPE_LABELS[t] || t)}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-400"><RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{documents.length === 0 ? 'No documents uploaded for this patient' : 'No documents match this filter'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {filtered.map(doc => (
                <a
                  key={doc.id}
                  href={docUrl(doc.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-emerald-300 transition-all group"
                >
                  <div className="h-32 bg-gray-100 flex items-center justify-center overflow-hidden">
                    {doc.hasThumbnail ? (
                      <img
                        src={authedMediaUrl(`${apiBase}/patients/${patientId}/documents/${doc.id}/thumbnail`)}
                        alt={doc.fileName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="flex flex-col items-center text-gray-400">
                        {doc.contentType === 'application/pdf' ? <FileText className="h-10 w-10" /> : <Eye className="h-10 w-10" />}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full font-medium">
                        {DOC_TYPE_LABELS[doc.documentType] || doc.documentType}
                      </span>
                      <span className="text-xs text-gray-400">{doc.fileSizeDisplay}</span>
                    </div>
                    {doc.description && <p className="text-xs text-gray-500 mt-1 truncate">{doc.description}</p>}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionManagement;